import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { Clock, GripVertical, Lock, Plus, Trash2, Upload } from "lucide-react";

import type {
  CreateLeaderboardInput,
  CreateRewardInput,
  CycleStatus,
  GuildAggregator,
  GuildScoreSource,
  Guards,
  LadderDecision,
  LadderImpact,
  LeaderboardStatus,
  PendingPackage,
  MatchmakingPrefer,
  Operator,
  RetentionImpact,
  Reward,
  RewardsFile,
  RewardsFileEntry,
  Scope,
  Segmentation,
  Sort,
  StatKeyCoverage,
  SubmitPolicy,
  Tier,
  TierConfig,
  TierMode,
  Visibility,
  WindowKind,
} from "./types";
import { isTierThresholdReward } from "./types";
import { isAutoScored, isMemberContribution } from "./autoScored";
import {
  LAYER_KINDS,
  LAYER_META,
  PREFER_LABEL,
  configFromDraft,
  defaultMatchmakingDraft,
  draftFromConfig,
  hasMatchmakingErrors,
  newLayerDraft,
  validateMatchmaking,
  type LayerDraft,
  type MatchmakingDraft,
  type MatchmakingErrors,
} from "./matchmaking";
import { countrySplitAllowed } from "./segments";
import { PendingMarksCtx } from "./pendingContext";
import { PendingNote, WhenBadge } from "./pendingMarks";
import { boardHasNextCycle, whenBadge } from "./pendingFields";
import { changedFields as diffChangedFields } from "./pendingDiff";
import type { NextCut } from "./pendingWhen";
import pendingStyles from "./pending.module.css";
import { parseRewardPayload, rewardPayloadToInput } from "./rewardPayload";
import {
  fileRewardBasis,
  fileRewardKind,
  type RewardsFileEntryKind,
} from "./rewardsFile";
import {
  ImportRewardsDraftModal,
  type ImportRewardsMode,
  type RewardsFilePreviewRow,
  type RewardsFileProblem,
  type RewardsFileReview,
} from "./ImportRewardsDraftModal";
import { Alert } from "../../components/Alert/Alert";
import type { SchemaField } from "../../components/SchemaEditor/SchemaEditor";
import { Select } from "../../components/Select/Select";
import { analyseCron } from "../../lib/format";
import {
  splitWindowLength,
  windowPauseSentence,
  WINDOW_UNIT_MINUTES,
  type WindowUnit,
} from "./windowLength";
import { schemaKeyOptions } from "../../lib/schemaFields";
import styles from "./LeaderboardConfigForm.module.css";

// === Component contract =============================================== //

/**
 * When a changed `cron` starts cutting cycles. The worker reads the expression
 * lazily, at the moment it closes a cycle, so a new schedule naturally takes
 * effect from the next one — "current_cycle" is the extra instruction to also
 * move the open cycle's close_at, and the only one of the two that shortens an
 * interval players were already told about.
 */
export type CronApply = "next_cycle" | "current_cycle";

/**
 * Which of the two save buttons was pressed. `now` patches the board and the
 * open cycle sees it at once; `schedule` puts the same values in a package the
 * next cut applies. A change that can only take effect at a cut leaves only
 * one of the two live.
 */
export type SubmitIntent = "now" | "schedule";

/**
 * What a save carries beyond the payload itself: decisions about how the
 * change lands rather than about what is stored. They travel beside the input
 * rather than inside it because none of them is a column.
 */
export interface SubmitMeta {
  cronApply: CronApply;
  /** Which button was pressed — see SubmitIntent. */
  intent: SubmitIntent;
  /**
   * The fields the form is actually holding differently from the board.
   *
   * The host cannot work this out for itself: it has `initial` and the payload,
   * but the payload states values for fields nobody touched (that is what makes
   * a PATCH a whole-board save), and a package built from it would schedule
   * thirty unchanged columns. The form is the side that knows what moved.
   */
  changedFields: string[];
  /** What becomes of the leagues players hold, where the change takes the
     board off its ladder and the question therefore had to be asked. */
  ladderDecision?: LadderDecision;
}

export interface LeaderboardConfigFormProps {
  mode: "create" | "edit";
  initial?: Partial<CreateLeaderboardInput>;
  /**
   * Lifecycle of the board being edited. Only `ext_key` reads it: the key is
   * the handle builds already in the stores submit to, so it stays locked for
   * as long as the board can take a submit at all, and archiving — which stops
   * that — is what makes renaming safe. `undefined` is read as "not archived":
   * of the two readings, that is the one that cannot break a shipped build.
   */
  status?: LeaderboardStatus;
  /**
   * Status of the cycle a `window_kind=event` board's dates describe, passed
   * by the host on that board and left out everywhere else.
   *
   * `undefined` means "not known yet" — the request is in flight, or this host
   * does not fetch cycles at all — and both dates stay editable, exactly as
   * they were before this prop existed. Deliberately the old behaviour rather
   * than the safest one: a field locked because an answer has not arrived
   * would stay locked forever on a host that never sends one.
   */
  eventCycleStatus?: CycleStatus;
  /**
   * `close_at` of the cycle open right now, where the host knows it — the date
   * a recurring board's players were told the interval they are in ends on.
   *
   * The two `cron_apply` options are a choice about exactly that date: keep it
   * as announced, or move it in. It is a fact about a cycle, which this form is
   * never handed otherwise, so the host reads it and passes it down. Left out
   * — no host, no open cycle, the answer still in flight — both sentences are
   * written without a date rather than around one nobody read.
   */
  openCycleCloseAt?: string;
  /**
   * `open_at` of the cycle open right now — the moment a recurring_event
   * board's running window started.
   *
   * Its own half of the same choice `openCycleCloseAt` is about: on this board
   * `cron_apply=current_cycle` moves the end to `open_at` plus the new length,
   * and the start never moves. That makes the new end a date the form can
   * NAME rather than describe — the arithmetic is addition, not a cron walk —
   * and this is the only fact it is missing to do so. Left out, the sentence
   * is written without it, as `openCycleCloseAt` already is.
   */
  openCycleOpenAt?: string;
  /**
   * The board is BETWEEN two windows: its newest cycle has not opened yet.
   *
   * A recurring_event board spends half its life like that, and it is the one
   * state where `cron_apply` has nothing to choose between — there is no
   * running window to keep or to cut short, and the server moves the window
   * that is already scheduled whichever option is sent. The row is therefore
   * not drawn at all, and the sentence in its place says what a save will
   * actually do. Left out (or false) means «a window is running, or nobody
   * looked» — the same reading `openCycleCloseAt` has when it is absent.
   */
  windowPaused?: boolean;
  /**
   * Why the server refused the last save, when what it refused was the length
   * of the window — see windowLengthRefusal, which is what the hosts pick it
   * out with.
   *
   * It travels as a prop rather than being computed here because the form does
   * not save anything: the host owns the request, and the one thing this form
   * can add is putting the answer under the field it is about instead of in a
   * banner at the top of the page. The text is the server's own, whole: the
   * numbers in it come from the cron walk only the server does.
   */
  windowLengthError?: string;
  /**
   * Rewards already persisted on the board. Only used by the claim_gated tier
   * editor, whose Unlock reward column mirrors the threshold reward bound to
   * each tier. In `mode="create"` nothing is persisted yet and the reward
   * drafts of section 5 are the source instead, so the host leaves this out.
   *
   * In `mode="edit"` `undefined` means "not known yet" — the request is still
   * in flight, or it failed. That is NOT the same as an empty array, which
   * means "this board has no rewards", and the difference is the whole story
   * the tier editor tells: an unanswered request must never be read as a
   * ladder with no rewards on it. Hosts therefore pass the query's data
   * straight through, never `data ?? []`.
   */
  rewards?: Reward[];
  /**
   * Asks the backend what a retention of `days` would delete on this board.
   * Only the edit-mode host supplies it: a board that does not exist yet has
   * no finished cycles to sweep, and without an answer the form shows no
   * numbers at all rather than numbers it made up.
   */
  fetchRetentionImpact?: (days: number) => Promise<RetentionImpact>;
  /**
   * The project's `profile.stats_schema` — the catalogue a `guild_stat_key`
   * must be declared in, as an `int` field. Passed down rather than fetched
   * here because both hosts already read the project for their breadcrumb.
   *
   * `undefined` means "not known yet" — the project request is in flight, or
   * it failed — and is NOT the same as `[]`, which means "this project has
   * declared no stats". Only the second one disables the picker and accuses
   * the project of having nothing to score by; a request that has not landed
   * must never be read as that, or every saved auto board would be told it is
   * broken for one frame. Hosts therefore pass the query's answer through
   * rather than `?? []`.
   */
  statsSchema?: SchemaField[];
  /**
   * How many of the project's players actually carry a stat key. Asked about
   * the key in the box, not about the board — a valid key nobody writes is a
   * board of zeroes, and this is the only place that fact is visible before
   * the board exists. Only a host that can reach the endpoint supplies it;
   * without one the line simply isn't shown, like the retention counts.
   */
  fetchStatKeyCoverage?: (key: string) => Promise<StatKeyCoverage>;
  /**
   * The package of changes waiting on this board, where there is one. Only a
   * package still `pending` marks anything: an applied one is on the board
   * already and a refused one never will be.
   *
   * `null` and `undefined` are read the same way here — "nothing is waiting" —
   * because a form with no marks is what the board looked like before this
   * feature, and the query landing is not a state worth drawing.
   */
  pending?: PendingPackage | null;
  /**
   * When a scheduled change is applied, where the host knows it. Used for the
   * save bar's "next cut Mon, Sep 15" (or "next cycle opens …" between two
   * windows) and nothing else — the sentence is written without it rather
   * than around a date nobody read.
   */
  nextCut?: NextCut;
  /**
   * How many players hold a league on this board. Only read while the edit is
   * taking the board off its ladder, which is the one scheduled change that
   * touches players rather than configuration. Absent, the question is still
   * asked — without the counts.
   */
  ladderImpact?: LadderImpact;
  /** A package being put on the board right now. Greys the second button the
     way `saving` greys the first. */
  scheduling?: boolean;
  /**
   * Values the form OPENS on that are not what the board holds — the fields of
   * a refused package, put back as an unsaved edit ("Edit and reschedule").
   *
   * Deliberately separate from `initial`: `initial` is the BASELINE the save
   * bar counts against, and merging the two would make a form full of refused
   * values report nothing to save. Read once, at mount, so a host that changes
   * it has to remount the form — which is what makes "put these values back"
   * an event rather than a state the form keeps re-applying.
   */
  seed?: Partial<CreateLeaderboardInput>;
  submitLabel?: string;
  /**
   * Called with the leaderboard payload, plus an optional list of reward
   * drafts to create after the leaderboard itself is created. The form
   * only collects reward drafts in `mode="create"`; in edit mode the
   * second argument is always an empty array (rewards are managed on the
   * detail page's Rewards tab).
   *
   * The third argument carries what the payload cannot say — see SubmitMeta.
   * A host that has nothing to do with it simply declares fewer parameters.
   */
  onSubmit: (
    input: CreateLeaderboardInput,
    rewards: CreateRewardInput[],
    meta: SubmitMeta,
  ) => void;
  onCancel: () => void;
  saving?: boolean;
  /**
   * Why an immediate save cannot be offered right now, in the host's own
   * words — a fact only the host has. Today there is one: the board's
   * scheduled package could not be read, so nothing on this page can say what
   * an immediate save would collide with, and a save made blind is one the
   * next cut silently rolls back.
   *
   * It lands as the title of a greyed-out "Save now" that stays focusable, the
   * same way the cut-only refusal does, and "Schedule for next cycle" becomes
   * the primary action. Only ever set on a board that HAS a next cycle: on one
   * that does not there is no second button to fall back on, and refusing the
   * only save there is would be a page with no way forward.
   */
  saveNowBlocked?: string;
}

/**
 * Draft of a reward as edited in the create-form mini-editor. All fields
 * are strings because they're bound directly to inputs; conversion to the
 * API shape happens in buildRewardInputs().
 */
interface RewardDraft {
  name: string;
  /**
   * "tier_threshold" has no counterpart on the wire — a reward is one when it
   * carries a tier_key and no range at all (types.ts isTierThresholdReward).
   * The draft names it explicitly because the operator picks it explicitly.
   */
  type: "rank_range" | "percentile" | "tier_threshold";
  /**
   * Row that arrived from a file rather than being typed. Display only — it
   * tints the row so the operator can see what the last import actually
   * added, and it never leaves the form.
   */
  imported?: boolean;
  /**
   * uid of the tier row this reward is bound to, or ALL_TIERS ("") for a
   * reward that pays out in every tier. The reference is the row's uid, not
   * its key, so renaming a key on step 4 carries the binding along; a removed
   * row leaves a dangling uid, which buildRewardInputs resolves back to "all
   * tiers" rather than posting a tier_key the backend would reject.
   */
  tier_uid: string;
  range_from: string;
  range_to: string;
  payload: string;
}

/** Sentinel for "all tiers" — the option that omits tier_key. */
const ALL_TIERS = "";

/**
 * tier_threshold only exists on a claim_gated board — ValidateRewardCreate
 * rejects it anywhere else. Leaving the mode therefore drops the drafts back
 * to the default type instead of posting something guaranteed to 400, the
 * same silent fallback a dangling tier_uid gets.
 *
 * At module scope because two callers reach it now: the segmentation cards,
 * and `confineGuildSegmentation`, which takes the tier board away without
 * anybody clicking those cards at all.
 */
function resetThresholdDrafts(rewards: RewardDraft[]): RewardDraft[] {
  if (!rewards.some((r) => r.type === "tier_threshold")) return rewards;
  return rewards.map((r) =>
    r.type === "tier_threshold" ? { ...r, type: "rank_range" } : r,
  );
}

function emptyRewardDraft(): RewardDraft {
  return {
    name: "",
    type: "rank_range",
    tier_uid: ALL_TIERS,
    range_from: "",
    range_to: "",
    payload: "",
  };
}

// === Tier drafts ====================================================== //

/**
 * One row of the tier editor. `min_score` is a string because it's bound
 * straight to an input; conversion to the API shape happens in
 * buildTierConfig().
 */
interface TierDraft {
  /** Stable React key — survives sorting and reordering. */
  uid: string;
  key: string;
  name: string;
  min_score: string;
  /**
   * Row seeded from an already-persisted tier_config. Its `key` is
   * referenced by leaderboard_player_tiers.tier_key and by rewards, so
   * renaming it would orphan both — the input stays disabled.
   */
  saved: boolean;
  /**
   * min_score as it arrived from the persisted tier_config, or null for a row
   * added in this session. claim_gated compares against it: moving a live
   * threshold changes the goal for everybody, and the operator is told so.
   */
  savedMinScore: string | null;
  /** The operator typed the key by hand; stop deriving it from the name. */
  keyDirty: boolean;
}

/**
 * Modes whose rows are driven by a threshold: same columns, same automatic
 * sorting, same "min_score on every tier" rule. promotion is the odd one out —
 * there the array order alone is the ladder.
 */
function isThresholdTierMode(mode: TierMode | ""): boolean {
  return mode === "score_bucket" || mode === "claim_gated";
}

/**
 * Mode preselected when segmentation flips to tier. score_bucket is the
 * common case, but it's rejected outright next to operator=increment
 * (validate.go:125), so that combination starts on promotion instead.
 */
function defaultTierMode(operator: Operator | undefined): TierMode {
  return operator === "increment" ? "promotion" : "score_bucket";
}

let tierUidSeq = 0;

function emptyTierDraft(): TierDraft {
  return {
    uid: `tier-${++tierUidSeq}`,
    key: "",
    name: "",
    min_score: "",
    saved: false,
    savedMinScore: null,
    keyDirty: false,
  };
}

/**
 * `Bronze` → `bronze`. Tier keys travel to leaderboard_player_tiers.tier_key,
 * to reward.tier_key and out through the SDK, so they're kept slug-shaped
 * even though the backend only requires them to be non-empty and unique.
 */
function slugifyTierKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Parsed threshold, or null when the field is empty or not an integer. */
function parseMinScore(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) ? n : null;
}

/**
 * score_bucket order: ascending min_score. PickScoreTier (tier.go:8) walks
 * the array front-to-back and keeps the last tier whose threshold is ≤ the
 * incoming score, so an unsorted array silently mis-assigns players. Rows
 * without a usable threshold sink to the bottom so they stay visible.
 */
function sortByMinScore(tiers: TierDraft[]): TierDraft[] {
  return [...tiers].sort((a, b) => {
    const av = parseMinScore(a.min_score);
    const bv = parseMinScore(b.min_score);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return av - bv;
  });
}

/** 2999 → "2 999". One grouping for every number the form prints. */
function formatCount(n: number): string {
  return n.toLocaleString("en-US").replace(/,/g, " ");
}

/**
 * 21904 → "21,904". The stat-key coverage line alone, where the two numbers
 * are read as one ratio ("21,904 of 24,110 players carry it") rather than as
 * the standalone counts formatCount thins the separator for — t-100 mockup §6
 * writes them grouped by comma, and a ratio split by spaces reads as four
 * numbers instead of two.
 */
function formatPlayers(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * Upper bound of each tier, keyed by row uid. There is no max_score in the
 * model (types.go:55) — a tier simply runs until the next threshold — so
 * this is display-only and never leaves the form. "∞" for the top tier, "—"
 * when the row (or the ordering around it) can't produce a number.
 */
function deriveMaxScores(tiers: TierDraft[]): Record<string, string> {
  const ordered = sortByMinScore(tiers);
  const out: Record<string, string> = {};
  for (let i = 0; i < ordered.length; i++) {
    const own = parseMinScore(ordered[i].min_score);
    if (own === null) {
      out[ordered[i].uid] = "—";
      continue;
    }
    let next: number | null = null;
    for (let j = i + 1; j < ordered.length; j++) {
      const candidate = parseMinScore(ordered[j].min_score);
      if (candidate !== null) {
        next = candidate;
        break;
      }
    }
    if (next === null) {
      out[ordered[i].uid] = "∞";
    } else if (next - 1 < own) {
      // Two tiers share a threshold — the range is empty, so there is no
      // honest number to show.
      out[ordered[i].uid] = "—";
    } else {
      out[ordered[i].uid] = formatCount(next - 1);
    }
  }
  return out;
}

/**
 * The claim_gated ladder: the rows that carry a usable threshold, bottom rung
 * first. A row without one isn't on the ladder at all — there is no score at
 * which a player would reach it, so it neither unlocks nor is unlocked.
 */
function ladderRungs(tiers: TierDraft[]): TierDraft[] {
  return sortByMinScore(tiers).filter((t) => parseMinScore(t.min_score) !== null);
}

/**
 * The rung the ladder starts on — whichever row lands at tier_config[0] once
 * buildTierConfig sorts them, which is exactly what the backend calls the
 * bottom tier.
 *
 * Nothing unlocks it: every player begins there, the claimed-threshold ceiling
 * starts at Tiers[0] and NextTierUp never returns it, while MaterializeGrants
 * skips threshold rewards altogether. So a tier_threshold reward bound here
 * could never be granted by any path, and ValidateRewardCreate now rejects the
 * pair outright (leaderboard_invalid_reward_range). claim_gated only — in
 * promotion mode the array order is the ladder, and thresholds don't exist.
 */
function bottomTier(tiers: TierDraft[]): TierDraft | undefined {
  return sortByMinScore(tiers)[0];
}

/** Display name of a tier row, falling back to the key and then the position. */
function tierRowLabel(t: TierDraft, index: number): string {
  return t.name.trim() || t.key.trim() || `Tier ${index + 1}`;
}

/**
 * The same row, for the places that print its key: prose about rewards and
 * tier_config quotes keys, not display names. A row with a threshold but no
 * key yet is still a rung, so the fallback chain of tierRowLabel applies —
 * naming it "Tier 2" beats rendering an empty <code> the operator can't match
 * to anything on screen.
 */
function tierRowKey(t: TierDraft, index: number): string {
  return t.key.trim() || t.name.trim() || `Tier ${index + 1}`;
}

/**
 * Same explanation, told without names — for a ladder that can't yet carry the
 * concrete one. Every beat of the full version is here: the reward arrives on
 * crossing, the league moves only on claiming, a falling score takes the league
 * back but not the reward, and nothing expires.
 */
const CLAIM_GATED_STORY_GENERIC =
  "Crossing the next tier's threshold puts that tier's reward in the player's " +
  "hands — the league itself doesn't move yet. Claiming it moves them up, and " +
  "the tier above becomes the next goal. A falling score takes the league back " +
  "down, but the goal stays where it was: a reward that has already been " +
  "granted is never granted a second time. An unclaimed reward never expires.";

/**
 * "What the player sees", told with this board's own tiers, thresholds and
 * reward names. Hard-coding the mockup's example would be worse than useless
 * on a board of Wood and Stone — it would describe someone else's ladder in
 * the one place that exists to explain this one.
 *
 * The concrete story needs three rungs (start → first goal → the goal after
 * it) and a reward on both rungs above the bottom. Short of that there is no
 * ladder to narrate yet, so the mechanic is described without names.
 */
function claimGatedStory(
  rungs: TierDraft[],
  unlockRewards: Record<string, string>,
): string {
  if (rungs.length < 3) return CLAIM_GATED_STORY_GENERIC;

  const [bottom, first, second] = rungs;
  const firstReward = unlockRewards[first.uid];
  const secondReward = unlockRewards[second.uid];
  if (!firstReward || !secondReward) return CLAIM_GATED_STORY_GENERIC;

  // ladderRungs() only keeps rows with a usable threshold, so these always
  // parse; the guard is here so the narrative can never print "null points".
  const firstMin = parseMinScore(first.min_score);
  const secondMin = parseMinScore(second.min_score);
  if (firstMin === null || secondMin === null) return CLAIM_GATED_STORY_GENERIC;

  const bottomName = tierRowLabel(bottom, 0);
  const firstName = tierRowLabel(first, 1);
  // Thresholds are written plainly here, without the thousands grouping the
  // derived Max score column uses: prose and a numeric column are not the same
  // context, and the mockup sets both conventions side by side in one state.
  return (
    `Reached ${firstMin} points → the reward "${firstReward}" is in hand and ` +
    `they are still in ${bottomName}. Claimed it → moved to ${firstName}, and ` +
    `the next goal became "${secondReward}" at ${secondMin}. Dropped below ` +
    `${firstMin} — back to ${bottomName}, but the goal is still ` +
    `"${secondReward}": the ${firstName} reward has already been granted and ` +
    `is never granted twice. An unclaimed reward never expires.`
  );
}

// === Cycle lifetime =================================================== //

/**
 * Boards where a cycle ever finishes, and therefore the only ones the Cycle
 * lifetime section has anything to say about: an all_time board has one cycle
 * that never closes, so it has nothing to hold and nothing to sweep. The same
 * condition decides whether the section renders, whether its field is
 * validated and whether either value goes on the wire — one function so the
 * three can't drift.
 */
function hasCycleLifetime(windowKind: WindowKind): boolean {
  return (
    windowKind === "recurring" ||
    windowKind === "event" ||
    // A recurring_event window finishes at every close — and then the board
    // stands shut, which is precisely when a player comes back to read the
    // results. Migration 0075 widened lb_hold_finished_check to say so.
    windowKind === "recurring_event"
  );
}

/** Parsed retention, or null while the box holds something unsaveable. */
function parseRetentionDays(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

/** Long enough that a three-digit term is one question, short enough that the
   answer lands while the operator is still looking at the number. */
const RETENTION_IMPACT_DEBOUNCE_MS = 400;

/** The same wait, for the same reason, on a request that costs more: the
   coverage count is a full scan of the project's players (no index covers
   jsonb's key-exists operator), so a key clicked through on the way to
   another must not leave a scan running behind it. */
const STAT_KEY_COVERAGE_DEBOUNCE_MS = 400;

// === Anti-cheat guards ================================================ //

/**
 * A guard cap as its box holds it. Three readings, and they are three
 * different things: `null` — the box is empty, which is the guard switched
 * off; `undefined` — text that is not a cap at all, which is the validator's
 * business and never goes on the wire; a number — the cap itself.
 *
 * An empty box is emphatically not a 0. A cap of zero is a cap the server
 * enforces — `max_score_per_submit: 0` turns every scoring submit away — so
 * the two cannot share a spelling, and the box is the only place the
 * difference can be spelled at all.
 *
 * 0 is nonetheless a value, not a typo: nothing on the Go side validates the
 * caps, so a board can arrive here holding one, and refusing it would leave
 * that board unable to save its own NAME until the operator blanked a field
 * they never came here for. This section exists to make a stored guard
 * editable; a rule that cannot represent one of its stored values defeats it.
 */
function parseGuard(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

/** A stored cap as the box shows it — empty for a guard that is off. */
function guardText(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}

const GUARD_ERROR =
  "Must be a whole number, 0 or more — leave it empty to switch the guard off.";

// === Metadata ========================================================= //

/**
 * The metadata box read as a value. `empty` is its own answer rather than a
 * value of its own: a blank box means the board carries no metadata, which is
 * not the same as the JSON literal `null` somebody may legitimately type.
 *
 * An object, and only an object. The column is JSONB and nothing on the Go
 * side narrows it, but the SDK binds the field as a Dictionary
 * (socialhub-sdk Runtime/Models/Leaderboards/Leaderboard.cs), so a `[1, 2]`
 * saved from here breaks the response parse in every game client that reads
 * the board — a save that reports success and takes the SDK down with it. The
 * player meta editor turns away the same shapes for the same reason, and this
 * form was asked to follow it.
 */
type MetadataDraft =
  | { ok: true; empty: true }
  | { ok: true; empty: false; value: Record<string, unknown> }
  | { ok: false; error: string };

/** Carried as the whole message rather than a reason appended to one: the
   text is not about JSON that failed to parse — this JSON parsed. */
const METADATA_OBJECT_ERROR = "Metadata must be a JSON object.";

function parseMetadata(raw: string): MetadataDraft {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, empty: true };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed) as unknown;
  } catch (e) {
    // The parser's own message, which names the position it gave up at —
    // worth more on a half-typed object than any sentence written here. The
    // prefix is the cron field's, for the same reason: it says which box the
    // engine is talking about before the engine starts talking.
    return { ok: false, error: `Invalid JSON: ${(e as Error).message}` };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: METADATA_OBJECT_ERROR };
  }
  return { ok: true, empty: false, value: parsed as Record<string, unknown> };
}

/** Stored metadata as the editor shows it — pretty-printed, empty for none. */
function metadataText(v: unknown): string {
  if (v === undefined || v === null) return "";
  return JSON.stringify(v, null, 2);
}

// === Window length (window_kind=recurring_event) ====================== //

/** Smallest first, the order a duration is read in (mockup §2). */
const WINDOW_UNIT_OPTIONS = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

/** The length box as a number. `null` for anything that is not a whole number,
   0 or more — including an empty box, which is not a 0 but a length nobody has
   typed yet, and unsaveable either way. */
function parseDurationBox(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // Digits and nothing else, because `Number` is generous in ways a duration
  // box has no use for: it reads "1e3" as 1000 and "0x10" as 16, both of which
  // are whole non-negative numbers and neither of which anybody typed meaning
  // minutes. `type="number"` makes them hard to enter and not impossible — a
  // paste goes straight through it.
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * The box and its unit as the minutes that go on the wire — the number the
 * `= N min` line prints, the number `window_minutes` carries, and the number
 * the dirty flag is measured on, so the three can never disagree.
 *
 * The unit itself never travels: the API has one field for the length, and a
 * second representation of one value would have to be kept agreeing with it.
 *
 * `null` while the box holds nothing saveable.
 */
function parseWindowMinutes(
  state: Pick<FormState, "window_value" | "window_unit">,
): number | null {
  const value = parseDurationBox(state.window_value);
  if (value === null) return null;
  return value * WINDOW_UNIT_MINUTES[state.window_unit];
}

// === Cycle close ====================================================== //

/**
 * A cycle's close as the schedule rows print it: UTC, and saying so.
 *
 * Everything in this part of the form is on that clock — the server evaluates
 * cron in UTC, and the human line right under the box says "at 00:00 UTC" —
 * so a date rendered in the operator's own zone beside it would name a
 * different day than the one the schedule actually cuts on.
 */
/** "Mon, Sep 15" — the save bar's own short form of the next cut. Short on
   purpose: the bar is one line and the year is never the question there. */
const SHORT_CUT_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const CLOSE_AT_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

/** "Sun, 17 Aug 2026, 00:00 UTC", or null for a date there is none of. Null
   rather than a dash: the sentences around it are rewritten when the date is
   missing, not filled with a placeholder. */
function formatCloseAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return `${CLOSE_AT_FMT.format(t)} UTC`;
}

// === Event window ===================================================== //

/** Does this timestamp say which zone it is in? Mirrors SettingsTab's ZONED,
   because the two have to agree about what a bare string means. */
const ZONED = /([Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * An event-date box as an instant. A `datetime-local` value carries no zone,
 * and `Date.parse` reads a bare one as LOCAL time — but the host serialises
 * the very same string as UTC (SettingsTab.fromDateTimeLocal appends the `Z`)
 * and fills it from `toISOString()`. Read it the other way and every
 * comparison against the clock is out by the operator's offset: a form in
 * Moscow refuses to extend a running event by less than three hours, and one
 * in UTC-5 accepts an end four hours in the actual past, which the worker
 * then closes the cycle on at its next tick.
 *
 * A value that already names a zone is left to speak for itself — `initial`
 * can carry an RFC3339 string through from a host that does no conversion.
 *
 * NaN for a box that holds nothing usable, which every caller tests for: a
 * half-typed date is not a date in the past.
 */
function parseEventDateTime(value: string): number {
  const trimmed = value.trim();
  if (trimmed.length === 0) return NaN;
  return Date.parse(ZONED.test(trimmed) ? trimmed : `${trimmed}Z`);
}

/**
 * The event is over: this window has already decided who got what. `closing`
 * counts — the rewards are being handed out by it as we speak.
 */
function eventCycleFinished(status: CycleStatus | undefined): boolean {
  return status === "closing" || status === "closed" || status === "archived";
}

/** The event's start has happened, so it is a fact rather than a plan. */
function eventCycleStarted(status: CycleStatus | undefined): boolean {
  return status === "open" || eventCycleFinished(status);
}

/**
 * A stored timestamp as a locked row prints it. The `T` is the separator the
 * datetime-local box needs, not something anybody reads — a pill has no box to
 * satisfy, so it drops it (mockup §3). Nothing else about the value is
 * touched: re-formatting a date into the viewer's timezone here would print a
 * different day than the one the board is stored with.
 */
function displayDateTime(value: string): string {
  return value.replace("T", " ");
}

// === Defaults ========================================================= //
// Defaults documented in the task spec; we never seed cron/event_*/aggregator/
// bucket_size/tier_mode/tier_config. Metadata and the guards are seeded from
// `initial` now that the form has controls for them — before it had none, and
// carried whatever the board held through untouched.

interface FormState {
  ext_key: string;
  name: string;
  description: string;
  operator: Operator;
  sort: Sort;
  window_kind: WindowKind;
  scope: Scope;
  segmentation: Segmentation;
  submit_policy: SubmitPolicy;
  visibility: Visibility;
  /** The country view. Kept even while the board's scope or segmentation
     forbids it — see countrySplitAllowed, which is what stops it travelling —
     so flipping through the scopes does not throw the choice away. */
  country_split: boolean;
  /** Kept across a flip to sort=asc rather than cleared, exactly as
     country_split is kept across a scope it does not fit: the row goes grey,
     the value stays, and buildInput is what stops it travelling. Switching
     back to desc hands the operator their own choice again. */
  auto_enroll: boolean;
  /** Kept on a board that cannot take it, the way auto_enroll is kept under
     asc: the row shows Off and buildInput leaves it off the wire. A table
     coming back brings the `true` back on screen before any save, so the
     remembered choice is never sent unseen. */
  realtime: boolean;
  /** The three caps, as strings because they're bound straight to inputs. An
     empty one is a guard that is off — see parseGuard. */
  guard_max_score: string;
  guard_max_delta: string;
  guard_min_interval: string;
  /** Free-form JSON as typed. Parsed on validate and on submit, never in
     between: reformatting under the cursor would fight the typing. */
  metadata: string;
  cron: string;
  /** Which cycle a changed schedule starts cutting. Only asked for while the
     schedule actually differs from the saved one — on a recurring_event board
     that is either half of it, the cron or the length. */
  cron_apply: CronApply;
  /** window_kind=recurring_event: how long the window the cron starts stays
     open — one number, in the unit beside it. A string because it is bound
     straight to the input, and the wire value is neither this nor the unit:
     see parseWindowMinutes, which multiplies them into the minutes that
     travel. */
  window_value: string;
  /** What `window_value` counts. Never saved and never sent — the API holds
     the length as one number of minutes, and a second representation of it
     would have to be kept agreeing with the first. */
  window_unit: WindowUnit;
  event_start_at: string;
  event_end_at: string;
  guild_aggregator: GuildAggregator | "";
  /** scope=guild_aggregated only: who computes the guild's score. "submit" is
     the default and the board every guild_aggregated leaderboard has always
     been; "player_stat" hands the arithmetic to the server and takes the
     submit half of the form away with it. */
  guild_score_source: GuildScoreSource;
  /** The players.stats key "player_stat" aggregates. Kept while the source is
     "submit" — flipping the cards back and forth must not throw away a key
     that was already picked — and off the wire from there (see buildInput). */
  guild_stat_key: string;
  /** "member_contribution" only: how many members a team needs before it may
     enter the battle. A string because it is bound straight to an input; an
     empty box and a `0` both mean «no threshold», which is why the box starts
     empty rather than at a number nobody typed. */
  min_guild_members: string;
  /** "member_contribution" only: whether the team's placement grant goes to
     its contributors alone. `true` is the column's own default, and the switch
     shows it from the moment the block opens. */
  reward_contributors_only: boolean;
  /** "member_contribution" only: whether taking part in the battle is «is in
     the guild now» rather than «was in it when the team entered». Same default
     as the switch above, and shown from the moment the block opens; a board
     cut before the column existed arrives carrying an explicit `false`. */
  late_joiners_may_score: boolean;
  bucket_size: string;
  /** segmentation=personal only: how long one personal list is, the owner
     included. A string because it is bound straight to an input, and it is a
     field of its own rather than a second reading of bucket_size — the two
     count different things, and a board that switched from rooms to lists
     carries only this one. */
  roster_size: string;
  /** segmentation=personal only: the rules the list is picked by. Kept while
     the board is a bucket one — flipping the switch back and forth must not
     throw away layers already arranged — and off the wire from there, exactly
     as guild_stat_key is. */
  matchmaking: MatchmakingDraft;
  /** segmentation=tier only: whether each league is split into rooms. Off is
     the default, so a board that wants none is left alone rather than emptied
     out — the size input does not exist while this is false. */
  rooms: boolean;
  tier_mode: TierMode | "";
  /** Rows of the tier editor — the array order is the tier_config order. */
  tiers: TierDraft[];
  promote_top_k: string;
  demote_bottom_k: string;
  hold_finished_cycles: boolean;
  /** Days, as a string because it's bound straight to an input. 0 is a real
     term — «keep nothing» — so an empty box is an error rather than a
     shorthand for the default. */
  retention_days: string;
  /** Only populated in mode="create". Edit mode leaves this empty. */
  rewards: RewardDraft[];
}

function initialState(
  initial: Partial<CreateLeaderboardInput> | undefined,
  mode: "create" | "edit",
): FormState {
  const cfg = initial?.tier_config;
  const segmentation = ((initial?.segmentation as Segmentation) ||
    "none") as Segmentation;
  const tierMode = (initial?.tier_mode as TierMode | "") ?? "";
  // A stored length as the operator should read it back: the largest unit that
  // says it whole, which is the same split the list's badge prints. 4320
  // minutes are 3 days, and offering them as 4320 would be true and useless.
  const windowLength =
    initial?.window_minutes != null
      ? splitWindowLength(initial.window_minutes)
      : null;
  return {
    ext_key: initial?.ext_key ?? "",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    operator: (initial?.operator ?? "best") as Operator,
    sort: (initial?.sort ?? "desc") as Sort,
    window_kind: (initial?.window_kind ?? "all_time") as WindowKind,
    scope: ((initial?.scope as Scope) || "global") as Scope,
    segmentation,
    submit_policy: (initial?.submit_policy ?? "client") as SubmitPolicy,
    visibility: (initial?.visibility ?? "public") as Visibility,
    country_split: initial?.country_split ?? false,
    auto_enroll: initial?.auto_enroll ?? false,
    realtime: initial?.realtime ?? false,
    guard_max_score: guardText(initial?.guards?.max_score_per_submit),
    guard_max_delta: guardText(initial?.guards?.max_delta_per_submit),
    guard_min_interval: guardText(initial?.guards?.min_interval_sec),
    metadata: metadataText(initial?.metadata),
    cron: initial?.cron ?? "",
    // Nothing has been changed yet, and this is what an unchanged schedule
    // means anyway: the worker picks the new expression up at the next close.
    cron_apply: "next_cycle",
    // Left empty on a board that has no length rather than seeded with a
    // plausible one: a window nobody typed is not a window anybody chose. The
    // unit an empty box starts on is `minutes`, so the blank row reads as the
    // field's own name — window_minutes — and nobody has to guess what the
    // digits they are about to type will mean.
    window_value: windowLength ? String(windowLength.value) : "",
    window_unit: windowLength ? windowLength.unit : "minutes",
    event_start_at: initial?.event_start_at ?? "",
    event_end_at: initial?.event_end_at ?? "",
    guild_aggregator: (initial?.guild_aggregator as GuildAggregator | "") ?? "",
    // "" and an absent value both mean the board scores by submits — the
    // column's own DEFAULT, and the only thing a board created before t-100
    // can be.
    guild_score_source: (initial?.guild_score_source || "submit") as GuildScoreSource,
    guild_stat_key: initial?.guild_stat_key ?? "",
    // `null` and `0` are the same answer — no threshold — and the box says so
    // by being empty. Only a real number seeds it.
    min_guild_members:
      initial?.min_guild_members !== undefined &&
      initial?.min_guild_members !== null &&
      initial.min_guild_members !== 0
        ? String(initial.min_guild_members)
        : "",
    // The column's own DEFAULT, so a board created from this form without
    // touching the switch lands on exactly what the switch was showing.
    reward_contributors_only: initial?.reward_contributors_only ?? true,
    // The default a NEW board is created with, which lives in the server's
    // write paths and deliberately not in the schema (migration 0089 explains
    // why the column carries no DEFAULT). A board from before the column says
    // `false` out loud, so the switch draws the rule that board has been
    // playing by instead of the one new boards open on.
    late_joiners_may_score: initial?.late_joiners_may_score ?? true,
    bucket_size:
      initial?.bucket_size !== undefined && initial?.bucket_size !== null
        ? String(initial.bucket_size)
        : "",
    // A board that has no list length yet starts at the same ten a room does.
    // It is not a guess about the product: ten is what every other size on this
    // form defaults to, and the number is right there in a box.
    roster_size:
      initial?.roster_size !== undefined && initial?.roster_size !== null
        ? String(initial.roster_size)
        : "10",
    matchmaking: draftFromConfig(initial?.matchmaking_config),
    // A stored size on a tier board IS the rooms being on; there is no second
    // flag on the wire, and inventing one would let the two disagree.
    rooms: segmentation === "tier" && initial?.bucket_size != null,
    // The seg control shows score_bucket as the active mode as soon as
    // segmentation=tier, so the state has to say the same thing — otherwise
    // the form validates and submits a mode nobody selected.
    tier_mode: segmentation === "tier" && !tierMode ? defaultTierMode(initial?.operator) : tierMode,
    // Seeded rows count as "saved" only in edit mode — that's what locks
    // their key. A create form pre-filled from a template must stay fully
    // editable.
    tiers: (cfg?.tiers ?? []).map((t) => {
      const min =
        t.min_score !== undefined && t.min_score !== null ? String(t.min_score) : "";
      return {
        uid: `tier-${++tierUidSeq}`,
        key: t.key,
        name: t.name,
        min_score: min,
        saved: mode === "edit",
        savedMinScore: mode === "edit" ? min : null,
        keyDirty: true,
      };
    }),
    promote_top_k:
      cfg?.promote_top_k !== undefined && cfg?.promote_top_k !== null
        ? String(cfg.promote_top_k)
        : "",
    demote_bottom_k:
      cfg?.demote_bottom_k !== undefined && cfg?.demote_bottom_k !== null
        ? String(cfg.demote_bottom_k)
        : "",
    hold_finished_cycles: initial?.hold_finished_cycles ?? false,
    // 90 is the column's own DEFAULT (migration 0049), so a board created
    // from this form without touching the field lands on exactly the number
    // the box showed while it was being filled in.
    retention_days:
      initial?.retention_days !== undefined && initial?.retention_days !== null
        ? String(initial.retention_days)
        : "90",
    rewards: [],
  };
}

/**
 * The persisted thresholds, as one comparable string. `initial` is rebuilt by
 * the host on every render, so its identity says nothing about whether the
 * board itself has changed; this does.
 */
function persistedTierSignature(
  initial: Partial<CreateLeaderboardInput> | undefined,
): string {
  return (initial?.tier_config?.tiers ?? [])
    .map((t) => `${t.key}=${t.min_score ?? ""}`)
    .join("|");
}

/**
 * Re-baseline the rows against a tier_config that has just come back from the
 * server — a save landed, and the form was not remounted. A row whose key is
 * in the new config is persisted now: its threshold is the saved one, and its
 * key is locked, exactly as if the page had been reopened. Everything the
 * operator typed stays put; only the "what is saved" side moves, so a
 * shifted-threshold warning retires with the save that made it true instead of
 * outliving it. Rows absent from the config were added in this session and are
 * left alone.
 */
function adoptPersistedTiers(rows: TierDraft[], persisted: Tier[]): TierDraft[] {
  return rows.map((t) => {
    const match = persisted.find((p) => p.key === t.key.trim());
    if (!match) return t;
    const min =
      match.min_score !== undefined && match.min_score !== null
        ? String(match.min_score)
        : "";
    if (t.saved && t.savedMinScore === min) return t;
    return { ...t, saved: true, savedMinScore: min };
  });
}

// === Option metadata ================================================== //

interface OptionMeta<T extends string> {
  value: T;
  glyph: string;
  title: string;
  desc: string;
  /**
   * The card is on the page but cannot be picked — the board this form is
   * describing forbids exactly this value, and the reason lives in another
   * section. Used by `segmentation=tier` on scope=guild_aggregated (a league is
   * a PLAYER's league) and by `window_kind=all_time` on
   * guild_score_source=member_contribution.
   *
   * The form's usual rule is «removed, not greyed out», and these two are its
   * deliberate exceptions for the same reason auto_enroll's row is: the choice
   * that forbids them was made in a section the operator can see, the card is
   * one of three that read as a set, and a set that silently loses a member
   * reads as a broken page rather than as a board half decided. `title` on the
   * card says which section to go and change.
   */
  disabled?: boolean;
  /** Tooltip of a disabled card — why this one cannot be picked. */
  disabledReason?: string;
}

const OPERATOR_OPTIONS: OptionMeta<Operator>[] = [
  { value: "best", glyph: "↓", title: "best", desc: "Higher score wins — keeps the maximum." },
  { value: "worst", glyph: "↑", title: "worst", desc: "Lower score wins — keeps the minimum." },
  { value: "increment", glyph: "Σ", title: "increment", desc: "Accumulates submitted values." },
  { value: "set", glyph: "=", title: "set", desc: "Always overwrites the previous value." },
];

/**
 * Ordered from «never cut» to «cut most often», which is why the fourth card
 * goes last rather than beside the `recurring` it shares a cron with. The
 * glyph is `event`'s diamond with a dot inside — the same window, repeating.
 */
const WINDOW_OPTIONS: OptionMeta<WindowKind>[] = [
  { value: "all_time", glyph: "∞", title: "all_time", desc: "No reset — scores persist forever." },
  { value: "recurring", glyph: "↻", title: "recurring", desc: "Resets on a cron schedule." },
  { value: "event", glyph: "◇", title: "event", desc: "Fixed start_at → end_at window." },
  {
    value: "recurring_event",
    glyph: "◈",
    title: "recurring_event",
    desc: "A timed window on a schedule — closed in between.",
  },
];

const SCOPE_OPTIONS: OptionMeta<Scope>[] = [
  { value: "global", glyph: "G", title: "global", desc: "All players ranked together." },
  { value: "guild_internal", glyph: "⌂", title: "guild_internal", desc: "Members ranked inside each guild." },
  { value: "guild_aggregated", glyph: "Σ", title: "guild_aggregated", desc: "Guilds ranked by aggregated member scores." },
];

/**
 * The one new control of t-100, and the heaviest in the Scope section: it
 * decides whether the game submits to this board at all, and takes a third of
 * the form with it. Radio cards rather than the reveal block's usual inline
 * seg control for exactly that reason — `scope`, `operator`, `window_kind` and
 * `segmentation` are all cards, and this belongs with them, not with
 * `guild_aggregator`.
 */
const GUILD_SCORE_SOURCE_OPTIONS: OptionMeta<GuildScoreSource>[] = [
  {
    value: "submit",
    glyph: "↑",
    title: "submit",
    desc: "Members send scores; each submit folds into their guild. A member who leaves keeps the guild's total.",
  },
  {
    value: "player_stat",
    glyph: "ƒ",
    title: "player_stat",
    desc: "The server scores each guild from a stat on its members' profiles.",
  },
  {
    value: "member_contribution",
    glyph: "Σ",
    title: "member_contribution",
    desc: "The guild's score is the sum of its participating members' own contributions to this cycle.",
  },
];

/**
 * Card copy of the segmentation group. The `bucket` and `tier` descriptions
 * differ between a player board and a guild one — a room seat is taken by a
 * team there, and the ladder is a player's league — so the group is built per
 * scope rather than declared once. See `segmentationOptions`.
 */
const SEGMENTATION_OPTIONS: OptionMeta<Segmentation>[] = [
  { value: "none", glyph: "–", title: "none", desc: "Single flat ranking." },
  { value: "bucket", glyph: "▦", title: "bucket", desc: "Fixed-size matchmaking pools." },
  { value: "tier", glyph: "T", title: "tier", desc: "Ranked ladder." },
  {
    value: "personal",
    glyph: "◆",
    title: "personal",
    desc: "Every player gets their own list of opponents.",
  },
];

/** Why personal lists are shut on a board of guilds: there is nobody to put on
   one. A list is assembled from PLAYERS, and the rows here are teams. */
const PERSONAL_ON_GUILDS_REASON =
  "A personal list is assembled from players, and the rows on this board are guilds — there is nobody to put on one.";

/** How a layer orders the candidates it found. Same three everywhere, so the
   list is declared once rather than per row. */
const PREFER_OPTIONS: { value: MatchmakingPrefer; label: string }[] = (
  ["fresher_last_seen", "same_timezone", "random"] as MatchmakingPrefer[]
).map((v) => ({ value: v, label: PREFER_LABEL[v] }));

/** What the pace layer does when its band comes up short. Doubling is the only
   move — what the operator chooses is how many times it may happen before the
   layer gives its slots to the one below. */
const WIDEN_OPTIONS = [
  { value: "3", label: "×2 up to 3 steps" },
  { value: "5", label: "×2 up to 5 steps" },
  { value: "0", label: "never" },
];

// Why each of the two shut cards is shut, per the board it is shut on. One
// sentence apiece rather than one for «a guild board»: the rules have
// different reasons and different ways out, and a card that says the wrong one
// sends the operator to change the wrong section.

/** The ladder, on any board of guilds. Never available — the entity is wrong. */
const TIER_ON_GUILDS_REASON =
  "A league is a player's league — leaderboard_player_tiers has no row for a guild.";

/** …and on the one guild board that has somewhere else to put the operator. */
const TIER_ON_CONTRIBUTION_REASON = `${TIER_ON_GUILDS_REASON} Rank teams in rooms instead.`;

/** Rooms, on a guild board scored by anything but the team battle. */
const BUCKET_NEEDS_CONTRIBUTION_REASON =
  "A room seat is taken when a team ENTERS a cycle, which only guild_score_source = member_contribution has teams doing. On this source every guild of the project simply sits on the board, so the rooms would be filled by nobody playing. Pick that source, or keep none.";

/**
 * The segmentation cards this board may take, and why the others are shut.
 *
 * On `guild_aggregated` the rows are teams, and what is open to them depends
 * on the SOURCE as well as on the scope:
 *
 *  - `member_contribution` — `none` and `bucket` both, because a team takes a
 *    room seat exactly the way a player does: it enters the cycle;
 *  - `submit` / `player_stat` — `none` alone. Nothing enters anything there;
 *    the board is every guild of the project, and rooms cut across a list
 *    nobody is competing in.
 *
 * `tier` is shut on all three: the ladder a tier board moves entities along is
 * a player's, and no migration gives a guild a row in it.
 */
function segmentationOptions(
  scope: Scope,
  source: GuildScoreSource,
): OptionMeta<Segmentation>[] {
  if (scope !== "guild_aggregated") return SEGMENTATION_OPTIONS;
  const contribution = source === "member_contribution";
  return [
    { value: "none", glyph: "–", title: "none", desc: "Single flat ranking of every guild." },
    {
      value: "bucket",
      glyph: "▦",
      title: "bucket",
      desc: "Fixed-size rooms. A team takes a seat when it enters the cycle.",
      disabled: !contribution,
      disabledReason: contribution ? undefined : BUCKET_NEEDS_CONTRIBUTION_REASON,
    },
    {
      value: "tier",
      glyph: "T",
      title: "tier",
      desc: "Ranked ladder.",
      disabled: true,
      disabledReason: contribution
        ? TIER_ON_CONTRIBUTION_REASON
        : TIER_ON_GUILDS_REASON,
    },
    {
      value: "personal",
      glyph: "◆",
      title: "personal",
      desc: "Every player gets their own list of opponents.",
      disabled: true,
      disabledReason: PERSONAL_ON_GUILDS_REASON,
    },
  ];
}

/** Whether this board may be segmented that way at all — the one reading of
   the rules above, so the cards and `confineGuildSegmentation` cannot drift. */
function segmentationAllowed(
  scope: Scope,
  source: GuildScoreSource,
  segmentation: Segmentation,
): boolean {
  const opt = segmentationOptions(scope, source).find(
    (o) => o.value === segmentation,
  );
  return !!opt && !opt.disabled;
}

/**
 * The window cards for one source. `member_contribution` refuses `all_time`:
 * the whole mechanic rests on "was in the team when it entered", and in an
 * endless cycle that sentence has no moment to be true at.
 */
function windowOptions(state: FormState): OptionMeta<WindowKind>[] {
  if (!isMemberContribution(state)) return WINDOW_OPTIONS;
  return WINDOW_OPTIONS.map((o) =>
    o.value === "all_time"
      ? { ...o, disabled: true, disabledReason: ALL_TIME_ON_CONTRIBUTION_REASON }
      : o,
  );
}

const ALL_TIME_ON_CONTRIBUTION_REASON =
  "In an endless cycle «was in the team when it entered» degenerates, so all_time is refused with this source at create.";

// === Copy for the locked fields ======================================= //
// What each frozen value means for THIS board, and where the way out is. A
// locked field has no options to compare, so the one line under it is all the
// operator gets — it says what the board does now, not what the enum could
// have been.

const WINDOW_LOCKED_HELPER: Record<WindowKind, string> = {
  all_time:
    "Scores are never cut — one cycle that stays open forever. To reset them on a schedule, or to run a one-off event, create a new leaderboard.",
  recurring:
    "Scores are cut into cycles by the schedule below. To switch to a one-off event window or an all-time board, create a new leaderboard.",
  event:
    "Scores are cut by the one window below. To switch to a repeating schedule or an all-time board, create a new leaderboard.",
  recurring_event:
    "Scores are cut by a window that opens on the schedule below and closes after the time next to it. To switch to a plain repeating schedule, a one-off event or an all-time board, create a new leaderboard.",
};

const SCOPE_LOCKED_HELPER: Record<Scope, string> = {
  global: "Every player of the project ranks in one list.",
  guild_internal:
    "Members rank against each other inside their own guild — one list per guild.",
  guild_aggregated:
    "Guilds rank against each other, each by its members' aggregated scores.",
};

/**
 * The source is fixed at create for the same reason `scope` is: every row on
 * the board was written by it. UpdateParams carries no field for it at all, so
 * a PATCH cannot even name one — hence a pill rather than a control whose
 * changes would be refused.
 */
const GUILD_SCORE_SOURCE_LOCKED_HELPER: Record<GuildScoreSource, string> = {
  player_stat:
    "The server scores this board from member profiles. To rank guilds by what players submit, create a new leaderboard.",
  submit:
    "Members' own submits are what score this board. To have the server score it from a stat on their profiles, create a new leaderboard.",
  member_contribution:
    "A team's score is the sum of what its participating members contributed to the running cycle. To rank guilds another way, create a new leaderboard.",
};

const SEGMENTATION_LOCKED_HELPER: Record<Segmentation, string> = {
  none: "Everybody is in one flat ranking. To split the board into rooms or a tier ladder, create a new leaderboard.",
  bucket:
    "Rows already written belong to the room they were scored in, and the size below is what cuts the rooms — so it is fixed with the mode.",
  tier: "Rows already written carry the league they were scored in. The rooms below are the one part of this section that still moves.",
  personal:
    "Every player gets their own list of opponents. Lists are asymmetric: a player can be on your list while you are not on theirs.",
};

/**
 * The one shape an auto-scored board is confined to: CHECK
 * lb_guild_score_source_window_check allows `all_time` alone, because the
 * score is a snapshot of the present and a cycle reset would bring the same
 * number straight back.
 *
 * Applied to the WHOLE next state rather than to the field that moved, because
 * either half of `isAutoScored` can turn the auto mode on — the source cards
 * and the scope cards both. Enforced on one of them only, the other assembles
 * `{guild_score_source: "player_stat", window_kind: "recurring"}`: create
 * refuses the pair with a 400, and the operator has nothing to act on, because
 * the Window section is by then showing a pill over the value it is being
 * refused for.
 */
function confineAutoScoredWindow(next: FormState): FormState {
  return isAutoScored(next) && next.window_kind !== "all_time"
    ? { ...next, window_kind: "all_time" }
    : next;
}

/**
 * The window a member_contribution board may NOT have, and where it lands
 * instead. The mirror image of `confineAutoScoredWindow`: `all_time` is the
 * form's own starting value and the one this source refuses, so leaving it in
 * place would assemble a pair create answers 400 for while the Window section
 * shows a card the operator picked and the Guild-scoring reveal shows a badge
 * saying that very card is refused.
 *
 * `recurring` is the successor, not a guess: it is the plainest of the three
 * the source allows, and the only one whose schedule the operator can go on to
 * type without a date picker. Applied to the WHOLE next state for the same
 * reason its twin is — either the source cards or the scope cards can turn the
 * mode on.
 */
function confineContributionWindow(next: FormState): FormState {
  return isMemberContribution(next) && next.window_kind === "all_time"
    ? { ...next, window_kind: "recurring" }
    : next;
}

/**
 * The segmentations a board of GUILDS cannot have, folded back to the one it
 * always can. Which those are depends on the source as well as the scope —
 * `segmentationAllowed` is the single reading of that.
 *
 * The cards are disabled while the pair says so, which stops the click; this
 * stops the other two ways in, where the segmentation was picked FIRST and the
 * scope or the source moved afterwards. Left alone the form then showed a
 * selected card the group had greyed out, and sent a pair create refuses.
 *
 * Everything the tier branch owns leaves with it, exactly as the segmentation
 * cards' own handler does it: the rooms switch, the mode, and the threshold
 * reward drafts that have nothing left to open.
 */
function confineGuildSegmentation(next: FormState): FormState {
  if (segmentationAllowed(next.scope, next.guild_score_source, next.segmentation)) {
    return next;
  }
  return {
    ...next,
    // `none` is the one segmentation every board can have, and the one every
    // guild board had before this feature.
    segmentation: "none",
    rooms: false,
    tier_mode: "",
    rewards: resetThresholdDrafts(next.rewards),
  };
}

/** Every confinement this form applies, in one place — see `updateMany`. */
function confine(next: FormState): FormState {
  return confineGuildSegmentation(
    confineContributionWindow(confineAutoScoredWindow(next)),
  );
}

// === Validation ======================================================= //

/** Per-input errors of one tier row, so the bad field is the one that
   lights up rather than the whole section. */
interface TierRowError {
  key?: string;
  name?: string;
  min_score?: string;
}

interface FormErrors {
  ext_key?: string;
  name?: string;
  cron?: string;
  /** The length of a recurring_event window. One message for the number and
     the unit beside it, because one value is what they hold between them. */
  window_minutes?: string;
  event_start_at?: string;
  event_end_at?: string;
  guild_aggregator?: string;
  guild_stat_key?: string;
  min_guild_members?: string;
  bucket_size?: string;
  /** The personal list's length. */
  roster_size?: string;
  /** The matchmaking card's own messages, nested rather than flattened: the
     card has a field per rule and a rule per row, and a single string here
     would have to name which of them it is about. */
  matchmaking?: MatchmakingErrors;
  tier_mode?: string;
  tier_config?: string;
  promote_top_k?: string;
  demote_bottom_k?: string;
  retention_days?: string;
  /** One key per guard box, named after the state field it belongs to, so
     `update()` clears the message the moment that box is retyped. */
  guard_max_score?: string;
  guard_max_delta?: string;
  guard_min_interval?: string;
  metadata?: string;
  rewards?: string;
  /** Per-row reward errors keyed by index. */
  rewardRows?: Record<number, string>;
  /** Per-row tier errors keyed by index. */
  tierRows?: Record<number, TierRowError>;
}

const EXT_KEY_RE = /^[a-z][a-z0-9_]*$/;

/**
 * validate.go:125 rejects this pair: score_bucket derives the tier from the
 * incoming value on every submit, so an accumulating operator would let the
 * stored score drift past the threshold its tier_key was picked from.
 */
const SCORE_BUCKET_INCREMENT_ERROR =
  "score_bucket is unavailable: the Scoring section has operator = increment. Pick promotion, or change the operator.";

/**
 * What the form's own props say about the board behind it — the parts the
 * rules depend on. Passed in rather than read off `state` because none of it
 * is something the operator can type: which fields are open to them at all is
 * decided by the board's status and by its cycle.
 */
interface ValidationContext {
  /** ext_key is typeable — a board being created, or an archived one. */
  extKeyEditable: boolean;
  /** Status of the event board's cycle, where the host knows it. */
  eventCycleStatus?: CycleStatus;
  /** The end in the box is not the end the board is stored with. Rules that
     refuse a date only refuse a date somebody MOVED — see eventEndDirty. */
  eventEndDirty: boolean;
}

function validate(
  state: FormState,
  mode: "create" | "edit",
  ctx: ValidationContext,
): FormErrors {
  const errors: FormErrors = {};

  if (state.name.trim().length === 0) {
    errors.name = "Name is required.";
  }

  // Checked wherever it can be typed, which is no longer only on create: an
  // archived board opens the key up again, and the shape it has to keep is the
  // same one — it is still what a snapshot import matches boards by.
  if (ctx.extKeyEditable) {
    const ek = state.ext_key.trim();
    if (!ek || !EXT_KEY_RE.test(ek)) {
      errors.ext_key =
        "Ext key must start with a lowercase letter and contain only lowercase, digits, and underscores.";
    }
  }

  if (state.window_kind === "recurring") {
    if (state.cron.trim().length === 0) {
      errors.cron = "Cron is required for recurring window.";
    } else {
      const cronCheck = analyseCron(state.cron);
      if (cronCheck.kind === "invalid") {
        errors.cron = `Invalid cron: ${cronCheck.reason}`;
      }
    }
  }

  if (state.window_kind === "recurring_event") {
    // The cron half is judged exactly as `recurring`'s is — same expression,
    // same parser. Only what a tick MEANS differs, and that is not syntax.
    if (state.cron.trim().length === 0) {
      errors.cron = "Cron is required for a recurring event window.";
    } else {
      const cronCheck = analyseCron(state.cron);
      if (cronCheck.kind === "invalid") {
        errors.cron = `Invalid cron: ${cronCheck.reason}`;
      }
    }
    // The length half. Deliberately NOT checked against the gap between two
    // starts: that gap is walked by the same cron library the cycle worker
    // uses, on the server, and a second opinion computed here would be a
    // number shown to the operator and a guess in fact. What this can settle
    // is only whether the box holds a length at all.
    const minutes = parseWindowMinutes(state);
    if (state.window_value.trim().length > 0 && minutes === null) {
      errors.window_minutes = "The window length must be a whole number.";
    } else if (minutes === null || minutes < 1) {
      // An empty box and a typed 0 are the same answer — no length yet — and
      // the message names the floor the server holds rather than the shape of
      // the mistake.
      errors.window_minutes =
        "Set how long the window stays open — one minute at least.";
    }
  }

  if (state.window_kind === "event") {
    if (
      state.event_start_at.trim().length === 0 ||
      state.event_end_at.trim().length === 0
    ) {
      errors.event_start_at = "Event window dates are required.";
    } else {
      const start = parseEventDateTime(state.event_start_at);
      const end = parseEventDateTime(state.event_end_at);
      if (Number.isFinite(start) && Number.isFinite(end) && end <= start) {
        errors.event_end_at = "Event end must be after event start.";
      } else if (
        // A running event can be wound up early, but not retroactively: an end
        // in the past would ask the worker to close a cycle at a moment that
        // has already gone by, and the players would find the event over with
        // no warning at all. The start is locked in this state, so this is the
        // only date that can reach backwards.
        //
        // Only against an end the operator MOVED. A cycle stays `open` until
        // the worker gets round to it, so a board whose window ran out an hour
        // ago still reports one — and read off the value rather than the edit,
        // this rule would refuse to save the description of such a board over
        // a date the server itself handed the form. Same for anyone who opened
        // the tab five minutes before the end and typed for ten.
        ctx.eventEndDirty &&
        ctx.eventCycleStatus === "open" &&
        Number.isFinite(end) &&
        end < Date.now()
      ) {
        errors.event_end_at =
          "The event is running — its end can't be moved into the past.";
      }
    }
  }

  if (state.scope === "guild_aggregated" && isMemberContribution(state)) {
    // The aggregator and the operator are not asked for here at all — the
    // server fixes the pair (sum ↔ increment) — so the only thing left to
    // check is the source's own threshold. Empty is a real answer: no
    // threshold, the same as 0.
    const raw = state.min_guild_members.trim();
    if (raw !== "") {
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) {
        errors.min_guild_members =
          "Minimum team size must be a whole number, 0 or more. Leave it empty for no threshold.";
      }
    }
  } else if (state.scope === "guild_aggregated") {
    if (!state.guild_aggregator) {
      errors.guild_aggregator = "Guild aggregator is required.";
    } else if (!isAutoScored(state)) {
      // The pair rule only has two sides to check while the operator picks
      // one of them. On an auto-scored board `operator` is not a field at all
      // — the server derives it from the aggregator (sum↔increment,
      // max↔best), so the pair is correct by construction and holding the
      // form's stale operator against it would refuse a valid board.
      const pair =
        (state.guild_aggregator === "sum" && state.operator === "increment") ||
        (state.guild_aggregator === "max" && state.operator === "best");
      if (!pair) {
        errors.guild_aggregator =
          "Guild aggregator must pair with operator (sum↔increment, max↔best).";
      }
    }
    // Required, and the one thing on an auto board that cannot be defaulted:
    // there is no sensible key to guess, and a board saved without one would
    // be refused by the server anyway.
    if (isAutoScored(state) && state.guild_stat_key.trim() === "") {
      errors.guild_stat_key = "Pick the stat this board scores guilds by.";
    }
  }

  // The same rule, and deliberately the same wording, in both places the size
  // can be typed: as the segmentation itself on a bucket board, and as the
  // room size on a tier board with rooms switched on.
  if (state.segmentation === "bucket" || (state.segmentation === "tier" && state.rooms)) {
    const n = Number(state.bucket_size);
    if (!Number.isInteger(n) || n < 2) {
      errors.bucket_size = "Bucket size must be an integer ≥ 2.";
    }
  }

  // The personal board's own two fields, validated in both modes for the reason
  // the tier rows below are: the same form creates the board and switches a
  // live one onto lists, and a refusal the server would answer with
  // leaderboard_invalid_segmentation is one the operator should read under the
  // field rather than after a save.
  if (state.segmentation === "personal") {
    const n = Number(state.roster_size);
    if (!Number.isInteger(n) || n < 2) {
      errors.roster_size = "List size must be an integer ≥ 2.";
    }
    const mm = validateMatchmaking(state.matchmaking, n);
    if (hasMatchmakingErrors(mm)) {
      errors.matchmaking = mm;
    }
  }

  // Tier rows are validated in BOTH modes: the same form creates the board,
  // and an empty tier_config is a 400 (leaderboard_invalid_tier_config) that
  // the operator would otherwise only discover after submitting.
  if (state.segmentation === "tier") {
    if (!state.tier_mode) {
      errors.tier_mode = "Tier mode is required.";
    } else if (state.tier_mode === "score_bucket" && state.operator === "increment") {
      errors.tier_mode = SCORE_BUCKET_INCREMENT_ERROR;
    }

    if (state.tiers.length < 2) {
      errors.tier_config = "At least 2 tiers are required.";
    }

    // Duplicates are flagged on every row that shares the key, not just the
    // second one — either of them may be the typo.
    const keyCounts = new Map<string, number>();
    for (const t of state.tiers) {
      const k = t.key.trim();
      if (k) keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
    }

    const tierRows: Record<number, TierRowError> = {};
    state.tiers.forEach((t, i) => {
      const row: TierRowError = {};
      const key = t.key.trim();
      if (!key) {
        row.key = "Key is required.";
      } else if ((keyCounts.get(key) ?? 0) > 1) {
        row.key = `Key "${key}" is already used by another row — keys must be unique.`;
      }
      if (t.name.trim().length === 0) {
        row.name = "Tier name is required.";
      }
      if (parseMinScore(t.min_score) === null) {
        // Both threshold modes need one on every row — claim_gated because a
        // rung with no threshold can never be crossed, so its reward would
        // never be granted.
        if (state.tier_mode === "score_bucket") {
          row.min_score = "score_bucket needs a min_score on every tier.";
        } else if (state.tier_mode === "claim_gated") {
          row.min_score = "Min score is required in claim_gated mode.";
        }
      }
      if (Object.keys(row).length > 0) tierRows[i] = row;
    });
    if (Object.keys(tierRows).length > 0) {
      errors.tierRows = tierRows;
    }

    if (state.tier_mode === "promotion") {
      const top = Number(state.promote_top_k);
      if (!state.promote_top_k.trim() || !Number.isInteger(top) || top <= 0) {
        errors.promote_top_k = "promote_top_k must be a positive integer.";
      }
      const bottom = Number(state.demote_bottom_k);
      if (!state.demote_bottom_k.trim() || !Number.isInteger(bottom) || bottom <= 0) {
        errors.demote_bottom_k = "demote_bottom_k must be a positive integer.";
      }
    }
  }

  // Only where the section exists. An all_time board never shows the box, and
  // whatever a draft left in it must not be able to block a submit.
  if (hasCycleLifetime(state.window_kind)) {
    const days = parseRetentionDays(state.retention_days);
    if (days === null) {
      errors.retention_days = "Retention must be an integer ≥ 0.";
    } else if (days === 0 && state.hold_finished_cycles) {
      // The server refuses the pair, and it is right to: a cycle deleted the
      // moment it closes takes the score row — and with it the mark that holds
      // the player — along with it. Caught here so the operator reads why
      // rather than a 400.
      errors.retention_days =
        "0 cannot be combined with Hold finished cycles — a cycle deleted at close holds nobody.";
    }
  }

  // One message per box rather than one for the section: three caps sit under
  // each other, and "guard values must be positive integers" leaves the
  // operator to work out which of the three it is about.
  //
  // Skipped wholesale on an auto-scored board, where the section is not on the
  // page at all: half-typed text left behind before the source was switched
  // would otherwise block the save with three messages pointing at nothing —
  // and the caps do not travel from there either (see buildGuards).
  if (!isAutoScored(state)) {
    if (parseGuard(state.guard_max_score) === undefined) {
      errors.guard_max_score = GUARD_ERROR;
    }
    if (parseGuard(state.guard_max_delta) === undefined) {
      errors.guard_max_delta = GUARD_ERROR;
    }
    if (parseGuard(state.guard_min_interval) === undefined) {
      errors.guard_min_interval = GUARD_ERROR;
    }
  }

  const metadata = parseMetadata(state.metadata);
  if (!metadata.ok) {
    errors.metadata = metadata.error;
  }

  if (mode === "create" && state.rewards.length > 0) {
    const rowErrs: Record<number, string> = {};
    for (let i = 0; i < state.rewards.length; i++) {
      const r = state.rewards[i];
      const rowErr = validateRewardDraft(r, state.tiers);
      if (rowErr) rowErrs[i] = rowErr;
    }
    if (Object.keys(rowErrs).length > 0) {
      errors.rewardRows = rowErrs;
      errors.rewards = "One or more rewards have errors — fix them before submitting.";
    }
  }

  return errors;
}

function validateRewardDraft(r: RewardDraft, tiers: TierDraft[]): string | null {
  if (r.name.trim().length === 0) return "Name is required.";

  if (r.type === "tier_threshold") {
    // ValidateRewardCreate rejects a threshold reward without a tier_key, and
    // a dangling uid resolves back to "all tiers" in buildRewardInputs — so
    // both cases are the same mistake and get the same message.
    const bound = tiers.find((t) => t.uid === r.tier_uid);
    if (!bound || bound.key.trim().length === 0) {
      return 'tier_threshold needs a specific tier — "All tiers" is not allowed.';
    }
    // The picker never offers the starting league, but the ladder can move
    // under a draft that was bound before: drop another row's threshold below
    // this one and the bound tier becomes the bottom. ValidateRewardCreate
    // answers 400 leaderboard_invalid_reward_range to that — this says which
    // row is at fault and why.
    if (bound.uid === bottomTier(tiers)?.uid) {
      return "The bottom tier is where every player starts, so a threshold reward bound to it can never be granted. Pick a tier above it.";
    }
    return validateRewardPayload(r.payload);
  }

  const from = Number(r.range_from);
  const to = Number(r.range_to);
  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    return "Range must be two integers.";
  }
  if (from < 0 || to < 0) {
    return "Range values must be non-negative.";
  }
  if (from > to) {
    return "Range start must be ≤ end.";
  }
  if (r.type === "rank_range") {
    if (from < 1) return "Rank range must start at 1 or above.";
  } else {
    if (from > 100 || to > 100) return "Percentile values must be in 0..100.";
  }

  return validateRewardPayload(r.payload);
}

function validateRewardPayload(raw: string): string | null {
  const parsed = parseRewardPayload(raw);
  return parsed.ok ? null : parsed.error;
}

// === Rewards file ===================================================== //
// A file exported from another board, read into the drafts above. There is no
// board here to import into and nothing is saved, so the whole exchange is
// local: the file is checked against the rows of step 4 and becomes drafts, or
// it is turned away with the reasons.

/**
 * The tier key a file row asks for, or "" when it asks for none. Only reached
 * for a row reviewRewardsFile has already found a text `tier_key` on, so the
 * guard is a narrowing rather than the silent fallback it would otherwise be.
 */
function entryTierKey(e: RewardsFileEntry): string {
  return typeof e.tier_key === "string" ? e.tier_key.trim() : "";
}

/**
 * Whether the row spells out either bound of that range form. A hand-edited
 * file may write an absent bound as `null`, which is the same as not writing
 * it — but a `0` is a bound, so the test is against null, not falsiness.
 */
function hasRankRange(e: RewardsFileEntry): boolean {
  return (e.rank_from ?? null) !== null || (e.rank_to ?? null) !== null;
}

function hasPercentileRange(e: RewardsFileEntry): boolean {
  return (
    (e.percentile_from ?? null) !== null || (e.percentile_to ?? null) !== null
  );
}

/**
 * Whether `tier_key` arrived as text. The declaration says it is, but the file
 * is hand-edited and nothing enforced that: anything else reads as "no tier"
 * downstream, which would turn a reward for one league into a reward for all
 * of them without saying so.
 */
function tierKeyIsText(e: RewardsFileEntry): boolean {
  const raw: unknown = e.tier_key;
  return raw === undefined || raw === null || typeof raw === "string";
}

/**
 * The range fields this row fills in that its own kind has no use for.
 *
 * A kind is derived from which range the row populates, and it is derived from
 * the `_from` bound alone — so every other bound on the row is read by nobody
 * and dropped by everybody: a `rank_to` on a threshold reward, a `rank_from`
 * on a row that also carries `percentile_from`, either bound of the range the
 * kind didn't win. Dropping one silently is the same tacit rewrite as reading
 * a numeric `tier_key` as "All tiers", so the row is turned away instead, and
 * this reports what it was that had no place.
 */
function strayRangeFields(
  e: RewardsFileEntry,
  kind: RewardsFileEntryKind,
): string[] {
  const stray: string[] = [];
  if (kind !== "rank_range" && hasRankRange(e)) stray.push("rank_from/rank_to");
  if (kind !== "percentile" && hasPercentileRange(e)) {
    stray.push("percentile_from/percentile_to");
  }
  return stray;
}

/** A range bound as the editor's input holds it. A hand-edited file may spell
   an absent bound `null`, which String() would render as the text "null". */
function boundText(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}

/**
 * One file row as a draft row. The tier is resolved to the row's uid rather
 * than kept as a key, so an imported reward follows a rename on step 4 exactly
 * as a hand-typed one does (see RewardDraft.tier_uid).
 */
function draftFromFileEntry(
  e: RewardsFileEntry,
  tiers: TierDraft[],
  tiered: boolean,
): RewardDraft {
  const kind = fileRewardKind(e);
  const key = entryTierKey(e);
  const bound = tiered && key ? tiers.find((t) => t.key.trim() === key) : undefined;
  return {
    name: e.name,
    type: kind,
    tier_uid: bound?.uid ?? ALL_TIERS,
    range_from:
      kind === "percentile"
        ? boundText(e.percentile_from)
        : kind === "rank_range"
          ? boundText(e.rank_from)
          : "",
    range_to:
      kind === "percentile"
        ? boundText(e.percentile_to)
        : kind === "rank_range"
          ? boundText(e.rank_to)
          : "",
    payload: rewardPayloadToInput(e.payload),
    imported: true,
  };
}

/** The same row as the modal lists it — name, tier chip, range. */
function previewFromFileEntry(
  e: RewardsFileEntry,
  tiers: TierDraft[],
  tiered: boolean,
): RewardsFilePreviewRow {
  const kind = fileRewardKind(e);
  const key = entryTierKey(e);
  const bound = tiered && key ? tiers.find((t) => t.key.trim() === key) : undefined;
  return {
    name: e.name,
    tier: tiered ? (bound ? bound.name.trim() || bound.key.trim() : null) : undefined,
    meta:
      kind === "tier_threshold"
        ? "tier threshold"
        : kind === "percentile"
          ? `pct ${boundText(e.percentile_from)} – ${boundText(e.percentile_to)}`
          : `rank ${boundText(e.rank_from)} – ${boundText(e.rank_to)}`,
  };
}

/**
 * What this form makes of a rewards file: the draft rows it becomes, or every
 * reason it can't.
 *
 * The file is taken whole or not at all. A row naming a tier that step 4
 * doesn't define has nothing to be shown as — the tier picker only knows the
 * keys typed above — and quietly turning it into an "All tiers" reward would
 * change who the reward pays out to without saying so. The same goes for a
 * threshold reward on a board that isn't claim_gated: the type isn't even on
 * offer there, so there is no row to put it in.
 *
 * Ahead of both comes the question of whether the row can be read at all. A
 * draft row holds one range and one tier, so a row whose `tier_key` isn't
 * text, or that fills in a range bound its own kind has no use for, has no
 * single reading — and every reading of it throws away something the file
 * spelled out. Those are turned away too rather than resolved by precedence.
 *
 * Everything else is validateRewardDraft, the rules the form already applies
 * to rows typed by hand. Only the two questions a draft row cannot express —
 * which tier keys exist, and which reward types this board allows — are asked
 * here, and one row reports one problem: the first, which is the one to fix.
 */
function reviewRewardsFile(
  file: RewardsFile,
  tiers: TierDraft[],
  segmentation: Segmentation,
  tierMode: TierMode,
): RewardsFileReview<RewardDraft> {
  const tiered = segmentation === "tier";
  const claimGated = tiered && tierMode === "claim_gated";
  const problems: RewardsFileProblem[] = [];

  // Checked in file order so the numbers in the problem list are the ones the
  // operator will count to in a text editor.
  file.rewards.forEach((e, i) => {
    const problem = (reason: ReactNode) =>
      problems.push({ index: i + 1, name: e.name, reason });

    // First, because the kind is derived from `tier_key` as well as from the
    // ranges: a `tier_key` that isn't text reads as no tier at all, and the
    // kind derived from that would be the wrong question to ask about the row.
    if (!tierKeyIsText(e)) {
      problem(
        <>
          <code>tier_key</code> must be text — the key of a tier on step 4
        </>,
      );
      return;
    }

    // A reward of the SECOND table. The create form has one reward editor and
    // no basis column, so such a row has no shape to be shown in — and reading
    // it as an ordinary reward would move a contributor prize into the team
    // table, where an identical range may already sit. Turned away with the
    // rest of the file, and told where the row does belong.
    if (fileRewardBasis(e) === "guild_member") {
      problem(
        <>
          a <code>rank_basis: guild_member</code> reward belongs to the
          contributor table, which is filled in on the board&apos;s Rewards tab
          once it exists
        </>,
      );
      return;
    }

    const kind = fileRewardKind(e);
    const stray = strayRangeFields(e, kind);
    if (stray.length > 0) {
      problem(
        <>
          a reward has one range: this row reads as <code>{kind}</code> but
          also sets{" "}
          {stray.map((f, n) => (
            <Fragment key={f}>
              {n > 0 && " and "}
              <code>{f}</code>
            </Fragment>
          ))}
        </>,
      );
      return;
    }

    const key = entryTierKey(e);

    if (key && !tiered) {
      problem(
        <>
          tier <code>{key}</code> needs a tiered board — step 4 is set to{" "}
          <code>{segmentation}</code>
        </>,
      );
      return;
    }
    if (key && !tiers.some((t) => t.key.trim() === key)) {
      problem(
        <>
          tier <code>{key}</code> is not in step 4
        </>,
      );
      return;
    }
    if (kind === "tier_threshold" && !claimGated) {
      problem(
        <>
          a <code>tier_threshold</code> reward needs tier mode{" "}
          <code>claim_gated</code> on step 4
        </>,
      );
      return;
    }
    // The rule is parseRewardPayload's, the same one the row editor applies;
    // only the sentence differs. The editor names the box being typed in and
    // can afford to explain itself at length — a problem list names the file's
    // own field, in the words the server's own list uses for it
    // (internal/admin/snapshot/rewards.go rewardPayloadShapeOK).
    const payload = parseRewardPayload(rewardPayloadToInput(e.payload));
    if (!payload.ok) {
      problem(
        <>
          <code>payload</code> must be a JSON object or an array of items
        </>,
      );
      return;
    }
    const rowErr = validateRewardDraft(draftFromFileEntry(e, tiers, tiered), tiers);
    if (rowErr) problem(rowErr);
  });

  if (problems.length > 0) return { ok: false, problems };

  // `position` is what the file says the order is; in the editor the order IS
  // the row index, and buildRewardInputs numbers the positions from it again.
  const ordered = [...file.rewards].sort((a, b) => a.position - b.position);
  return {
    ok: true,
    rows: ordered.map((e) => draftFromFileEntry(e, tiers, tiered)),
    preview: ordered.map((e) => previewFromFileEntry(e, tiers, tiered)),
  };
}

/**
 * Convert the form's reward drafts into the API-ready `CreateRewardInput`
 * array. Position is derived from the row index (0-based) so the caller
 * sees the rewards in display order. Assumes drafts have already passed
 * validateRewardDraft — submit is blocked while any row still has an error, so
 * a payload that doesn't parse here can only be a caller bug and degrades to
 * `{}` rather than throwing mid-submit.
 *
 * `tiers` resolves each draft's tier_uid into the key that actually goes on
 * the wire. ValidateRewardCreate (validate.go:213) rejects a tier_key on a
 * non-tier board and one that's absent from tier_config, so the key is
 * dropped whenever the board isn't tiered, the row has since been deleted, or
 * its key ended up blank.
 *
 * A tier_threshold draft goes out as that tier_key and nothing else: no rank
 * range, no percentile range. That absence is what the API reads as "this is a
 * ladder rung" — there is no type field to send.
 */
function buildRewardInputs(
  rewards: RewardDraft[],
  tiers: TierDraft[] = [],
  segmentation: Segmentation = "none",
): CreateRewardInput[] {
  return rewards.map((r, i) => {
    const from = Number(r.range_from);
    const to = Number(r.range_to);
    // Object or array, whatever the operator typed — the draft has already
    // been through validateRewardDraft, so a failure here is a caller bug and
    // {} is the same fallback an empty textarea gets.
    const parsed = parseRewardPayload(r.payload);
    const payload = parsed.ok ? parsed.value ?? {} : {};
    const base: CreateRewardInput = {
      name: r.name.trim(),
      position: i,
      payload,
    };
    if (segmentation === "tier" && r.tier_uid !== ALL_TIERS) {
      const key = tiers.find((t) => t.uid === r.tier_uid)?.key.trim();
      if (key) base.tier_key = key;
    }
    if (r.type === "rank_range") {
      base.rank_from = from;
      base.rank_to = to;
    } else if (r.type === "percentile") {
      base.percentile_from = from;
      base.percentile_to = to;
    }
    return base;
  });
}

/**
 * Convert the tier rows into the API-ready TierConfig.
 *
 * score_bucket and claim_gated: rows go out sorted by min_score (both pick the
 * tier with PickScoreTier, which depends on that order) and carry their
 * threshold. promotion: the row order IS the ladder — CompilePromotionSQL
 * (promotion.go:24) reads nothing else — and min_score is dropped because the
 * mode ignores it.
 */
function buildTierConfig(state: FormState): TierConfig {
  const threshold = isThresholdTierMode(state.tier_mode);
  const ordered = threshold ? sortByMinScore(state.tiers) : state.tiers;
  const tiers: Tier[] = ordered.map((t) => {
    const tier: Tier = { key: t.key.trim(), name: t.name.trim() };
    if (threshold) {
      const min = parseMinScore(t.min_score);
      if (min !== null) tier.min_score = min;
    }
    return tier;
  });
  const cfg: TierConfig = { tiers };
  if (state.tier_mode === "promotion") {
    cfg.promote_top_k = Number(state.promote_top_k);
    cfg.demote_bottom_k = Number(state.demote_bottom_k);
  }
  return cfg;
}

/**
 * The three caps as the API takes them: a key for every guard that is on, and
 * no key at all for one that is off. A `0` in its place would be a cap of
 * zero — every submit refused — so an off guard has to be an absence.
 */
function buildGuards(state: FormState): Guards {
  const guards: Guards = {};
  const maxScore = parseGuard(state.guard_max_score);
  if (typeof maxScore === "number") guards.max_score_per_submit = maxScore;
  const maxDelta = parseGuard(state.guard_max_delta);
  if (typeof maxDelta === "number") guards.max_delta_per_submit = maxDelta;
  const interval = parseGuard(state.guard_min_interval);
  if (typeof interval === "number") guards.min_interval_sec = interval;
  return guards;
}

/**
 * lb_realtime_check, read off what buildInput sends: rank.changed goes to one
 * table, so the board needs a bucket_size or a guild to be that table.
 */
function realtimeAllowed(
  state: Pick<FormState, "scope" | "segmentation" | "rooms">,
): boolean {
  return (
    state.scope === "guild_internal" ||
    state.segmentation === "bucket" ||
    (state.segmentation === "tier" && state.rooms)
  );
}

function buildInput(
  state: FormState,
  /** The board already carries metadata, so an emptied box has something to
     clear rather than merely nothing to add. */
  hadMetadata: boolean,
): CreateLeaderboardInput {
  const auto = isAutoScored(state);
  const contribution = isMemberContribution(state);
  const input: CreateLeaderboardInput = {
    // Trimmed in both modes: in edit this is the value the operator typed on
    // an archived board, and everywhere else it is the seeded one unchanged.
    ext_key: state.ext_key.trim(),
    name: state.name.trim(),
    description: state.description,
    sort: state.sort,
    window_kind: state.window_kind,
    scope: state.scope,
    segmentation: state.segmentation,
    visibility: state.visibility,
  };
  // The four fields the Scoring section's Alert says are not shown, kept off
  // the payload as one — the rule the Alert states is the rule the body has to
  // keep, or the sentence stops describing the request.
  //
  // `operator` is the one the server insists on: R3 (spec §4.1) makes it inert
  // on an auto-scored board — derived from guild_aggregator — and refuses a
  // body that names one at all, on create AND on PATCH. The other three the
  // server would accept; they are withheld because a value nobody was shown is
  // not a value anybody chose, and on a PATCH sending them would overwrite
  // whatever the board holds. Absent, create resolves submit_policy and
  // realtime to their own defaults and a PATCH leaves those columns alone.
  //
  // `realtime` joins them: such a board has no rooms, so it could never be on.
  if (!auto) {
    // `member_contribution` withholds this one and nothing else: the board
    // DOES take submits — that is the whole mechanic — so the policy, the caps
    // and the realtime flag are all live settings of it. Only the operator is
    // decided for it, at `increment`, together with the aggregator below;
    // sending one would be a value nobody was shown, over a column the server
    // fixes for itself.
    if (!contribution) {
      input.operator = state.operator;
    }
    input.submit_policy = state.submit_policy;
    input.guards = buildGuards(state);
    // Absent where the server refuses it: create defaults to false, and a
    // PATCH that leaves the board without a table has the flag reset for it.
    if (realtimeAllowed(state)) {
      input.realtime = state.realtime;
    }
  }
  const metadata = parseMetadata(state.metadata);
  // Only a draft that parsed travels; a broken one has already blocked the
  // submit. An empty box says "this board carries no metadata", which is worth
  // sending only where there is some to take away — on a board that never had
  // any, the key stays off the wire and the column is left alone.
  if (metadata.ok) {
    if (!metadata.empty) {
      input.metadata = metadata.value;
    } else if (hadMetadata) {
      input.metadata = {};
    }
  }
  if (state.window_kind === "recurring") {
    // Trimmed, because everything that judges the expression already is:
    // `cronDirty` compares trimmed strings and the host compares the payload
    // against the stored cron. A trailing space sent raw is a schedule the
    // form calls unchanged — no `apply` row, `cron_apply` left at its default
    // — and the body carries anyway, asking the server to re-cut a board
    // nobody was rescheduling.
    input.cron = state.cron.trim();
  }
  if (state.window_kind === "recurring_event") {
    // Trimmed for the reason above: the same two comparisons decide whether
    // the schedule moved at all.
    input.cron = state.cron.trim();
    // The box and its unit as the one number the column holds. `null` cannot
    // reach here through a submit — validate() blocks it — and where it does
    // (a caller building a payload off a half-typed state) the key stays off
    // the wire rather than travelling as a length nobody typed.
    const minutes = parseWindowMinutes(state);
    if (minutes !== null) {
      input.window_minutes = minutes;
    }
  }
  if (state.window_kind === "event") {
    input.event_start_at = state.event_start_at;
    input.event_end_at = state.event_end_at;
  }
  // The other half of the pair the contribution board does not choose. The
  // form shows `sum` there as a read-only badge, and the create validator
  // refuses a body that names it.
  if (state.scope === "guild_aggregated" && state.guild_aggregator && !contribution) {
    input.guild_aggregator = state.guild_aggregator;
  }
  // Only from the scope the source belongs to, and only for the value that is
  // not the column's default: a `submit` board is every guild_aggregated board
  // there has ever been, and saying so on the wire would make every existing
  // board's payload change shape for a decision nobody made.
  if (auto) {
    input.guild_score_source = "player_stat";
    // Byte for byte, deliberately not trimmed: validateGuildStatKeyShape
    // REFUSES a padded key rather than trimming it, because players.stats keys
    // are matched exactly and a key validated trimmed but stored padded is a
    // board of silent zeroes. The value came from a list, so it cannot be
    // padded — and if that ever changes, the server says so instead of the
    // form hiding it.
    input.guild_stat_key = state.guild_stat_key;
  }
  // The third source, and the three fields that exist only under it. They
  // travel unconditionally rather than «only when set»: the server refuses
  // them on any other source, so their presence IS the statement that this is
  // the team-battle board, and an omitted switch would leave a PATCH silently
  // keeping whatever the column holds while the screen says otherwise.
  if (contribution) {
    input.guild_score_source = "member_contribution";
    // An empty box and a 0 are the same answer — no threshold — and `null` is
    // how the API spells it, on create and in a PATCH alike.
    const min = state.min_guild_members.trim();
    input.min_guild_members = min === "" ? null : Number(min);
    input.reward_contributors_only = state.reward_contributors_only;
    input.late_joiners_may_score = state.late_joiners_may_score;
  }
  if (state.segmentation === "bucket") {
    input.bucket_size = Number(state.bucket_size);
  }
  // The personal pair travels together and only from a personal board — the
  // server refuses either field on any other mode, so their presence IS the
  // statement that this board ranks in lists. On a PATCH that also carries the
  // switch, the three keys are what turn a board of rooms into a board of
  // lists in one save; `bucket_size` is not sent with them, because the server
  // clears it in the same statement (the two cannot stand side by side).
  if (state.segmentation === "personal") {
    input.roster_size = Number(state.roster_size);
    input.matchmaking_config = configFromDraft(state.matchmaking);
  }
  // Exactly where the switch is shown, and nowhere else: a `true` the operator
  // ticked before moving the board to a scope or a segmentation that forbids
  // the pair stays in the state and off the wire, so the payload says what the
  // form is showing rather than what it remembers.
  if (
    countrySplitAllowed({
      scope: state.scope,
      segmentation: state.segmentation,
    })
  ) {
    input.country_split = state.country_split;
  }
  // Nothing travels from a guild_aggregated board, where the row is not shown
  // at all — not even the false the state happens to be holding, exactly as
  // hold_finished_cycles does not travel from there.
  //
  // sort=asc is different, and it is the reason this is not the same `if`: the
  // row is still on screen, reading «unavailable», and a board that ALREADY
  // enrols can be turned around under it. Dropping the key there would PATCH
  // the board straight into the pair the server refuses, and the operator
  // would be handed a 400 naming a field the form is showing as unavailable.
  // So unavailable is saved as what it says it is — off — and the state keeps
  // the operator's own choice for the moment they switch back to desc.
  if (state.scope !== "guild_aggregated") {
    // segmentation=personal is saved as an explicit `false` rather than left
    // out, and that is the whole of what makes the one-way switch work on a
    // board that enrols today: the server judges the pair the board will HAVE,
    // so an omitted key leaves the stored `true` standing and refuses the
    // switch. The state keeps the operator's own choice for the moment they
    // step back to `bucket`. Same shape, and the same reason, as `sort=asc`.
    input.auto_enroll =
      state.sort === "asc" || state.segmentation === "personal"
        ? false
        : state.auto_enroll;
  }
  if (hasCycleLifetime(state.window_kind)) {
    // Assigned unconditionally, because 0 is the term that keeps nothing:
    // treating it as "unset" would quietly save the board as 90.
    input.retention_days = Number(state.retention_days);
    // The switch isn't offered on a guild_aggregated board and the server
    // refuses the pair, so nothing about it travels from there — not even the
    // false the state happens to be holding.
    if (state.scope !== "guild_aggregated") {
      input.hold_finished_cycles = state.hold_finished_cycles;
    }
  }
  if (state.segmentation === "tier") {
    if (state.tier_mode) input.tier_mode = state.tier_mode;
    input.tier_config = buildTierConfig(state);
    // null, not undefined: on a tier board the size is patchable, and the
    // PATCH body needs to say «switch the rooms off» rather than «leave them
    // as they are». On create the backend treats the two the same.
    input.bucket_size = state.rooms ? Number(state.bucket_size) : null;
  }
  return input;
}

// === Sub-components =================================================== //

interface RadioCardProps<T extends string> {
  name: string;
  options: OptionMeta<T>[];
  value: T;
  cols: 2 | 3 | 4;
  onChange: (v: T) => void;
}

function RadioCardGroup<T extends string>({
  name,
  options,
  value,
  cols,
  onChange,
}: RadioCardProps<T>) {
  const colsClass =
    cols === 2 ? styles.cols2 : cols === 4 ? styles.cols4 : styles.cols3;
  return (
    <div className={`${styles.radioCards} ${colsClass}`} role="radiogroup">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          /* The whole card is the label — same as SegRadio's pill, and the
             only way the glyph, the description and the padding (four fifths
             of a card that advertises itself with cursor: pointer) switch the
             radio. Doing it natively also means no click handler and no
             transparent overlay: the browser forwards the activation. */
          <label
            key={opt.value}
            className={`${styles.option} ${selected ? styles.selected : ""} ${
              opt.disabled ? styles.optionDisabled : ""
            }`}
            /* The reason a card cannot be picked, on the card itself: the
               choice that forbids it was made in another section, so the
               operator needs to be told which one before they go looking. */
            title={opt.disabled ? opt.disabledReason : undefined}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={selected}
              disabled={opt.disabled}
              onChange={() => {
                if (opt.disabled) return;
                onChange(opt.value);
              }}
              /* The accessible name stays the title alone: aria-label wins
                 over the wrapping label's text, which now reads "G global All
                 players ranked together." Without it every option would be
                 named by its whole card and getByLabelText("global") — how the
                 form's tests reach these controls — would find nothing. */
              aria-label={opt.title}
            />
            <span className={styles.glyphBox} aria-hidden>
              {opt.glyph}
            </span>
            <span className={styles.optBody}>
              <span className={styles.optTitle}>{opt.title}</span>
              <span className={styles.optDesc}>{opt.desc}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

interface SegRadioProps<T extends string> {
  name: string;
  /** `disabled` greys the option out and makes selecting it a no-op — used
     for tier_mode (immutable post-create, and score_bucket is incompatible
     with operator=increment). */
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (v: T) => void;
  /** When provided, set as aria-label on each input — lets getByLabelText
     resolve a control by group name (e.g. "Guild aggregator"). */
  groupLabel?: string;
}

function SegRadio<T extends string>({
  name,
  options,
  value,
  onChange,
  groupLabel,
}: SegRadioProps<T>) {
  // Use a fieldset so the group itself is form-associable; that means
  // testing-library's getByLabelText(groupLabel) resolves cleanly through
  // aria-label without needing a sibling <label htmlFor>. Visually the
  // fieldset is stripped of borders to match the inline seg control.
  return (
    <div
      className={styles.seg}
      role="radiogroup"
      aria-label={groupLabel}
      // tabIndex makes the group focusable so RTL's accessibility-tree query
      // returns the wrapper element by name (groupLabel) — avoids the
      // "element associated with this label is non-labellable" error when
      // the consumer passes a sibling <label htmlFor>.
      tabIndex={groupLabel ? -1 : undefined}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const id = `${name}-${opt.value}`;
        return (
          <label
            key={opt.value}
            className={`${styles.segOpt} ${active ? styles.segActive : ""} ${
              opt.disabled ? styles.segDisabled : ""
            }`}
            htmlFor={id}
          >
            <input
              id={id}
              type="radio"
              name={name}
              value={opt.value}
              checked={active}
              disabled={opt.disabled}
              onChange={() => {
                if (opt.disabled) return;
                onChange(opt.value);
              }}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

interface FieldRowProps {
  label: string;
  apiName?: string;
  /** Rich text allowed: several hints name an API error code, and a code the
     operator will search the logs for is worth setting in <code>. */
  hint?: ReactNode;
  required?: boolean;
  full?: boolean;
  children: ReactNode;
  error?: string;
  /** When set, the .name block becomes a real <label htmlFor=...>. Clicks
     then focus the associated input. Radio-card / switch groups omit this. */
  htmlFor?: string;
  /** Dims the name and the api name of a row standing in place unavailable
     (auto_enroll under asc, realtime without a table). The hint stays at full
     contrast: it is the part that says why. */
  mutedLabel?: boolean;
  /**
   * Which package key the "Scheduled: …" line under this row is about.
   * Defaults to `apiName`, which is right for every row whose control IS the
   * field. `false` on the three rows that wrap a whole reveal block — there
   * the note has to sit under the control it is about, not at the bottom of
   * everything hanging off it, so those place it by hand.
   */
  pendingField?: string | false;
}

function FieldRow({
  label,
  apiName,
  hint,
  required,
  full,
  children,
  error,
  htmlFor,
  mutedLabel,
  pendingField,
}: FieldRowProps) {
  const NameTag = htmlFor ? "label" : "div";
  const nameProps = htmlFor ? { htmlFor } : {};
  const muted = mutedLabel ? styles.rowMuted : "";
  return (
    <div className={`${styles.fieldRow} ${full ? styles.fieldRowFull : ""}`}>
      <div className={styles.fieldLabel}>
        <NameTag className={`${styles.name} ${muted}`} {...nameProps}>
          {label}
        </NameTag>
        {required && (
          <span className={`${styles.req} ${muted}`} aria-hidden>
            *
          </span>
        )}
        {apiName && (
          <div className={`${styles.apiName} ${muted}`}>
            {apiName}
            {/* When this field may move. Read off the api name rather than
               passed in, so a row that names a column is marked by the fact
               that it names it — see pendingFields.whenBadge. */}
            <WhenBadge field={apiName} />
          </div>
        )}
        {hint && <div className={styles.hint}>{hint}</div>}
        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
      <div className={styles.fieldInput}>
        {children}
        {pendingField !== false && (
          <PendingNote field={pendingField ?? apiName ?? ""} />
        )}
      </div>
    </div>
  );
}

/**
 * A field the board no longer lets anybody change: its value as a pill with a
 * padlock, and a line saying why. One shape for every locked field of the form
 * — segmentation and tier_mode already read this way, and the padlock is what
 * turns "there is no control here" from something to infer into something
 * stated.
 *
 * Deliberately not a disabled control. A greyed-out radio group still invites
 * the click it is going to refuse, and it spends the width of a chooser on a
 * field with nothing left to choose; next to `operator`, whose four cards are
 * all live, a dimmed `scope` beside it would read as a form half broken rather
 * than as a board half decided.
 */
function LockedValue({
  value,
  note,
  children,
}: {
  value: ReactNode;
  /**
   * How the line under the pill is set. The default reads as prose, because on
   * every other locked field it IS prose — what the value means, and what to
   * do instead. `lock` is the mockup's smaller, fainter `.lock` type with the
   * padlock repeated in front (§1 293-296): the shape for a line that says
   * nothing but "this is locked", which of the locked fields is only ext_key.
   */
  note?: "helper" | "lock";
  /** Why it is locked, or what the value means. Omitted where something else
     on the row already says it. */
  children?: ReactNode;
}) {
  return (
    <>
      <code className={styles.lockedPill}>
        <Lock size={11} aria-hidden className={styles.lockedPillIcon} />
        {value}
      </code>
      {children &&
        (note === "lock" ? (
          <span className={styles.lockNote}>
            <Lock size={11} aria-hidden />
            {children}
          </span>
        ) : (
          <div className={styles.helper}>{children}</div>
        ))}
    </>
  );
}

// === Tier editor ====================================================== //

interface TierEditorProps {
  tierMode: TierMode;
  tiers: TierDraft[];
  rowErrors?: Record<number, TierRowError>;
  /**
   * claim_gated only: name of the threshold reward bound to each row, keyed by
   * uid. Absent uid = no reward configured for that tier.
   */
  unlockRewards: Record<string, string>;
  /**
   * Whether `unlockRewards` is the whole truth yet. False while the board's
   * rewards are still on their way, when an absent uid means "we don't know",
   * not "there is none".
   */
  rewardsKnown: boolean;
  onKeyChange: (index: number, key: string) => void;
  onNameChange: (index: number, name: string) => void;
  onMinScoreChange: (index: number, value: string) => void;
  onMinScoreBlur: () => void;
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (tier: TierDraft) => void;
  onAdd: () => void;
}

/**
 * Rows of tier_config. Built on the .rewardEditor idiom — same frame, header
 * strip, "+ Add" footer and row buttons.
 *
 * score_bucket shows the thresholds (with a derived, non-editable upper
 * bound); promotion shows reorder arrows instead, because there the array
 * order alone is the ladder. claim_gated is score_bucket's columns plus one
 * more derived cell: the reward that opens the tier.
 */
function TierEditor({
  tierMode,
  tiers,
  rowErrors,
  unlockRewards,
  rewardsKnown,
  onKeyChange,
  onNameChange,
  onMinScoreChange,
  onMinScoreBlur,
  onMove,
  onRemove,
  onAdd,
}: TierEditorProps) {
  const threshold = isThresholdTierMode(tierMode);
  const gated = tierMode === "claim_gated";
  const rowClass = gated
    ? styles.tierRowGated
    : threshold
      ? styles.tierRowBucket
      : styles.tierRowLadder;
  const maxScores = threshold ? deriveMaxScores(tiers) : {};
  // Ladder position by uid — the row order on screen may lag the threshold
  // order until the next blur re-sorts it.
  const rungOf: Record<string, number> = {};
  if (gated) {
    ladderRungs(tiers).forEach((t, i) => {
      rungOf[t.uid] = i;
    });
  }

  return (
    <div className={styles.tierEditor} role="group" aria-label="Tiers">
      <div className={`${styles.tierRow} ${rowClass} ${styles.tierHead}`}>
        <div>Key</div>
        <div>Tier name</div>
        {threshold ? (
          <>
            <div className={styles.tierColNum}>Min score</div>
            <div className={styles.tierColNum}>Max score</div>
            {gated && <div>Unlock reward</div>}
          </>
        ) : (
          <div>Order</div>
        )}
        <div aria-hidden />
      </div>

      {tiers.length === 0 ? (
        <div className={styles.tierEmpty}>
          No tiers yet. At least two are required — Bronze and Silver, for
          example.
        </div>
      ) : (
        tiers.map((t, i) => {
          const err = rowErrors?.[i];
          const messages = [err?.key, err?.name, err?.min_score].filter(Boolean);
          return (
            <div key={t.uid} className={`${styles.tierRow} ${rowClass}`}>
              <input
                className={`${styles.input} ${styles.mono} ${
                  err?.key ? styles.invalid : ""
                }`}
                aria-label={`Tier ${i + 1} key`}
                placeholder="bronze"
                value={t.key}
                disabled={t.saved}
                title={
                  t.saved
                    ? "Saved tier keys are referenced by players and rewards — they can't be renamed."
                    : undefined
                }
                onChange={(e) => onKeyChange(i, e.target.value)}
              />
              <input
                className={`${styles.input} ${err?.name ? styles.invalid : ""}`}
                aria-label={`Tier ${i + 1} name`}
                placeholder="Bronze"
                value={t.name}
                onChange={(e) => onNameChange(i, e.target.value)}
              />
              {threshold ? (
                <>
                  <input
                    className={`${styles.input} ${styles.mono} ${styles.tierNum} ${
                      err?.min_score ? styles.invalid : ""
                    }`}
                    aria-label={`Tier ${i + 1} min score`}
                    placeholder="0"
                    inputMode="numeric"
                    value={t.min_score}
                    onChange={(e) => onMinScoreChange(i, e.target.value)}
                    onBlur={onMinScoreBlur}
                  />
                  <div className={styles.tierDerived}>{maxScores[t.uid] ?? "—"}</div>
                  {gated && (
                    <div>
                      <UnlockRewardSlot
                        rung={rungOf[t.uid] ?? -1}
                        rewardName={unlockRewards[t.uid]}
                        known={rewardsKnown}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.moveGroup}>
                  <button
                    type="button"
                    className={`${styles.rowBtn} ${styles.rowBtnMove}`}
                    aria-label={`Move tier ${i + 1} up`}
                    title="Move up"
                    disabled={i === 0}
                    onClick={() => onMove(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={`${styles.rowBtn} ${styles.rowBtnMove}`}
                    aria-label={`Move tier ${i + 1} down`}
                    title="Move down"
                    disabled={i === tiers.length - 1}
                    onClick={() => onMove(i, 1)}
                  >
                    ↓
                  </button>
                </div>
              )}
              <button
                type="button"
                className={styles.rowBtn}
                aria-label={`Remove tier ${i + 1}`}
                title="Remove tier"
                onClick={() => onRemove(t)}
              >
                ✕
              </button>
              {messages.map((m, mi) => (
                <div key={mi} className={styles.tierRowError}>
                  {m}
                </div>
              ))}
            </div>
          );
        })
      )}

      <button type="button" className={styles.tierAdd} onClick={onAdd}>
        + Add tier
      </button>
    </div>
  );
}

/**
 * Mirror of the tier_threshold reward that opens this tier. A derived cell,
 * never an input: rewards are created in the Rewards section of this form, or
 * later on the Rewards tab, and a picker here would either mean two places
 * that create the same entity or a selector that is empty exactly when it is
 * needed — in create mode the rewards are defined one section further down.
 */
function UnlockRewardSlot({
  rung,
  rewardName,
  known,
}: {
  /** Ladder position of the row, or -1 when it carries no usable threshold. */
  rung: number;
  rewardName: string | undefined;
  /** False while the board's rewards are still unknown — see the props doc. */
  known: boolean;
}) {
  if (rung < 0) {
    return (
      <span
        className={`${styles.rewardSlot} ${styles.rewardSlotNone}`}
        title="Set a min score to put this tier on the ladder."
      >
        —
      </span>
    );
  }
  if (rung === 0) {
    return (
      <span
        className={`${styles.rewardSlot} ${styles.rewardSlotNone}`}
        title="The bottom rung — everybody starts here, so nothing unlocks it."
      >
        starting league
      </span>
    );
  }
  // Neither the reward nor the warning: both would be a claim about rewards
  // nobody has read yet. The dashed slot is the form's idiom for "nothing to
  // state here", and it is the honest shape of a request still in flight — or
  // one that failed, where the answer never arrives at all.
  if (!known) {
    return (
      <span
        className={`${styles.rewardSlot} ${styles.rewardSlotNone}`}
        title="This board's rewards haven't loaded, so what unlocks this tier is still unknown."
      >
        not loaded
      </span>
    );
  }
  if (!rewardName) {
    return (
      <span
        className={`${styles.rewardSlot} ${styles.rewardSlotMissing}`}
        title="No tier_threshold reward points at this tier, so nobody can move into it."
      >
        ⚠ no reward set
      </span>
    );
  }
  return (
    <span
      className={styles.rewardSlot}
      title={`Claiming ${rewardName} moves the player into this tier`}
    >
      <span aria-hidden>🎁</span>
      <span className={styles.rewardSlotName}>{rewardName}</span>
    </span>
  );
}

/**
 * league → reward → league, in threshold order. The quickest way to spot a
 * ladder that stops: the missing reward sits right where the players do.
 */
function ClaimGatedLadder({
  rungs,
  unlockRewards,
}: {
  rungs: TierDraft[];
  unlockRewards: Record<string, string>;
}) {
  return (
    <div className={styles.ladderPreview}>
      {rungs.map((t, i) => (
        <Fragment key={t.uid}>
          {i > 0 && (
            <>
              <span className={styles.ladderArrow} aria-hidden>
                →
              </span>
              {unlockRewards[t.uid] ? (
                <span className={styles.ladderGift}>
                  🎁 {unlockRewards[t.uid]}
                </span>
              ) : (
                <span className={`${styles.ladderGift} ${styles.ladderGiftMiss}`}>
                  ⚠ no reward
                </span>
              )}
              <span className={styles.ladderArrow} aria-hidden>
                →
              </span>
            </>
          )}
          <span className={styles.ladderRung}>{tierRowLabel(t, i)}</span>
        </Fragment>
      ))}
    </div>
  );
}

// === Main component =================================================== //

export function LeaderboardConfigForm(
  props: LeaderboardConfigFormProps,
): JSX.Element {
  const {
    mode,
    initial,
    rewards: savedRewards,
    status,
    eventCycleStatus,
    openCycleCloseAt,
    openCycleOpenAt,
    windowPaused,
    windowLengthError,
    fetchRetentionImpact,
    statsSchema,
    fetchStatKeyCoverage,
    pending,
    nextCut,
    ladderImpact,
    scheduling,
    seed,
    submitLabel,
    onSubmit,
    onCancel,
    saving,
    saveNowBlocked,
  } = props;

  // Confined on the way in as well as on every write: a template or a
  // half-filled `initial` can name a source without naming the window it
  // confines, and the form must never open on a pair the API refuses.
  const [state, setState] = useState<FormState>(() =>
    confine(initialState(seed ? { ...initial, ...seed } : initial, mode)),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  /** uid of the saved tier whose removal is waiting for confirmation. */
  const [tierPendingRemoval, setTierPendingRemoval] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // One closure for as long as the form lives. Modal is indifferent either way
  // — it reaches onClose through a ref and keys its focus effect on `open`
  // alone — so this is tidiness, not a constraint the dialog imposes.
  const closeImport = useCallback(() => setImportOpen(false), []);
  /**
   * What the last import brought in. Kept until the next one rather than timed
   * out: the note is the only thing that says where those rows came from, and
   * a highlight that disappears on its own is one the operator can miss
   * entirely — plus a timer would make the tests wait on a clock.
   */
  const [imported, setImported] = useState<{
    count: number;
    fileName: string;
  } | null>(null);

  // The persisted tier_config the rows were last baselined against. The host
  // re-renders this form with a fresh `initial` after a save instead of
  // remounting it, so without this the form would keep calling the just-saved
  // threshold "the new one" for as long as the operator stays on the page.
  const persistedTiers = persistedTierSignature(initial);
  const [baselinedTiers, setBaselinedTiers] = useState(persistedTiers);
  if (mode === "edit" && persistedTiers !== baselinedTiers) {
    setBaselinedTiers(persistedTiers);
    setState((s) => ({
      ...s,
      tiers: adoptPersistedTiers(s.tiers, initial?.tier_config?.tiers ?? []),
    }));
  }

  const extKeyId = useId();
  const nameId = useId();
  const descId = useId();
  const cronId = useId();
  const windowValueId = useId();
  const eventStartId = useId();
  const eventEndId = useId();
  const bucketSizeId = useId();
  const rosterSizeId = useId();
  const freshnessId = useId();
  const gateMinId = useId();
  const retentionId = useId();
  const guardMaxScoreId = useId();
  const guardMaxDeltaId = useId();
  const guardIntervalId = useId();
  const metadataId = useId();

  // --- What this board still lets the operator move --------------------
  // Each of these answers one question the payload cannot: not "what is
  // stored", but "who may still change it".

  // Renaming is only ever safe once the board has stopped taking submits —
  // until then the key is a live handle in builds already shipped. An unknown
  // status reads as "not archived" (see the prop doc).
  const extKeyEditable = mode === "create" || status === "archived";

  // --- What a cut can still move ---------------------------------------
  //
  // The board's shape — the window kind, the scope, the segmentation, the tier
  // mode, the guild score source — used to be settable only at creation. It is
  // editable now, because the cycle carries a snapshot of the configuration it
  // was cut with: the week already being played keeps its own answer, and the
  // next one may be born of another. The change waits for the cut, which is
  // what the badge beside each of them says.
  //
  // On a board where no cut is coming there is nothing to wait for, so those
  // fields go back to being pills. `event` closes without a successor and
  // `all_time` never closes at all — see boardHasNextCycle.
  // Read off the kind the form OPENED on, never off the live one. The window
  // kind is itself a field a package can move, and a form whose second save
  // button vanished the moment the operator picked `event` would take away the
  // only button that could carry that very change.
  const openedWindowKind = useMemo(
    () => initialState(initial, mode).window_kind,
    [initial, mode],
  );
  const boardCuts = boardHasNextCycle(
    mode === "edit" ? openedWindowKind : undefined,
  );
  /** Edit mode, on a board that will never cut another cycle: the shape is
     frozen for good and shown as it was before this feature. */
  const cutFrozen = mode === "edit" && !boardCuts;
  /** Edit mode on a board that does cut: the shape is a live control again. */
  const cutEditable = mode === "edit" && boardCuts;

  // Dirty flags for the two fields whose change reaches into the cycle that is
  // already running. Compared against what the board has stored, so retyping a
  // value back to where it was retires the warning with it.
  const savedSort = (initial?.sort ?? "desc") as Sort;
  const sortDirty = mode === "edit" && state.sort !== savedSort;

  const savedCron = (initial?.cron ?? "").trim();
  const cronDirty =
    mode === "edit" &&
    (state.window_kind === "recurring" ||
      state.window_kind === "recurring_event") &&
    state.cron.trim() !== savedCron;

  // The other half of a recurring_event schedule. A window is described by
  // WHEN it opens and HOW LONG it stays open, and moving either re-cuts it —
  // so the length raises the `apply` row on its own, and the server takes a
  // `cron_apply` on a body that carries nothing but this number.
  //
  // Measured on the minutes that would travel, not on the row: 3 days and 72
  // hours are the same window, and a form that called one of them a change
  // would ask the operator which cycle to apply a no-op from.
  //
  // A box mid-edit — empty of digits, «1.5», a stray letter — parses to null,
  // and null is not «a different length», it is «no length yet». Asking which
  // window to apply a value from that cannot be sent (buildInput drops it,
  // validate refuses the save) would raise the row on the way through a
  // keystroke and drop it again on the next one.
  const draftWindowMinutes = parseWindowMinutes(state);

  // The schedule the last save handed to the server, so a refusal it answered
  // with can be retired the moment the schedule moves off it. State rather
  // than a ref: what it holds decides whether the refusal is drawn, and a
  // value the render reads has to be one the render can be woken by.
  //
  // `null` until this form has saved anything, and a refusal handed to it
  // before then is trusted rather than second-guessed: the host is the one
  // that knows what it asked, and a form that hid an error it was given on
  // mount would be answering a question nobody asked it.
  const [judgedSchedule, setJudgedSchedule] = useState<{
    cron: string;
    minutes: number | null;
  } | null>(null);
  const staleRefusal =
    judgedSchedule !== null &&
    (judgedSchedule.cron !== state.cron.trim() ||
      judgedSchedule.minutes !== draftWindowMinutes);
  /** The server's refusal of the length, while it is still about this pair. */
  const liveWindowLengthError = staleRefusal ? undefined : windowLengthError;
  const windowLengthDirty =
    mode === "edit" &&
    state.window_kind === "recurring_event" &&
    draftWindowMinutes !== null &&
    draftWindowMinutes !== (initial?.window_minutes ?? null);

  /** Either half of the schedule differs from what the board has stored —
     which is exactly when a PATCH carries one, and therefore the only time
     «from which window» is a question with an answer. */
  const scheduleDirty = cronDirty || windowLengthDirty;

  // --- Auto-enrolment ---------------------------------------------------

  // The server refuses auto_enroll in three shapes, and the form answers them
  // differently ON PURPOSE.
  //
  // scope=guild_aggregated and segmentation=personal are the ordinary case of
  // this form's rule: the row goes away, because the choice that forbids it was
  // made in ANOTHER section and a switch that could only produce a 400 should
  // not be offered. On personal there is no shared table to enrol into at all —
  // the reader's list is assembled on their first contact and they are already
  // on it, so a zero row would be a write nobody can see.
  //
  // sort=asc is the deliberate exception. That switch sits DIRECTLY ABOVE this
  // row, so a row that vanished under the operator's own click would read as a
  // trick — they saw it a second ago and would be left hunting for where it
  // went. It stays where it is, dimmed, and says why.
  const autoEnrollOffered =
    state.scope !== "guild_aggregated" && state.segmentation !== "personal";
  const autoEnrollBlocked = state.sort === "asc";
  // What the row SHOWS, which on a blocked row is off whatever the state
  // remembers — «unavailable» and «on» together would be a promise the board
  // is not keeping, and off is also what buildInput saves from there.
  const autoEnrollOn = state.auto_enroll && !autoEnrollBlocked;
  // A save that will silently turn the toggle off: the board is enrolling
  // players as things stand, and the order it is being moved to forbids that.
  // Read off `initial` rather than the form state, because the question is what
  // the BOARD is doing right now, not what the operator has ticked.
  const autoEnrollTurnsOff =
    mode === "edit" && autoEnrollBlocked && initial?.auto_enroll === true;
  // The same sentence for the mode switch, said where that switch is: a live
  // board that enrols players today CAN be moved to personal lists, and the one
  // patch that does it has to carry `auto_enroll: false` with it (buildInput
  // sends it). Without this line the toggle would simply be gone from the page
  // and the operator would find out from the board's behaviour next week.
  const autoEnrollEndsWithSwitch =
    mode === "edit" &&
    state.segmentation === "personal" &&
    initial?.segmentation !== "personal" &&
    initial?.auto_enroll === true;

  // Off on screen whatever the state remembers, for the reason autoEnrollOn is.
  const realtimeBlocked = !realtimeAllowed(state);
  const realtimeOn = state.realtime && !realtimeBlocked;
  // Off `initial`, like autoEnrollTurnsOff: what matters is a live table the
  // board has today, which the server drops unasked when this edit lands.
  const realtimeTurnsOff =
    mode === "edit" && realtimeBlocked && initial?.realtime === true;

  // Whether a percentile band is in play, which is the only case the widening
  // warning has anything to say about. Two sources, because the rewards live
  // in two different places depending on the mode: drafts in section 5 while
  // the board is being created, the saved rewards of the board once it exists.
  // `savedRewards` undefined is «not answered yet», not «no rewards» — the
  // warning simply stays away until the query lands.
  const hasPercentileReward =
    mode === "create"
      ? state.rewards.some((r) => r.type === "percentile")
      : (savedRewards ?? []).some(
          (r) => r.percentile_from != null || r.percentile_to != null,
        );

  // --- The auto-scored board -------------------------------------------

  const autoScored = isAutoScored(state);

  // --- The team-battle board -------------------------------------------
  // The third source. Unlike the auto-scored one it keeps the submit half of
  // the form — members DO submit here, that is what a contribution is — and
  // takes only the two columns the server decides for it: the operator and the
  // aggregator, shown as badges inside its own reveal block.
  const contribution = isMemberContribution(state);

  // What a row of this board IS. The Segmentation section counts rows — per
  // room, per league — and on the one scope whose rows belong to a guild the
  // whole section reads about teams instead of players.
  const guildRows = state.scope === "guild_aggregated";

  // The `int` fields of the project's schema, which is the whole list a stat
  // key may come from. Asked twice on purpose: once carrying whatever key is
  // stored (so a key deleted from the schema is still shown rather than
  // silently dropped), and once bare — that one answers "has this project
  // declared anything to score by at all", which the stored key must not be
  // allowed to answer yes to on its own.
  const statKeyOptions = useMemo(
    () => schemaKeyOptions(statsSchema ?? [], state.guild_stat_key),
    [statsSchema, state.guild_stat_key],
  );
  // …and only a schema that actually ARRIVED can say the project has nothing
  // to score by. An answer still in flight leaves the picker open and silent.
  const noIntStat = useMemo(
    () => statsSchema !== undefined && schemaKeyOptions(statsSchema, "").length === 0,
    [statsSchema],
  );
  // The same declared int stats, for the two personal-board fields that name
  // one: the entry threshold and the strength fallback. Both read
  // `players.stats`, so both are picked from the project's own schema rather
  // than typed — a key that is not declared is a board of silent zeroes.
  const gateKeyOptions = useMemo(
    () => schemaKeyOptions(statsSchema ?? [], state.matchmaking.gate_key),
    [statsSchema, state.matchmaking.gate_key],
  );
  // Its sibling, led by the sentinel that says having NO fallback is a
  // complete answer: a player with no previous cycle is then simply treated as
  // a newcomer, and the pace layer hands its slots to the layer below.
  const fallbackKeyOptions = useMemo(
    () => [
      {
        value: "",
        label: "— none: treat as newcomer —",
        muted: true,
        separatorAfter: true,
      },
      ...schemaKeyOptions(statsSchema ?? [], state.matchmaking.fallback_stat_key),
    ],
    [statsSchema, state.matchmaking.fallback_stat_key],
  );
  // One writer for the matchmaking draft, so every control below is a one-line
  // change of one field rather than a spread of the whole card.
  // The card's own messages go out when the thing they are about is edited,
  // exactly as the tier rows below do it — a structural change (add, remove,
  // reorder, or a kind swapped) drops the row messages wholesale, a field edit
  // clears only the message it is about. Without this the card was the one
  // section of the form whose errors could not be dismissed by fixing them:
  // these editors write to setState directly, and every message this form
  // clears is cleared by updateMany, which they do not go through.
  const clearMmErrors = useCallback(
    (keys: (keyof MatchmakingErrors)[]) => {
      if (!submitted) return;
      setErrors((prev) => {
        if (!prev.matchmaking) return prev;
        const mm = { ...prev.matchmaking };
        for (const k of keys) delete mm[k];
        return { ...prev, matchmaking: mm };
      });
    },
    [submitted],
  );
  const clearLayerError = useCallback(
    (uid: string) => {
      if (!submitted) return;
      setErrors((prev) => {
        if (!prev.matchmaking?.rows?.[uid]) return prev;
        const rows = { ...prev.matchmaking.rows };
        delete rows[uid];
        return { ...prev, matchmaking: { ...prev.matchmaking, rows } };
      });
    },
    [submitted],
  );
  const patchMatchmaking = useCallback(
    (patch: Partial<MatchmakingDraft>) => {
      setState((s) => ({ ...s, matchmaking: { ...s.matchmaking, ...patch } }));
      const keys: (keyof MatchmakingErrors)[] = [];
      if ("gate_on" in patch || "gate_key" in patch || "gate_min" in patch) {
        keys.push("gate");
      }
      if ("freshness_days" in patch) keys.push("freshness_days");
      // The set of layers decides both the «last one must be a filler» rule and
      // every row's «this kind appears twice», so adding or removing one takes
      // the lot.
      if ("layers" in patch) keys.push("layers", "rows");
      clearMmErrors(keys);
    },
    [setState, clearMmErrors],
  );
  const patchLayer = useCallback(
    (uid: string, patch: Partial<LayerDraft>) => {
      setState((s) => ({
        ...s,
        matchmaking: {
          ...s.matchmaking,
          layers: s.matchmaking.layers.map((l) =>
            l.uid === uid ? { ...l, ...patch } : l,
          ),
        },
      }));
      if ("kind" in patch) {
        // A kind is not a field of one row: it decides duplicates across the
        // list and whether the last row is a filler.
        clearMmErrors(["layers", "rows"]);
        return;
      }
      clearLayerError(uid);
    },
    [setState, clearMmErrors, clearLayerError],
  );
  // Reorder: the array order IS the order the layers are consumed in, so moving
  // a row is the whole of what reordering means here.
  const moveLayer = useCallback(
    (fromUid: string, toUid: string) => {
      if (fromUid === toUid) return;
      setState((s) => {
        const rows = [...s.matchmaking.layers];
        const from = rows.findIndex((l) => l.uid === fromUid);
        const to = rows.findIndex((l) => l.uid === toUid);
        if (from < 0 || to < 0) return s;
        const [moved] = rows.splice(from, 1);
        rows.splice(to, 0, moved);
        return { ...s, matchmaking: { ...s.matchmaking, layers: rows } };
      });
      // The last row decides the filler rule, so any move can settle or break
      // it — the message goes and Save recomputes it.
      clearMmErrors(["layers"]);
    },
    [setState, clearMmErrors],
  );
  // One step up or down, which is what the keyboard has instead of a drag.
  const nudgeLayer = useCallback(
    (uid: string, delta: -1 | 1) => {
      setState((s) => {
        const rows = [...s.matchmaking.layers];
        const from = rows.findIndex((l) => l.uid === uid);
        const to = from + delta;
        if (from < 0 || to < 0 || to >= rows.length) return s;
        const [moved] = rows.splice(from, 1);
        rows.splice(to, 0, moved);
        return { ...s, matchmaking: { ...s.matchmaking, layers: rows } };
      });
      clearMmErrors(["layers"]);
    },
    [setState, clearMmErrors],
  );
  // Which row the pointer is over, and which one it picked up. Local to the
  // editor rather than part of the form's state: it is gone the moment the
  // drop lands, and nothing outside the list can read it.
  const [dragLayer, setDragLayer] = useState<string | null>(null);
  const [overLayer, setOverLayer] = useState<string | null>(null);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const layerMenuRef = useRef<HTMLDivElement>(null);
  // Closing on a click elsewhere. A menu that only closes by choosing an item
  // is a menu an operator who changed their mind cannot dismiss.
  useEffect(() => {
    if (!layerMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!layerMenuRef.current?.contains(e.target as Node)) {
        setLayerMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [layerMenuOpen]);
  // The one-way switch a LIVE board of rooms is offered. Only in edit mode,
  // only from a stored `bucket`, and only on the scope a list can be assembled
  // for — the same three conditions validateUpdatePersonal checks on the way
  // in, so the button is offered exactly where the PATCH would be accepted.
  const canSwitchToPersonal =
    mode === "edit" &&
    initial?.segmentation === "bucket" &&
    state.scope === "global";
  // The card's own messages, defaulted so every reader below is a plain lookup
  // rather than an optional chain.
  const mmErrors: MatchmakingErrors = errors.matchmaking ?? {};

  // The fourth and fifth dirty flags, and the two that cost the most: naming
  // either field in a PATCH rescores every guild of the project — the server
  // does it on the field's PRESENCE, without comparing values, so that a
  // rescore that failed can be repeated by saving again. SettingsTab keeps
  // them off the wire while they are unchanged; this pair is what puts the
  // warning on screen, and takes it away again when a value is typed back.
  const savedStatKey = initial?.guild_stat_key ?? "";
  const savedAggregator = (initial?.guild_aggregator ?? "") as GuildAggregator | "";
  const statKeyDirty =
    mode === "edit" && autoScored && state.guild_stat_key !== savedStatKey;
  const aggregatorDirty =
    mode === "edit" &&
    autoScored &&
    !!state.guild_aggregator &&
    state.guild_aggregator !== savedAggregator;

  // Is a key being CHOSEN right now? The prose box under the block, and the
  // coverage count folded into it, describe a decision in progress: which stat
  // this board is about to rank on. On a saved board that is settled, and
  // mockup §5 draws that block as the pill, the key and the aggregator — no
  // previewBox at all, only the alert under the "changed" gate.
  //
  // It is also what the count COSTS. The endpoint counts with
  // `count(*) FILTER (WHERE stats ? $key)`, and players_stats_gin is built on
  // jsonb_path_ops, which does not serve `?` — so every render of that
  // sentence is a sequential scan of the project's players. Spending one on
  // each open of Settings buys a line about a board that is already running.
  // Same rule as the retention impact next door, which asks only while the
  // term is actually being lowered.
  const statKeyBeingChosen = mode === "create" || statKeyDirty;

  // The event's own dates, against the cycle they describe. A start that has
  // happened is history; a window whose rewards are out has already been read
  // by the players it paid.
  const eventStartLocked = mode === "edit" && eventCycleStarted(eventCycleStatus);
  const eventEndLocked = mode === "edit" && eventCycleFinished(eventCycleStatus);

  // The third dirty flag, for the same reason as the two above: what the rule
  // is about is the operator's edit, not the board's stored value. In create
  // mode there is nothing stored, so every date is one they picked.
  const savedEventEnd = (initial?.event_end_at ?? "").trim();
  const eventEndDirty =
    mode === "create" || state.event_end_at.trim() !== savedEventEnd;

  // The date the open cycle was announced to close on, as both branches of the
  // `apply` choice print it.
  const announcedClose = formatCloseAt(openCycleCloseAt);

  // Where `cron_apply=current_cycle` moves that date on a recurring_event
  // board: the running window's own start plus the new length
  // (service.rescheduledCloseAt). Addition, not a cron walk — which is why
  // this one is named rather than described, unlike `recurring`'s, whose new
  // end is a tick only the server can find.
  //
  // null whenever any of the three facts is missing — no open cycle, no answer
  // from the host yet, a length the box does not hold — and the sentence is
  // then written without it rather than around a date nobody computed.
  const rescheduledClose = (() => {
    if (state.window_kind !== "recurring_event" || !openCycleOpenAt) return null;
    const minutes = parseWindowMinutes(state);
    if (minutes === null) return null;
    const openedAt = new Date(openCycleOpenAt).getTime();
    if (Number.isNaN(openedAt)) return null;
    return new Date(openedAt + minutes * 60_000);
  })();
  const rescheduledCloseText = rescheduledClose
    ? formatCloseAt(rescheduledClose.toISOString())
    : null;
  // A window shortened past the time it has already been open closes at a
  // moment that has gone by. The server writes that close_at all the same and
  // the worker acts on it at its next tick — so the date is still the truth,
  // and only «and the rewards are handed out then» would not be.
  const rescheduledClosePassed =
    rescheduledClose !== null && rescheduledClose.getTime() < Date.now();

  // An emptied metadata box is an instruction only where there is something to
  // erase — see buildInput.
  const hadMetadata = initial?.metadata !== undefined && initial?.metadata !== null;

  const validationContext: ValidationContext = {
    extKeyEditable,
    eventCycleStatus,
    eventEndDirty,
  };

  // --- Retention impact ------------------------------------------------
  // Shortening a board's term burns rewards nobody has claimed yet, so the
  // count is fetched before the save rather than discovered after it.

  const [retentionImpact, setRetentionImpact] = useState<RetentionImpact | null>(
    null,
  );
  const retentionDraft = parseRetentionDays(state.retention_days);
  const savedRetention = initial?.retention_days;
  // Only a shorter term destroys anything — whatever the sweep would remove at
  // the new number it would have removed at the old one too. And only in edit
  // mode: a board that isn't saved yet has nothing to lose.
  const retentionLowered =
    mode === "edit" &&
    retentionDraft !== null &&
    savedRetention !== undefined &&
    retentionDraft < savedRetention;

  // The host rebuilds its arrow on every render, so the request is driven off
  // the number alone: with the function in the dependency array every answer
  // would schedule the next one.
  const impactSource = useRef(fetchRetentionImpact);
  useEffect(() => {
    impactSource.current = fetchRetentionImpact;
  }, [fetchRetentionImpact]);

  useEffect(() => {
    const ask = impactSource.current;
    if (!retentionLowered || retentionDraft === null || !ask) return;
    let live = true;
    const timer = setTimeout(() => {
      ask(retentionDraft).then(
        (impact) => {
          if (live) setRetentionImpact(impact);
        },
        () => {
          // The alert is nothing but its numbers: with no answer it stays
          // unsaid rather than said with a count nobody counted.
          if (live) setRetentionImpact(null);
        },
      );
    }, RETENTION_IMPACT_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [retentionLowered, retentionDraft]);

  // An answer speaks only for the number it was asked about. While the next
  // request is on its way the previous count belongs to a term the operator
  // has already left behind, and a stale number here is worse than none.
  const shownImpact =
    retentionLowered && retentionImpact?.days === retentionDraft
      ? retentionImpact
      : null;

  // --- Stat-key coverage -----------------------------------------------
  // A key the schema declares and no player writes is a board of zeroes that
  // looks perfectly valid from here. The count is the only thing that catches
  // it while the form is still open. Same shape as the retention impact above,
  // and for the same two reasons: the host rebuilds its arrow every render, and
  // an answer speaks only for the key it was asked about.

  const [coverage, setCoverage] = useState<StatKeyCoverage | null>(null);
  // No key to ask about wherever the sentence is not shown — the request and
  // the line it feeds are gated by one expression, so a scan can never be paid
  // for a paragraph nobody is going to read.
  const coverageKey =
    autoScored && statKeyBeingChosen ? state.guild_stat_key : "";

  const coverageSource = useRef(fetchStatKeyCoverage);
  useEffect(() => {
    coverageSource.current = fetchStatKeyCoverage;
  }, [fetchStatKeyCoverage]);

  useEffect(() => {
    const ask = coverageSource.current;
    if (!coverageKey || !ask) return;
    let live = true;
    const timer = setTimeout(() => {
      ask(coverageKey).then(
        (next) => {
          if (live) setCoverage(next);
        },
        () => {
          // The line is nothing but its numbers — with no answer it stays
          // unsaid rather than said with a count nobody counted.
          if (live) setCoverage(null);
        },
      );
    }, STAT_KEY_COVERAGE_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [coverageKey]);

  // While the next answer is on its way the previous count belongs to a key
  // the operator has already left behind.
  const shownCoverage = coverage?.key === coverageKey ? coverage : null;

  /**
   * The one way this form writes state. Two jobs, and both of them belong to
   * every write: apply the patch, and drop the messages of the fields it
   * names — a field the operator has just moved is a field whose complaint is
   * out of date.
   *
   * Everything goes through `confineAutoScoredWindow`, so the auto board's
   * `all_time` cannot be left behind by a control that lives somewhere else.
   * A hand-rolled `setState` skips both, which is how the Window section came
   * to show a pill over a `recurring` it was still sending, and how a pairing
   * error outlived the `operator` cards it was about.
   *
   * A third kind of message outlives `patch` naming it, too:
   * `confineAutoScoredWindow` can rewrite `window_kind` to `all_time` on a
   * patch that never mentioned it — flipping `scope` or `guild_score_source`
   * is enough — and leaving `scope=guild_aggregated` takes the aggregator
   * control off the page without anyone touching `guild_aggregator` either.
   * Both leave the field the message was about gone from the screen with the
   * message still up. Cleared here, off the CONFINED `next` rather than off
   * `patch`, because that is the one state validate() can never re-produce
   * either of them from: `cron` / `event_start_at` / `event_end_at` only ever
   * come from `window_kind !== "all_time"`, and `guild_aggregator` only from
   * `scope === "guild_aggregated"`.
   */
  function updateMany(patch: Partial<FormState>) {
    setState((s) => {
      const next = confine({ ...s, ...patch });
      if (submitted) {
        setErrors((prev) => {
          const nextErrors = { ...prev };
          for (const key of Object.keys(patch)) {
            delete nextErrors[key as keyof FormErrors];
          }
          if (next.window_kind === "all_time") {
            delete nextErrors.cron;
            delete nextErrors.event_start_at;
            delete nextErrors.event_end_at;
          }
          // The length is one value spread over two controls and complains in
          // one message, so it is named by neither key — moving either the
          // number or the unit is what makes the message out of date, and
          // leaving the window kind takes both off the page with it.
          if (
            "window_value" in patch ||
            "window_unit" in patch ||
            next.window_kind !== "recurring_event"
          ) {
            delete nextErrors.window_minutes;
          }
          if (next.scope !== "guild_aggregated") {
            delete nextErrors.guild_aggregator;
          }
          // …and on the one scope that keeps the row, the source can take the
          // control away too: member_contribution fixes the aggregator itself.
          if (isMemberContribution(next)) {
            delete nextErrors.guild_aggregator;
          }
          // confineGuildSegmentation can drop the tier branch on a patch that
          // named `scope` alone, leaving the messages of a section that is no
          // longer on the page. Cleared off the CONFINED state, for the same
          // reason the window's are.
          if (next.segmentation !== "tier") {
            delete nextErrors.tier_mode;
            delete nextErrors.tier_config;
            delete nextErrors.tierRows;
            delete nextErrors.promote_top_k;
            delete nextErrors.demote_bottom_k;
          }
          if (
            next.segmentation !== "bucket" &&
            !(next.segmentation === "tier" && next.rooms)
          ) {
            delete nextErrors.bucket_size;
          }
          if (!isMemberContribution(next)) {
            delete nextErrors.min_guild_members;
          }
          return nextErrors;
        });
      }
      return next;
    });
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    updateMany({ [key]: value } as Partial<FormState>);
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    // Which button the Enter key stands for. On a create form there is only
    // one; on a saved board it is whichever of the two is live, because a
    // form whose primary action is greyed out must not submit through the
    // greyed-out one.
    if (mode === "edit" && saveNowRefusal) {
      // …and where the greyed-out one is the only one there is, Enter does
      // nothing. Scheduling on a board that cuts no further cycle would put
      // the edit somewhere it can never come out of.
      if (boardCuts) submitWith("schedule");
      return;
    }
    submitWith("now");
  }

  function submitWith(intent: SubmitIntent) {
    setSubmitted(true);
    const errs = validate(state, mode, validationContext);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // The pair the server is about to judge. Its answer stays on screen until
    // the next save — the host holds it in a mutation — so the form has to
    // decide for itself when it stopped being about what is in the row: an
    // operator who shortens the window after a refusal must not be left
    // staring at a red border quoting the length they just replaced. Both
    // halves, because the refusal is about the pair: a new cron makes the old
    // answer as stale as a new length does.
    setJudgedSchedule({
      cron: state.cron.trim(),
      minutes: parseWindowMinutes(state),
    });
    const rewards =
      mode === "create"
        ? buildRewardInputs(state.rewards, state.tiers, state.segmentation)
        : [];
    onSubmit(buildInput(state, hadMetadata), rewards, {
      // The choice is only ever asked for while the schedule is actually
      // changing — either half of it. With the row gone there is nothing to
      // apply, and the default is what an untouched schedule means anyway.
      cronApply: scheduleDirty ? state.cron_apply : "next_cycle",
      intent,
      changedFields: dirtyFields,
      // Only where the question was actually put. Sending "reset" from every
      // save would tell the server a decision nobody made, about a ladder the
      // edit does not touch.
      ladderDecision: ladderQuestion ? ladderDecision : undefined,
    });
  }

  // --- Tier rows -------------------------------------------------------
  // Row errors are keyed by index, so any structural change (add / remove /
  // move / sort) drops them wholesale rather than leaving them pinned to the
  // wrong row; a plain field edit only clears the row it touched.

  function clearTierErrors() {
    if (!submitted) return;
    setErrors((prev) => ({
      ...prev,
      tierRows: undefined,
      tier_config: undefined,
    }));
  }

  function clearTierRowError(index: number, field: keyof TierRowError) {
    if (!submitted) return;
    setErrors((prev) => {
      const row = prev.tierRows?.[index];
      if (!row) return prev;
      const nextRow = { ...row, [field]: undefined };
      const nextRows = { ...prev.tierRows, [index]: nextRow };
      return { ...prev, tierRows: nextRows };
    });
  }

  function addTier() {
    setState((s) => ({ ...s, tiers: [...s.tiers, emptyTierDraft()] }));
    clearTierErrors();
  }

  function removeTier(uid: string) {
    setState((s) => ({ ...s, tiers: s.tiers.filter((t) => t.uid !== uid) }));
    setTierPendingRemoval(null);
    clearTierErrors();
  }

  /** Deleting a persisted tier strands its players and rewards, so it asks
     first. Rows added in this session go straight out. */
  function requestRemoveTier(t: TierDraft) {
    if (t.saved) {
      setTierPendingRemoval(t.uid);
      return;
    }
    removeTier(t.uid);
  }

  function updateTier(index: number, patch: Partial<TierDraft>) {
    setState((s) => ({
      ...s,
      tiers: s.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));
  }

  /** Typing a name fills the key with its slug until the key is edited by
     hand (or belongs to an already-saved tier, where it's immutable). */
  function updateTierName(index: number, name: string) {
    setState((s) => ({
      ...s,
      tiers: s.tiers.map((t, i) => {
        if (i !== index) return t;
        const next: TierDraft = { ...t, name };
        if (!t.keyDirty && !t.saved) next.key = slugifyTierKey(name);
        return next;
      }),
    }));
    clearTierRowError(index, "name");
    clearTierRowError(index, "key");
  }

  function moveTier(index: number, delta: -1 | 1) {
    setState((s) => {
      const target = index + delta;
      if (target < 0 || target >= s.tiers.length) return s;
      const next = [...s.tiers];
      [next[index], next[target]] = [next[target], next[index]];
      return { ...s, tiers: next };
    });
    clearTierErrors();
  }

  /** Re-sort on blur rather than on every keystroke — reordering mid-typing
     would yank the row out from under the cursor. */
  function sortTiersByThreshold() {
    setState((s) =>
      isThresholdTierMode(s.tier_mode) ? { ...s, tiers: sortByMinScore(s.tiers) } : s,
    );
    clearTierErrors();
  }

  function addRewardDraft() {
    setState((s) => ({ ...s, rewards: [...s.rewards, emptyRewardDraft()] }));
  }

  function updateRewardDraft(index: number, patch: Partial<RewardDraft>) {
    setState((s) => ({
      ...s,
      rewards: s.rewards.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
    if (submitted) {
      setErrors((prev) => {
        if (!prev.rewardRows) return prev;
        const next = { ...prev.rewardRows };
        delete next[index];
        return { ...prev, rewardRows: next, rewards: undefined };
      });
    }
  }

  /**
   * The rows a file brought, appended to the draft or put in its place. No
   * request is involved and nothing is saved — they are ordinary draft rows
   * from here on, created with the board like the ones typed by hand.
   */
  function applyImportedRewards(
    rows: RewardDraft[],
    importMode: ImportRewardsMode,
    fileName: string,
  ) {
    setState((s) => ({
      ...s,
      rewards:
        importMode === "replace"
          ? rows
          : // The highlight belongs to the newest import alone; rows an
            // earlier one left behind give it up rather than pile up.
            [
              ...s.rewards.map((r) => (r.imported ? { ...r, imported: false } : r)),
              ...rows,
            ],
    }));
    setImported({ count: rows.length, fileName });
    setImportOpen(false);
    // Row errors are keyed by index. Replace throws every existing row away, so
    // its errors go with them; append leaves rows 0..n-1 exactly where they
    // were and only adds rows after them, so an error still marking one of
    // those rows is still true and has to stay on screen.
    if (submitted && importMode === "replace") {
      setErrors((prev) => ({ ...prev, rewardRows: undefined, rewards: undefined }));
    }
  }

  function removeRewardDraft(index: number) {
    setState((s) => ({
      ...s,
      rewards: s.rewards.filter((_, i) => i !== index),
    }));
    if (submitted) {
      setErrors((prev) => {
        if (!prev.rewardRows) return prev;
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prev.rewardRows)) {
          const ki = Number(k);
          if (ki === index) continue;
          next[ki < index ? ki : ki - 1] = v;
        }
        return { ...prev, rewardRows: next };
      });
    }
  }

  const effectiveSubmitLabel =
    submitLabel ?? (mode === "create" ? "Create leaderboard" : "Save changes");

  // --- What this edit is, and which of the two saves can carry it ------
  //
  // Everything below reads ONE fact: the payload the form is holding, compared
  // field by field against the board it was opened on. The save bar's counter,
  // the two buttons' states, the filter and the package the host builds are
  // all views of that list — computed once here rather than four times in
  // four places that could disagree.

  /** The payload as it stands, without submitting anything. */
  const draftInput = useMemo(
    () => buildInput(state, hadMetadata),
    [state, hadMetadata],
  );

  /**
   * What this form WOULD have submitted had nobody touched it — the payload
   * built from the state it opens on.
   *
   * The baseline is this rather than `initial` itself, and the difference is
   * not tidiness. `initial` is a partial: a board with no window length names
   * no `window_minutes`, and a host that hands over a half-filled shape names
   * neither `scope` nor `segmentation`. Compared against that, the form's own
   * defaults read as an edit the operator never made — and a defaulted `scope`
   * is a boundary field, so the page would grey out "Save now" on a board
   * nobody had changed. Built through the same `confine` + `buildInput` the
   * draft goes through, so only a keystroke can make the two differ. The
   * stored `realtime` is laid over it: buildInput withholds the flag from a
   * board without a table, and adding rooms would then count the untouched
   * flag as an edit.
   *
   * The seed is deliberately NOT in it: values put back from a refused package
   * are an unsaved edit, and an edit is exactly what a baseline must not
   * contain.
   */
  const baselineInput = useMemo(() => {
    const opened = confine(initialState(initial, mode));
    return { realtime: opened.realtime, ...buildInput(opened, hadMetadata) };
  }, [initial, mode, hadMetadata]);

  /** Fields the form holds differently from the board. Empty in create mode:
     a board that does not exist yet has nothing to differ from. */
  const dirtyFields = useMemo(
    () =>
      mode === "edit"
        ? diffChangedFields(
            draftInput as unknown as Record<string, unknown>,
            baselineInput as unknown as Record<string, unknown>,
          )
        : [],
    [mode, draftInput, baselineInput],
  );

  // --- The ladder question ---------------------------------------------
  //
  // A league is the one thing on this board that outlives a cycle: the
  // snapshot does not carry it, so a package that takes the board off its
  // ladder — or off the rung a player is standing on — has to say out loud
  // what becomes of those rows.
  const [ladderDecision, setLadderDecision] = useState<LadderDecision>("reset");
  const storedLadder =
    mode === "edit" &&
    initial?.segmentation === "tier" &&
    initial?.tier_mode === "promotion";
  /** Rungs the new ladder does not have, among the ones players stand on. */
  const orphanedTiers = useMemo(() => {
    if (!storedLadder || !ladderImpact) return [];
    const kept = new Set(
      state.segmentation === "tier"
        ? state.tiers.map((t) => t.key.trim()).filter(Boolean)
        : [],
    );
    return ladderImpact.tiers.filter(
      (t) => t.players > 0 && !kept.has(t.tier_key),
    );
  }, [storedLadder, ladderImpact, state.segmentation, state.tiers]);
  const leavesLadder =
    storedLadder &&
    (state.segmentation !== "tier" || state.tier_mode !== "promotion");
  const ladderQuestion = Boolean(leavesLadder || orphanedTiers.length > 0);
  const orphanedPlayers = orphanedTiers.reduce((n, t) => n + t.players, 0);

  /**
   * Everything the save bar counts. The ladder decision is one of them even
   * though no column holds it: it is a choice the operator has to make, it
   * travels in the package, and a counter that left it out would say "1
   * unsaved change" about a save that also wipes thirty thousand leagues.
   */
  const countedChanges = ladderQuestion
    ? [...dirtyFields, "ladder_decision"]
    : dirtyFields;

  /**
   * The ones an immediate PATCH will not take.
   *
   * Measured against THIS board, not against the list alone. A field can only
   * wait for a cut on a board that has one — and the two schedule-only fields
   * that survive on a board without one (`guild_aggregator`, `guild_stat_key`,
   * live on every scope=guild_aggregated board including the all_time ones an
   * auto-scored board is confined to) would otherwise be uneditable for good:
   * a stat key renamed in the project's schema would kill the board with no
   * way back. There they go out immediately, as they always did.
   */
  const cutOnlyDirty = dirtyFields.filter(
    (f) => boardCuts && whenBadge(f) === "cut",
  );
  /** A change that can only land at a cut cannot be made by hand — the server
     refuses the field outright, so the button that would sell it is dead. */
  const canSaveNow = cutOnlyDirty.length === 0;
  /**
   * Why "Save now" is greyed out, or nothing at all when it is live. The two
   * reasons are one control: whichever of them holds, the bar looks the same —
   * an unclickable-but-readable primary, and "Schedule for next cycle" taking
   * its place. The host's reason is named first because it is the harder of
   * the two: reverting the boundary fields would not make an unreadable
   * package readable.
   */
  const saveNowRefusal =
    saveNowBlocked ??
    (canSaveNow
      ? undefined
      : `Revert those ${cutOnlyDirty.length}, or schedule the lot`);
  const dirty = countedChanges.length > 0;

  /** Sections holding a change that needs a cut — what the filter keeps. */
  const cutSections = useMemo(() => {
    const out = new Set<string>();
    for (const f of cutOnlyDirty) {
      // Card names of THIS form, which is not the mockup's grouping: the
      // window rows live inside Scoring here, and Scope and Segmentation are
      // two cards rather than one.
      if (f === "window_kind" || f === "window_minutes") out.add("Scoring");
      else if (f === "segmentation" || f === "tier_mode") out.add("Segmentation");
      else out.add("Scope");
    }
    return out;
  }, [cutOnlyDirty]);
  const [onlyCuts, setOnlyCuts] = useState(false);
  // The filter cannot outlive the thing it filters: reverting the last
  // boundary change would otherwise leave the page showing two sections and no
  // way back to the rest of the form.
  if (onlyCuts && cutSections.size === 0) setOnlyCuts(false);
  function sectionHidden(name: string): boolean {
    return onlyCuts && !cutSections.has(name);
  }

  /**
   * The schedule-only fields this board draws but cannot move, because it will
   * never cut another cycle. Counted rather than written down: the number is
   * in a sentence the operator reads against the page in front of them, and
   * the page shows different rows on different boards.
   */
  const frozenCutFields = cutFrozen
    ? [
        "window_kind",
        "scope",
        "segmentation",
        ...(state.segmentation === "tier" ? ["tier_mode"] : []),
        ...(state.scope === "guild_aggregated" ? ["guild_score_source"] : []),
      ]
    : [];

  /**
   * The half-sentence after the count. One of three facts, in the order they
   * matter: the leagues a decision is about, the fields that cannot be saved
   * by hand, and — when neither applies — when the cut this edit could wait
   * for happens.
   */
  const saveBarSuffix = ladderQuestion
    ? ladderImpact
      ? `one of them decides what happens to ${formatCount(ladderImpact.players)} leagues`
      : "one of them decides what happens to the leagues players already hold"
    : cutOnlyDirty.length > 0
      ? countedChanges.length === 1
        ? "it can only take effect at a cut"
        : `${cutOnlyDirty.length} of them can only take effect at a cut`
      : nextCut
        ? `${nextCut.kind === "opens" ? "next cycle opens" : "next cut"} ${SHORT_CUT_FMT.format(new Date(nextCut.at))}`
        : "either save will do";

  /** Put the form back where it opened. The Cancel of a create form leaves the
     page; on a saved board there is no page to leave, so the button undoes the
     edit instead — which is what "Discard" says it does. */
  function discard() {
    setState(confine(initialState(initial, mode)));
    setErrors({});
    setSubmitted(false);
    setLadderDecision("reset");
    setOnlyCuts(false);
    onCancel();
  }

  // The mode the editor is actually driving. state.tier_mode is "" until
  // segmentation flips to tier, and the seg control already shows score_bucket
  // as active at that point, so the fallback keeps both in step.
  const tierMode = (state.tier_mode || "score_bucket") as TierMode;
  const claimGated = state.segmentation === "tier" && tierMode === "claim_gated";

  // Whether the map below is the whole truth yet. Until the `rewards` prop
  // arrives it is empty for lack of an answer rather than for lack of rewards,
  // so everything that reads a missing entry as a hole in the ladder has to
  // hold its tongue meanwhile.
  const rewardsKnown = mode === "create" || savedRewards !== undefined;

  /**
   * Which reward opens each tier row, keyed by uid. In create mode the drafts
   * of section 5 are the only rewards there are; in edit mode they live on the
   * Rewards tab and reach the form through the `rewards` prop. Either way the
   * tier editor only mirrors the binding — it never makes one.
   */
  const unlockRewards: Record<string, string> = {};
  if (claimGated) {
    if (mode === "create") {
      for (const r of state.rewards) {
        if (r.type !== "tier_threshold" || r.tier_uid === ALL_TIERS) continue;
        const row = state.tiers.find((t) => t.uid === r.tier_uid);
        // Only a real name goes in. A draft still being typed has nothing to
        // show and nothing to narrate, and its own row already says the name
        // is required — inventing a placeholder here would only leak it into
        // the ladder ribbon and the explanation below it.
        const name = r.name.trim();
        if (row && name) unlockRewards[row.uid] = name;
      }
    } else {
      for (const r of savedRewards ?? []) {
        if (!isTierThresholdReward(r)) continue;
        const row = state.tiers.find((t) => t.key.trim() === r.tier_key);
        if (row) unlockRewards[row.uid] = r.name;
      }
    }
  }

  const rungs = claimGated ? ladderRungs(state.tiers) : [];
  // First rung nobody can climb to: no threshold reward points at it, so the
  // players on the rung below stay there forever. The API accepts such a board
  // — the reward may legitimately be added later — so this is a warning, and
  // the form still submits. -1 while the rewards are unknown: every rung looks
  // unpaid then, and a correctly built ladder would be accused of breaking at
  // its first rung on every single visit to the Settings tab.
  const gapIndex = rewardsKnown
    ? rungs.findIndex((t, i) => i > 0 && !unlockRewards[t.uid])
    : -1;

  // Saved tiers whose threshold the operator has moved in this session. The
  // new value only takes effect from the next submit, and it never demotes
  // anyone who already claimed their way up — worth saying out loud.
  const shiftedTiers =
    claimGated && mode === "edit"
      ? state.tiers.filter((t) => {
          if (!t.saved || t.savedMinScore === null) return false;
          const was = parseMinScore(t.savedMinScore);
          const now = parseMinScore(t.min_score);
          return was !== null && now !== null && was !== now;
        })
      : [];

  // Something is already warning the operator about this very ladder. The
  // mockup shows one alert in those states, not a warning with a cheerful
  // explanation stapled underneath — the story is what the ladder does when it
  // works, and it comes back once the warning is answered.
  const ladderWarned = gapIndex > 0 || shiftedTiers.length > 0;

  // Has the rooms setting moved away from what the board has stored? Compared
  // as the value that would go on the wire — a size typed while the switch is
  // off changes nothing, and neither does re-typing the size already saved.
  const savedRoomSize =
    initial?.bucket_size !== undefined && initial?.bucket_size !== null
      ? initial.bucket_size
      : null;
  const draftRoomSize = state.rooms ? Number(state.bucket_size) : null;
  const roomsDirty = savedRoomSize !== draftRoomSize;

  // The reward rows only carry a Tier column while the board is actually
  // tiered. Flip segmentation away from tier and the column disappears
  // together with any tier_key the drafts were holding.
  const rewardsHaveTier = state.segmentation === "tier";
  const rewardTypeOptions = [
    { value: "rank_range", label: "rank_range" },
    { value: "percentile", label: "percentile" },
    // ValidateRewardCreate rejects a threshold reward unless the board runs
    // claim_gated, so the option isn't offered anywhere else.
    ...(claimGated ? [{ value: "tier_threshold", label: "tier_threshold" }] : []),
  ];
  // Labels are built from the unfiltered rows so a nameless one keeps the
  // number the tier editor gives it, whichever list it ends up in.
  const tierRowOptions = state.tiers.map((t, i) => ({
    value: t.uid,
    label: t.name.trim() || t.key.trim() || `Tier ${i + 1}`,
    meta: t.key.trim(),
  }));
  // The starting league is never offered as a threshold reward's tier: bound
  // there the reward can never be granted, and the API answers a 400 that says
  // nothing about which row is at fault. "All tiers" stays — it is the "not
  // chosen yet" state the draft starts on, and its row error asks for a real
  // tier rather than pretending one was picked.
  const bottomUid = claimGated ? bottomTier(state.tiers)?.uid : undefined;
  const thresholdRowOptions = tierRowOptions.filter(
    (o) => o.value !== bottomUid,
  );
  const allTiersOption = { value: ALL_TIERS, label: "All tiers", muted: true };
  const tierOptions = [
    { ...allTiersOption, separatorAfter: tierRowOptions.length > 0 },
    ...tierRowOptions,
  ];
  const thresholdTierOptions = [
    { ...allTiersOption, separatorAfter: thresholdRowOptions.length > 0 },
    ...thresholdRowOptions,
  ];

  /* Published to every row of the form at once — see pendingContext. In
     create mode it publishes nothing, and every mark disappears with it. */
  const marks = useMemo(
    () => ({ pending: pending ?? null, marked: mode === "edit", cuts: boardCuts }),
    [pending, mode, boardCuts],
  );

  return (
    <PendingMarksCtx.Provider value={marks}>
    <form className={styles.formGrid} onSubmit={handleSubmit} noValidate>
      {/* ============ SECTION 1: Identity ============ */}
      <section className={styles.section} hidden={sectionHidden("Identity")}>
        <div className={styles.cardTitle}>
          <h3>Identity</h3>
          <span className={styles.cardSub}>step 1 of 5</span>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.sectionDesc}>
            Unique slug used by the SDK, display name, and description.
          </p>

          <FieldRow
            label="Ext key"
            apiName="ext_key"
            hint={
              mode === "create"
                ? // Still worth warning about while it is being chosen, but no
                  // longer as "never": archiving the board gives it back.
                  "Slug, lowercase, snake_case. Locked once the board is live — only an archived one can be renamed."
                : extKeyEditable
                  ? "Slug, lowercase, snake_case."
                  : "The handle the SDK submits to. Can only be changed on an archived leaderboard."
            }
            required={mode === "create"}
            error={errors.ext_key}
            htmlFor={extKeyEditable ? extKeyId : undefined}
          >
            {extKeyEditable ? (
              <>
                <input
                  id={extKeyId}
                  className={`${styles.input} ${styles.mono} ${
                    errors.ext_key ? styles.invalid : ""
                  }`}
                  placeholder="weekly_score"
                  value={state.ext_key}
                  onChange={(e) => update("ext_key", e.target.value)}
                />
                {/* The board is archived — which is the whole reason the box
                   is here. Renaming it is what releases the old key, and the
                   sentence after that is the cost of doing so. */}
                {mode === "edit" && (
                  <div className={styles.helper}>
                    Renaming an archived board frees <code>{initial?.ext_key}</code>{" "}
                    for a new leaderboard. Snapshot import matches boards by this
                    key, so an import built for the old name will create a new
                    board instead of updating this one.
                  </div>
                )}
              </>
            ) : (
              <LockedValue value={state.ext_key} note="lock">
                Locked while the leaderboard is active — archive it to rename
              </LockedValue>
            )}
          </FieldRow>

          <FieldRow
            label="Name"
            apiName="name"
            hint="Display name in the admin UI."
            required
            error={errors.name}
            htmlFor={nameId}
          >
            <input
              id={nameId}
              className={`${styles.input} ${errors.name ? styles.invalid : ""}`}
              placeholder="Weekly Score"
              value={state.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </FieldRow>

          <FieldRow
            label="Description"
            apiName="description"
            hint="Optional."
            htmlFor={descId}
          >
            <textarea
              id={descId}
              className={styles.textarea}
              placeholder="Top scorers of the week…"
              value={state.description}
              onChange={(e) => update("description", e.target.value)}
            />
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 2: Scoring ============ */}
      <section className={styles.section} hidden={sectionHidden("Scoring")}>
        <div className={styles.cardTitle}>
          <h3>Scoring</h3>
          <span className={styles.cardSub}>step 2 of 5</span>
        </div>
        <div className={styles.cardBody}>
          {/* Removed, not greyed out. A dimmed field still reads as "a setting
             I haven't grown into", and a month later nobody remembers why it
             was grey — whereas a board with no submits has no incoming value
             to interpret, to permit or to bound, and there is nothing behind
             the three fields to come back to. One Alert stands where they
             were; it is the card's body, so it carries no field label. */}
          {autoScored ? (
            <div className={styles.autoScoredNotice}>
              <Alert
                variant="info"
                title="This board is scored by the server, so there is nothing to submit to it."
              >
                <code>operator</code>, <code>submit_policy</code> and the submit
                guards do not apply and are not shown — there is no incoming
                value to interpret, to permit or to bound. Nor is{" "}
                <code>realtime</code>: <code>rank.changed</code> rides on a
                submit, and the server&rsquo;s own recompute broadcasts nothing.
              </Alert>
            </div>
          ) : (
            <FieldRow
              label="Operator"
              apiName="operator"
              hint={
                contribution
                  ? "How to interpret incoming SDK score values. Set by the score source on this board."
                  : mode === "edit"
                    ? // The reading changes for submits from here on; the
                      // scores already in the table were folded by the old
                      // operator and are not recomputed by anything.
                      "How to interpret incoming SDK score values. Applies to submits made from now on — scores already stored keep the value they have."
                    : "How to interpret incoming SDK score values."
              }
              full
            >
              {/* A contribution IS an accumulation: the member's own total for
                 the cycle, which the guild's score is the sum of. The server
                 fixes the pair (increment ↔ sum) and refuses a create body
                 that names either half, so the four cards would be a chooser
                 over a value nobody gets to choose. The row stays — unlike on
                 an auto-scored board, this one still takes submits, and what
                 happens to them is worth saying. */}
              {contribution ? (
                <LockedValue value="increment">
                  A member&rsquo;s contribution is what they accumulate over
                  the cycle, and the team&rsquo;s score is the{" "}
                  <code>sum</code> of those. The pair is set by{" "}
                  <code>member_contribution</code> and cannot be edited on this
                  source.
                </LockedValue>
              ) : (
                <RadioCardGroup
                  name="operator"
                  options={OPERATOR_OPTIONS}
                  value={state.operator}
                  cols={4}
                  onChange={(v) => update("operator", v)}
                />
              )}
            </FieldRow>
          )}

          <FieldRow
            label="Sort"
            apiName="sort"
            /* The one Scoring field an auto-scored board keeps: it is about
               reading, not writing, and here it decides which end of the table
               the strong guilds sit at. */
            hint={
              autoScored
                ? "Score sort direction. On this board it decides whether the strongest guilds sit at the top or the bottom."
                : "Score sort direction. Decoupled from operator."
            }
          >
            <SegRadio
              name="sort"
              options={[
                { value: "desc", label: "desc" },
                { value: "asc", label: "asc" },
              ]}
              value={state.sort}
              onChange={(v) => update("sort", v)}
            />
            {/* The order is a read-side derivation, so flipping it rewrites
               the standings of the interval that is open right now — the same
               slot, and the same reasoning, as the rooms warning below. */}
            {sortDirty && (
              <div className={styles.alertSlot}>
                <Alert
                  variant="warn"
                  title="Ranks in the running cycle will be re-drawn"
                >
                  Saving flips the order of the interval that is open right now:
                  the current leader becomes the last place. Scores are
                  untouched — only the order they are read in changes, and
                  rewards are handed out by the new order when the cycle closes.
                  {/* The second consequence of the same click, and the only one
                     in this section the operator cannot see before pressing
                     Save: the row below goes grey, but nothing there says that
                     saving TURNS THE TOGGLE OFF on a board that is enrolling
                     players right now. The server refuses the pair outright, so
                     there is no third option to offer — only the choice between
                     saying so and not. */}
                  {autoEnrollTurnsOff && (
                    <p>
                      <b>Auto-enrol will be switched off</b> by this save: it is
                      not allowed on an <code>asc</code> board, and this one is
                      enrolling players today. Rows already written stay — they
                      are ordinary rows — but no new player joins by reading.
                    </p>
                  )}
                </Alert>
              </div>
            )}
          </FieldRow>

          {/* Auto-enrol — directly under `sort`, because `sort` is what decides
             whether the toggle is allowed at all. See autoEnrollOffered /
             autoEnrollBlocked above for why one refusal hides the row and the
             other only dims it. */}
          {autoEnrollOffered && (
            <FieldRow
              label="Auto-enrol players"
              apiName="auto_enroll"
              mutedLabel={autoEnrollBlocked}
            >
              <div
                className={`${styles.switchRow} ${
                  autoEnrollBlocked ? styles.rowMuted : ""
                }`}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoEnrollOn}
                  aria-label="Auto-enrol players"
                  disabled={autoEnrollBlocked}
                  className={`${styles.switch} ${
                    autoEnrollOn ? styles.switchOn : ""
                  }`}
                  onClick={() => update("auto_enroll", !state.auto_enroll)}
                >
                  <span className={styles.switchKnob} aria-hidden />
                </button>
                {/* One label in both positions, like the Hold switch: it names
                   what the setting does, and the Off half of the story is the
                   hint below. */}
                <span className={styles.switchLabel}>
                  Put a player on the board with 0 the first time they read it
                </span>
              </div>
              {/* .hint rather than FieldRow's own, for the reason the Hold
                 switch gives: it runs under the control at full width and
                 carries code spans. Three texts, one per state of the row. */}
              <div className={styles.hint}>
                {autoEnrollBlocked ? (
                  <>
                    Unavailable while <code>sort</code> is <code>asc</code>: on a
                    board where lower is better, a 0 is first place. Switch the
                    order to <code>desc</code> to use it.
                  </>
                ) : autoEnrollOn ? (
                  <>
                    A player who opens the board is written into the running
                    cycle with score <b>0</b>, so they see themselves in the
                    table and in <code>GET /me</code> before their first submit.
                    The row is an ordinary row from then on.
                  </>
                ) : (
                  <>
                    Off — the previous behaviour: a player appears only after
                    their first submit, and until then <code>GET /me</code>{" "}
                    answers <code>404</code>.
                  </>
                )}
              </div>
              {/* Only while the toggle is on AND the board has a band to be
                 generous with — otherwise there is nothing to tell. */}
              {autoEnrollOn && hasPercentileReward && (
                <div className={styles.alertSlot}>
                  <Alert
                    variant="warn"
                    title="Percentile rewards get more generous"
                  >
                    <p>
                      Enrolled rows count as participants, so a{" "}
                      <code>percentile_from</code>/<code>percentile_to</code>{" "}
                      band is measured against everyone who <b>opened</b> the
                      board, not everyone who played it. «Top 10%» of 1000 rows,
                      900 of them still at 0, is 100 winners instead of 10.
                    </p>
                    <p>
                      Rank ranges are unaffected on a <code>desc</code> board — a
                      0 sits at the bottom.
                    </p>
                  </Alert>
                </div>
              )}
            </FieldRow>
          )}

          <FieldRow
            label="Window kind"
            apiName="window_kind"
            hint={
              cutFrozen
                ? "Lifecycle of stored scores. Fixed after creation — the cycles already opened were cut by it."
                : mode === "edit"
                  ? "The cycles already cut belong to the old kind. A new kind describes the ones not cut yet — so it takes effect where the next one is cut."
                  : "Lifecycle of stored scores."
            }
            full
            error={errors.cron || errors.event_start_at || errors.event_end_at}
            /* The row wraps the whole schedule reveal, so the "Scheduled: …"
               line is placed by hand under the chooser it is about rather than
               at the bottom of everything hanging off it. */
            pendingField={false}
          >
            {/* Every cycle this board has ever opened was cut by this value,
               and the CHECK constraints tie the schedule columns to it — so in
               edit mode it is a pill, like segmentation and tier_mode. What
               hangs off it (the schedule, the dates) is still live.

               On an auto-scored board it is a pill in CREATE mode too, and for
               a different reason: not "already decided" but "never a choice".
               The score is a snapshot of the present, so there is no history
               for a cycle to cut — CHECK lb_guild_score_source_window_check
               allows all_time alone. */}
            {autoScored ? (
              /* The value comes from the STATE, never from the literal the
                 board is confined to. A pill hard-coded to `all_time` is what
                 let a window picked elsewhere hide under it: the form said one
                 thing, the payload carried another, and create answered 400
                 about a field the page insisted was already correct. */
              <LockedValue value={state.window_kind} note="lock">
                The score is a snapshot of the present, so a cycle reset would
                bring back the same number. There is nothing to cut.
              </LockedValue>
            ) : cutFrozen ? (
              /* No cut is coming — an event window closes without a successor,
                 an all_time one never closes — so there is no moment the new
                 kind could take effect at, and the pill is the truth again. */
              <LockedValue value={state.window_kind}>
                {WINDOW_LOCKED_HELPER[state.window_kind]}
              </LockedValue>
            ) : (
              /* 2×2 rather than one row of four: the descriptions are what
                 tell these four apart, and a quarter of the row leaves
                 "A timed window on a schedule — closed in between." two words
                 wide (mockup §1). */
              <RadioCardGroup
                name="window_kind"
                /* `all_time` is greyed out under member_contribution rather
                   than removed: it is one of four cards that read as a set,
                   and the reveal block in Guild scoring already names it as
                   the refused one. */
                options={windowOptions(state)}
                value={state.window_kind}
                cols={2}
                onChange={(v) => update("window_kind", v)}
              />
            )}
            <PendingNote field="window_kind" />

            {/* What the pill above hides, this hides too. The schedule belongs
               to a window the auto board does not have, and a cron box open
               under an `all_time` pill is the form contradicting itself in the
               space of two rows. */}
            {!autoScored && state.window_kind === "recurring" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    {/* api-style snake_case label per mockup (.rl block) */}
                    <div className={styles.rl}>cron</div>
                    <div className={styles.cronCol}>
                      <input
                        id={cronId}
                        aria-label="Cron"
                        className={`${styles.input} ${styles.mono} ${
                          errors.cron ? styles.invalid : ""
                        }`}
                        placeholder="0 0 * * MON"
                        value={state.cron}
                        onChange={(e) => update("cron", e.target.value)}
                      />
                      {(() => {
                        const a = analyseCron(state.cron);
                        if (a.kind === "ok") {
                          return <div className={styles.cronHuman}>{a.text}</div>;
                        }
                        if (a.kind === "invalid") {
                          return (
                            <div className={styles.cronError}>
                              Invalid cron: {a.reason}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  {/* Only while the schedule actually differs from the stored
                     one: with nothing changing there is no moment to pick, and
                     an untouched cron never reaches the PATCH body at all. */}
                  {scheduleDirty && (
                    <div className={styles.revealRow}>
                      <div className={styles.rl}>apply</div>
                      <div className={styles.revealCol}>
                        <SegRadio
                          name="cron_apply"
                          groupLabel="Apply the new schedule"
                          options={[
                            { value: "next_cycle", label: "from the next cycle" },
                            {
                              value: "current_cycle",
                              label: "to the one running now",
                            },
                          ]}
                          value={state.cron_apply}
                          onChange={(v) => update("cron_apply", v)}
                        />
                        {state.cron_apply === "next_cycle" ? (
                          // The announced date is the whole of what this option
                          // preserves, so it is named (mockup §2). Without it —
                          // no open cycle, or the answer not back yet — the same
                          // sentence, minus the date it would be inventing.
                          <span className={styles.helper}>
                            {announcedClose ? (
                              <>
                                The cycle open right now still closes{" "}
                                <b>{announcedClose}</b> as announced.
                              </>
                            ) : (
                              <>The cycle open right now still closes as announced.</>
                            )}{" "}
                            The new schedule cuts every cycle after it.
                          </span>
                        ) : (
                          <Alert
                            variant="warn"
                            title="The running cycle will close earlier than announced"
                          >
                            {/* The date it moves FROM is a fact we have. The one
                               it moves TO is the first tick of the new schedule
                               after now, which only the server works out — it
                               parses the expression with the same library the
                               cycle worker does, and a second opinion computed
                               here would be a date to the operator and a guess
                               in fact. So: name the one, describe the other. */}
                            {announcedClose ? (
                              <>
                                Its end moves from <b>{announcedClose}</b> to the
                                first tick of the new schedule after now, and the
                                rewards are handed out then.
                              </>
                            ) : (
                              <>
                                Its end moves to the first tick of the new
                                schedule after now, and the rewards are handed
                                out then.
                              </>
                            )}{" "}
                            Players who counted on the announced date lose the
                            days in between.
                          </Alert>
                        )}
                      </div>
                    </div>
                  )}

                  {errors.cron && !errors.cron.startsWith("Invalid cron") && (
                    <div className={styles.fieldError}>{errors.cron}</div>
                  )}
                </div>
              </div>
            )}

            {/* The same reveal strip `recurring` gets, with a second row: here
               a tick of the cron STARTS a window instead of cutting one, and
               what ends it is the length beside it. Both halves are live in
               edit mode, and both are gated on `!autoScored` for the reason
               above — an auto board is confined to `all_time`. */}
            {!autoScored && state.window_kind === "recurring_event" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={`${styles.revealRow} ${styles.revealRowTop}`}>
                    <div className={styles.rl}>cron</div>
                    <div className={styles.cronCol}>
                      <input
                        id={cronId}
                        aria-label="Cron"
                        className={`${styles.input} ${styles.mono} ${
                          errors.cron ? styles.invalid : ""
                        }`}
                        placeholder="0 0 * * FRI"
                        value={state.cron}
                        onChange={(e) => update("cron", e.target.value)}
                      />
                      {(() => {
                        const a = analyseCron(state.cron);
                        if (a.kind === "ok") {
                          // The same sentence `recurring` prints, with the tail
                          // that says what a tick DOES here — the one thing the
                          // two boards read the identical expression for
                          // differently (mockup §2).
                          return (
                            <div className={styles.cronHuman}>
                              {a.text} — every window opens then.
                            </div>
                          );
                        }
                        if (a.kind === "invalid") {
                          return (
                            <div className={styles.cronError}>
                              Invalid cron: {a.reason}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>

                  <div className={`${styles.revealRow} ${styles.revealRowTop}`}>
                    <div className={styles.rl}>
                      window_minutes
                      <WhenBadge field="window_minutes" />
                    </div>
                    <div className={styles.revealCol}>
                      {(() => {
                        const draftMinutes = parseWindowMinutes(state);
                        // The client's own complaint first: it is about what
                        // the box holds NOW, while the server's is about the
                        // length it was last handed. Both print as one line
                        // under the row, and both put the red border on the
                        // NUMBER alone — a unit is never wrong by itself.
                        const lengthError =
                          errors.window_minutes ?? liveWindowLengthError;
                        return (
                          <>
                            <div className={styles.durRow}>
                              <input
                                id={windowValueId}
                                aria-label="Window length"
                                type="number"
                                /* 1, not 0: a 0 in either box used to be a
                                   complete half of «3 days 0 hours», and now
                                   both this form and the server refuse it —
                                   so the spinner stops at the first length
                                   that can actually be saved. */
                                min={1}
                                className={`${styles.input} ${styles.number} ${
                                  styles.mono
                                } ${lengthError ? styles.invalid : ""}`}
                                value={state.window_value}
                                onChange={(e) =>
                                  update("window_value", e.target.value)
                                }
                              />
                              <Select
                                className={styles.durUnitSelect}
                                value={state.window_unit}
                                onChange={(v) =>
                                  update("window_unit", v as WindowUnit)
                                }
                                options={WINDOW_UNIT_OPTIONS}
                                aria-label="Window length unit"
                              />
                              {/* What the row actually sends, printed only
                                 where the two differ: on `minutes` the number
                                 in the box IS the number on the wire, and
                                 repeating it would read as a second value. */}
                              {state.window_unit !== "minutes" &&
                                draftMinutes !== null && (
                                  <span className={styles.durUnit}>
                                    = {draftMinutes} min
                                  </span>
                                )}
                            </div>
                            {lengthError ? (
                              <div className={styles.cronError}>{lengthError}</div>
                            ) : (
                              <span className={styles.helper}>
                                How long a window stays open once it starts. It
                                must be shorter than the gap between two starts
                                — what is left over is the pause.
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* In the pause there is nothing to choose between: no
                     window is running, so neither option has anything to keep
                     as announced or to cut short, and the server moves the
                     window already scheduled either way. A row asking the
                     question anyway would print two sentences about a running
                     window that does not exist. */}
                  {scheduleDirty && windowPaused && (
                    <div className={`${styles.revealRow} ${styles.revealRowTop}`}>
                      <div className={styles.rl}>apply</div>
                      <div className={styles.revealCol}>
                        <span className={styles.helper}>
                          The board is between windows, so there is nothing
                          running to re-cut. The next window has already been
                          scheduled and this save moves it onto the new
                          schedule.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Raised by either half of the schedule — this is the whole
                     difference from the row above, where only the cron can
                     move. A length nobody changed reaches the PATCH body as
                     little as an untouched cron does. */}
                  {scheduleDirty && !windowPaused && (
                    <div className={`${styles.revealRow} ${styles.revealRowTop}`}>
                      <div className={styles.rl}>apply</div>
                      <div className={styles.revealCol}>
                        <SegRadio
                          name="cron_apply"
                          groupLabel="Apply the new schedule"
                          options={[
                            { value: "next_cycle", label: "from the next window" },
                            {
                              value: "current_cycle",
                              label: "to the one running now",
                            },
                          ]}
                          value={state.cron_apply}
                          onChange={(v) => update("cron_apply", v)}
                        />
                        {state.cron_apply === "next_cycle" ? (
                          <span className={styles.helper}>
                            {announcedClose ? (
                              <>
                                The window open right now still closes{" "}
                                <b>{announcedClose}</b> as announced.
                              </>
                            ) : (
                              <>
                                The window open right now still closes as
                                announced.
                              </>
                            )}{" "}
                            The new schedule cuts every window after it.
                          </span>
                        ) : (
                          <Alert
                            variant="warn"
                            title="The running window will close earlier than announced"
                          >
                            {/* Both dates are facts here: the one it moves from
                               is what the cycle carries, and the one it moves
                               to is that cycle's own start plus the new length.
                               The start never moves — which is what lets this
                               banner name a date where `recurring`'s can only
                               describe one. */}
                            Its end moves{" "}
                            {announcedClose ? (
                              <>
                                from <b>{announcedClose}</b>{" "}
                              </>
                            ) : null}
                            {rescheduledCloseText ? (
                              <>
                                to <b>{rescheduledCloseText}</b> — its start plus
                                the new length
                              </>
                            ) : (
                              <>to its start plus the new length</>
                            )}
                            {rescheduledClosePassed ? (
                              <>
                                , a moment that has already gone by — so the
                                window closes at the worker's next tick and the
                                rewards are handed out then.
                              </>
                            ) : (
                              <> — and the rewards are handed out then.</>
                            )}{" "}
                            Players who counted on the announced date lose the
                            time in between.
                          </Alert>
                        )}
                      </div>
                    </div>
                  )}

                  {errors.cron && !errors.cron.startsWith("Invalid cron") && (
                    <div className={styles.fieldError}>{errors.cron}</div>
                  )}
                </div>

                {/* What makes this board a different animal from `recurring`,
                   said once where the choice is MADE: between two windows it is
                   SHUT, and a game that does not expect that reads the closure
                   as an outage. Create only — a saved board's Settings tab is
                   read by somebody who has already chosen this window kind, and
                   the mockup draws that card without it (§4, §5).

                   The first line is about THIS board where the schedule allows
                   it to be — see windowPauseSentence. */}
                {mode === "create" && (
                <div className={styles.alertSlot}>
                  <Alert variant="info" title="Windows do not run back to back">
                    <p>{windowPauseSentence(state.cron, parseWindowMinutes(state))}{" "}
                      Rewards are handed out at every close, and the next window
                      starts everyone from nothing.
                    </p>
                    <p>
                      While it is shut a submit is refused with{" "}
                      <code>409 leaderboard_cycle_closed</code>, and{" "}
                      <code>GET /top</code> answers with the window that just
                      finished plus the <code>next_cycle</code> already scheduled
                      — so the game can show the results and the countdown to the
                      next start.
                    </p>
                  </Alert>
                </div>
                )}
              </div>
            )}

            {!autoScored && state.window_kind === "event" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>event_start_at</div>
                    {/* The start moves only while the event is still a plan.
                       Once its cycle is open the date has happened, and no
                       edit here could make it un-happen. */}
                    {eventStartLocked ? (
                      <div className={styles.revealCol}>
                        <LockedValue value={displayDateTime(state.event_start_at)}>
                          {eventEndLocked
                            ? undefined
                            : "The event has already started — its start is history now."}
                        </LockedValue>
                      </div>
                    ) : (
                      <input
                        id={eventStartId}
                        aria-label="Event start"
                        type="datetime-local"
                        className={`${styles.input} ${styles.mono} ${
                          errors.event_start_at ? styles.invalid : ""
                        }`}
                        value={state.event_start_at}
                        onChange={(e) => update("event_start_at", e.target.value)}
                      />
                    )}
                  </div>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>event_end_at</div>
                    {eventEndLocked ? (
                      <div className={styles.revealCol}>
                        <LockedValue value={displayDateTime(state.event_end_at)} />
                      </div>
                    ) : (
                      <div className={styles.revealCol}>
                        <input
                          id={eventEndId}
                          aria-label="Event end"
                          type="datetime-local"
                          className={`${styles.input} ${styles.mono} ${
                            errors.event_end_at ? styles.invalid : ""
                          }`}
                          value={state.event_end_at}
                          onChange={(e) => update("event_end_at", e.target.value)}
                        />
                        {/* Only worth saying while the event is running: on a
                           scheduled one the alert below already says both
                           dates carry the cycle with them. */}
                        {eventStartLocked && (
                          <span className={styles.helper}>
                            The end moves forward to extend the event and back
                            to wind it up early, but never into the past — the
                            cycle takes the same close_at with it.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {(errors.event_start_at || errors.event_end_at) && (
                    <div className={styles.fieldError}>
                      {errors.event_end_at || errors.event_start_at}
                    </div>
                  )}
                </div>

                {/* What the state of the cycle means for the pair, said once
                   under both rows rather than twice beside them. Nothing at
                   all while the status is unknown — an alert about a cycle
                   nobody has read yet would be a claim, not information. */}
                {mode === "edit" && eventCycleStatus === "scheduled" && (
                  <div className={styles.alertSlot}>
                    <Alert variant="info" title="Both dates move the cycle with them">
                      The event hasn't started, so its cycle is still{" "}
                      <code>scheduled</code> — it opens at the new start and
                      closes at the new end.
                    </Alert>
                  </div>
                )}
                {eventEndLocked && (
                  <div className={styles.alertSlot}>
                    <Alert variant="info" title="The event is over">
                      Rewards were handed out by this window — moving it now
                      would rewrite a result players already saw.
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </FieldRow>

          {/* Realtime — mockup §583–596 (blocked), §1442–1450 (rooms). Gone on
             an auto-scored board: only the submit path publishes rank.changed,
             and that board refuses submits. Without a room or a guild to send
             it to, the row stays dimmed rather than vanishing, so its hint can
             name what unlocks it. */}
          {!autoScored && (
            <FieldRow
              label="Realtime"
              apiName="realtime"
              mutedLabel={realtimeBlocked}
              hint={
                realtimeBlocked ? (
                  <>
                    Available only on boards with rooms or scope{" "}
                    <code>guild_internal</code>.
                  </>
                ) : (
                  <>
                    Pushes <code>rank.changed</code> WS events to players in the
                    same room (guild).
                  </>
                )
              }
            >
              <div
                className={`${styles.switchRow} ${
                  realtimeBlocked ? styles.rowMuted : ""
                }`}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={realtimeOn}
                  aria-label="Realtime"
                  disabled={realtimeBlocked}
                  aria-disabled={realtimeBlocked || undefined}
                  className={`${styles.switch} ${
                    realtimeOn ? styles.switchOn : ""
                  }`}
                  onClick={() => update("realtime", !state.realtime)}
                >
                  <span className={styles.switchKnob} aria-hidden />
                </button>
                <span className={styles.switchLabel}>
                  {realtimeOn
                    ? "On — broadcasts rank.changed"
                    : "Off — no realtime events"}
                </span>
              </div>
            </FieldRow>
          )}

          {/* Who may submit, on a board nothing submits to. Gone with the
             operator above, and named in the same Alert. */}
          {!autoScored && (
            <FieldRow
              label="Submit policy"
              apiName="submit_policy"
              hint="Who can submit scores."
            >
              <SegRadio
                name="submit_policy"
                options={[
                  { value: "client", label: "client" },
                  { value: "s2s_only", label: "s2s_only" },
                ]}
                value={state.submit_policy}
                onChange={(v) => update("submit_policy", v)}
              />
            </FieldRow>
          )}
        </div>
      </section>

      {/* ============ SECTION 3: Scope ============ */}
      <section className={styles.section} hidden={sectionHidden("Scope")}>
        <div className={styles.cardTitle}>
          <h3>Scope</h3>
          {/* The step numbering belongs to filling a form in; on a saved board
             the section is one pill and one live control, and the caption says
             which is which instead of where in a sequence this is (mockup
             §406). Segmentation's caption already reads this way. */}
          <span className={styles.cardSub}>
            {cutFrozen
              ? "who ranks against whom · fixed after create"
              : mode === "edit"
                ? "who ranks against whom · moves at the next cut"
                : "step 3 of 5"}
          </span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Scope"
            apiName="scope"
            hint={
              cutFrozen
                ? "Rows already written belong to the entity this names — a player or a guild. Fixed after creation."
                : mode === "edit"
                  ? "The entity a row belongs to. Rows already written are players; a cycle cut as guild_aggregated ranks guilds — two answers that can only live in two different cycles."
                  : "Who ranks against whom."
            }
            full
            /* The row wraps the guild-scoring reveal; the note is placed by
               hand under the chooser — as on window_kind. */
            pendingField={false}
            /* Not repeated here: the aggregator's own message renders once,
               inline next to the SegRadio it is about — see the
               errors.guild_aggregator block below, past the guild_stat_key
               reveal. A copy up here in the Scope label column said the same
               thing twice and, worse, could still say it after a leave from
               guild_aggregated took the control it was about off the page —
               see updateMany. */
          >
            {/* Every score row on the board is owned by the entity this names,
               so changing it would re-file rows that are already written. The
               aggregator below is the part of the section that still moves. */}
            {cutFrozen ? (
              <LockedValue value={state.scope}>
                {SCOPE_LOCKED_HELPER[state.scope]}
              </LockedValue>
            ) : (
              <RadioCardGroup
                name="scope"
                options={SCOPE_OPTIONS}
                value={state.scope}
                cols={3}
                onChange={(v) => update("scope", v)}
              />
            )}
            <PendingNote field="scope" />

            {state.scope === "guild_aggregated" && (
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  {/* FIRST, because it decides what the rows under it are:
                     the key exists only for one of the two answers, and the
                     Scoring section loses three fields to the same choice. */}
                  {/* Top-aligned whenever the row's own control is taller
                     than its label — the cards in create mode, and the
                     member_contribution block in either mode. Centred, the
                     label floats in the middle of a block it names the top
                     of. */}
                  <div
                    className={`${styles.revealRow} ${
                      !cutFrozen || contribution ? styles.revealRowTop : ""
                    }`}
                  >
                    <div className={styles.rl}>
                      guild_score_source
                      <WhenBadge field="guild_score_source" />
                    </div>
                    {/* One column for the answer and everything that hangs off
                       it. member_contribution's four rows live UNDER the cards
                       rather than beside the other reveal rows, per the mockup
                       — they describe one source, not the block. */}
                    <div className={styles.revealCol}>
                    {cutFrozen ? (
                      /* Every row on the board was written by this answer, and
                         on a board that cuts no further cycle there is no
                         moment another answer could start applying from — so
                         it is a pill, like `scope` right above it. The key and
                         the aggregator below stay live: the score is a
                         snapshot, so moving them just recomputes it, and
                         freezing them would make a key renamed in stats_schema
                         kill the board for good. */
                      <div>
                        {/* Prose, so no second padlock: `lock` is the shape
                           for a line that says nothing but "this is locked",
                           and this one says what the board does and where the
                           other kind is made. The `scope` pill right above it
                           reads the same way, in the mockup (§5) and here. */}
                        <LockedValue value={state.guild_score_source}>
                          {
                            GUILD_SCORE_SOURCE_LOCKED_HELPER[
                              state.guild_score_source
                            ]
                          }
                        </LockedValue>
                      </div>
                    ) : (
                      <RadioCardGroup
                        name="guild_score_source"
                        options={GUILD_SCORE_SOURCE_OPTIONS}
                        value={state.guild_score_source}
                        cols={3}
                        /* Through updateMany, not a bare setState: the card
                           moves the aggregator too, and the aggregator carries
                           a message — "must pair with operator" — that this
                           very click can make unanswerable, by taking the
                           `operator` cards off the page. The window it used to
                           set by hand is now confineAutoScoredWindow's job, so
                           the scope cards get the same treatment. */
                        onChange={(v) =>
                          updateMany({
                            guild_score_source: v,
                            // The seg control below shows `sum` as the active
                            // aggregator from the moment this block opens, so
                            // the state has to say the same thing — clicking
                            // the option that is already checked fires no
                            // change, and the board would otherwise refuse to
                            // save an aggregator the operator can see selected.
                            // Same fix, and the same reasoning, as tier_mode's
                            // in initialState. Only on the auto board, where
                            // the aggregator is the whole pair: on a submit
                            // board `operator` is the operator's own choice and
                            // seeding one half would pick the other for them.
                            guild_aggregator:
                              v === "player_stat"
                                ? state.guild_aggregator || "sum"
                                : state.guild_aggregator,
                          })
                        }
                      />
                    )}
                    <PendingNote field="guild_score_source" />

                    {/* The team-battle source's own four rows. A nested reveal
                       with its own annotation, exactly as the mockup draws it:
                       two of these are fields, and two are statements about
                       what the source has already decided — which is why they
                       sit together under the card rather than joining the
                       block's own rows.

                       Shown in edit mode too. The SOURCE is a pill there, but
                       these two columns are patchable (UpdateParams carries
                       both), so the block is the same block with a locked
                       heading above it. */}
                    {contribution && (
                      <div className={`${styles.reveal} ${styles.revealNested}`}>
                        <div className={styles.revealAnno}>
                          member_contribution
                        </div>
                        <div className={styles.revealInner}>
                          <div className={styles.revealRow}>
                            <div className={`${styles.rl} ${styles.rlText}`}>Requires</div>
                            <div className={styles.revealCol}>
                              <div className={styles.rowControls}>
                                <span
                                  className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}
                                >
                                  guild_aggregator = sum
                                </span>
                                <span
                                  className={`${styles.badge} ${styles.badgeNeutral} ${styles.badgeMono}`}
                                >
                                  operator = increment
                                </span>
                              </div>
                              <div className={styles.helper}>
                                The pair is set automatically and cannot be
                                edited on this source.
                              </div>
                            </div>
                          </div>

                          <div className={styles.revealRow}>
                            <div className={styles.rl}>
                              <code>min_guild_members</code>
                            </div>
                            <div className={styles.revealCol}>
                              <div className={styles.sizeRow}>
                                <input
                                  aria-label="Min guild members"
                                  type="number"
                                  min={0}
                                  className={`${styles.input} ${styles.number} ${
                                    errors.min_guild_members
                                      ? styles.invalid
                                      : ""
                                  }`}
                                  value={state.min_guild_members}
                                  onChange={(e) =>
                                    update("min_guild_members", e.target.value)
                                  }
                                />
                                <span className={styles.sizeUnit}>
                                  members to enter the battle
                                </span>
                              </div>
                              <div className={styles.helper}>
                                Checked only while the team has not entered yet:
                                a submit from a team below the threshold answers{" "}
                                <code>
                                  applied=false, reason=&quot;team_too_small&quot;
                                </code>
                                , the point is not counted and no room seat is
                                taken. Once the team has entered, dropping below
                                the threshold changes nothing. <code>0</code> —
                                no threshold.
                              </div>
                              {errors.min_guild_members && (
                                <div className={styles.fieldError}>
                                  {errors.min_guild_members}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className={styles.revealRow}>
                            <div className={styles.rl}>
                              <code>reward_contributors_only</code>
                            </div>
                            <div className={styles.revealCol}>
                              <div className={styles.switchRow}>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={state.reward_contributors_only}
                                  aria-label="Reward contributors only"
                                  className={`${styles.switch} ${
                                    state.reward_contributors_only
                                      ? styles.switchOn
                                      : ""
                                  }`}
                                  onClick={() =>
                                    update(
                                      "reward_contributors_only",
                                      !state.reward_contributors_only,
                                    )
                                  }
                                >
                                  <span
                                    className={styles.switchKnob}
                                    aria-hidden
                                  />
                                </button>
                                <span className={styles.switchLabel}>
                                  {state.reward_contributors_only
                                    ? "On — only contributors get the team reward"
                                    : "Off — every current member gets the team reward"}
                                </span>
                              </div>
                              <div className={styles.helper}>
                                Off restores the old behaviour: every current
                                member of the team takes the placement grant,
                                zero contribution included. It does not touch
                                the contributor rewards — a zero contribution is
                                excluded there by their very nature.
                              </div>
                            </div>
                          </div>

                          <div className={styles.revealRow}>
                            <div className={styles.rl}>
                              <code>late_joiners_may_score</code>
                            </div>
                            <div className={styles.revealCol}>
                              <div className={styles.switchRow}>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={state.late_joiners_may_score}
                                  aria-label="Late joiners may score"
                                  className={`${styles.switch} ${
                                    state.late_joiners_may_score
                                      ? styles.switchOn
                                      : ""
                                  }`}
                                  onClick={() =>
                                    update(
                                      "late_joiners_may_score",
                                      !state.late_joiners_may_score,
                                    )
                                  }
                                >
                                  <span
                                    className={styles.switchKnob}
                                    aria-hidden
                                  />
                                </button>
                                <span className={styles.switchLabel}>
                                  {state.late_joiners_may_score
                                    ? "On — anyone in the guild right now may score"
                                    : "Off — only members who were in the guild when the team entered"}
                                </span>
                              </div>
                              <div className={styles.helper}>
                                Off is the strict rule: a member who joined
                                after the team entered gets{" "}
                                <code>
                                  applied=false,
                                  reason=&quot;not_participating&quot;
                                </code>{" "}
                                for every submit. The switch works FORWARD and
                                grants nothing retroactively — a refused submit
                                is stored nowhere, so turning it on mid-event
                                lets the latecomers start scoring from zero at
                                that moment. Either move can still shift a
                                team&apos;s total, where the points are already
                                in the table: off takes the latecomers&apos;
                                banked contributions out of it, and on brings
                                back those of a member who left and re-joined.
                              </div>
                            </div>
                          </div>

                          <div className={styles.revealRow}>
                            <div className={`${styles.rl} ${styles.rlText}`}>Window</div>
                            <div className={styles.revealCol}>
                              <div className={styles.rowControls}>
                                <span
                                  className={`${styles.badge} ${styles.badgeOk} ${styles.badgeMono}`}
                                >
                                  recurring
                                </span>
                                <span
                                  className={`${styles.badge} ${styles.badgeOk} ${styles.badgeMono}`}
                                >
                                  event
                                </span>
                                <span
                                  className={`${styles.badge} ${styles.badgeOk} ${styles.badgeMono}`}
                                >
                                  recurring_event
                                </span>
                                <span
                                  className={`${styles.badge} ${styles.badgeDanger} ${styles.badgeMono}`}
                                >
                                  all_time
                                </span>
                              </div>
                              <div className={styles.helper}>
                                In an endless cycle &quot;was in the team when it
                                entered&quot; degenerates, so <code>all_time</code>{" "}
                                is refused with this source at create.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Only on the answer that has one. Not disabled-when-absent:
                     the field means nothing at all on a submit board, and the
                     form hides what cannot be used rather than greying it. */}
                  {autoScored && (
                    <div className={styles.revealRow}>
                      <div className={styles.rl}>
                        guild_stat_key
                        <WhenBadge field="guild_stat_key" />
                      </div>
                      <div className={styles.revealCol}>
                        <Select
                          className={styles.statKeySelect}
                          value={state.guild_stat_key}
                          onChange={(v) => update("guild_stat_key", v)}
                          options={statKeyOptions}
                          monospaceValue={state.guild_stat_key !== ""}
                          /* Empty is not an option here, it is a field nobody
                             filled in — so it shows Select's own placeholder
                             rather than a list entry the form would refuse. */
                          placeholder={
                            noIntStat ? "No int stat declared" : "Select a stat…"
                          }
                          /* The only disabled control in this form, and a
                             deliberate exception to its own rule (see the
                             country-split comment below): the field is
                             REQUIRED, so it cannot be hidden — its absence
                             would read as a broken page — and an empty list
                             the operator clicks into explains nothing. */
                          disabled={noIntStat}
                          aria-label="Guild stat key"
                        />
                        {/* What the field is FOR. Said next to the control
                           rather than in a label column the reveal block does
                           not have, and said unconditionally: until a key is
                           picked the previewBox below has nothing to describe,
                           so without this line the one state where the operator
                           most needs a sentence is the state with none. */}
                        <div className={styles.helper}>
                          Which <code>players.stats</code> field the guild’s
                          score is aggregated from.
                        </div>
                        {errors.guild_stat_key && (
                          <div className={styles.fieldError}>
                            {errors.guild_stat_key}
                          </div>
                        )}
                        {noIntStat && (
                          <div
                            className={`${styles.previewBox} ${styles.previewWarn}`}
                          >
                            This project declares no <code>int</code> field in{" "}
                            <code>profile.stats_schema</code>, so there is
                            nothing to score guilds by. Declare one under{" "}
                            <b>Configuration → Profile</b>, then come back.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Gone on member_contribution, where the source fixes it:
                     the row above already states `guild_aggregator = sum` as a
                     badge, and a chooser under a badge saying the value is not
                     choosable is the form arguing with itself. Removed rather
                     than greyed, the way the whole submit half is removed on an
                     auto-scored board. */}
                  {!contribution && (
                    <div className={styles.revealRow}>
                      {/* api-style label per mockup; getByLabelText targets the
                         SegRadio wrapper via groupLabel→aria-label. */}
                      <div className={styles.rl}>
                        guild_aggregator
                        <WhenBadge field="guild_aggregator" />
                      </div>
                      <SegRadio
                        name="guild_aggregator"
                        groupLabel="Guild aggregator"
                        options={[
                          { value: "sum", label: "sum" },
                          { value: "max", label: "max" },
                        ]}
                        value={
                          (state.guild_aggregator || "sum") as GuildAggregator
                        }
                        onChange={(v) => update("guild_aggregator", v)}
                      />
                    </div>
                  )}
                  {errors.guild_aggregator && (
                    <div className={styles.fieldError}>
                      {errors.guild_aggregator}
                    </div>
                  )}
                </div>

                {/* Outside .revealInner: this is the sum of the block, not
                   another field in it — and the only place on the page that
                   says what the board actually does.

                   ONE box in every state, per mockup §6. The coverage count is
                   not a second remark about the board, it is a clause of the
                   same sentence — «the sum of players.stats.level … 21,904 of
                   24,110 players carry it» — and when NOBODY carries the key
                   it does not join the prose, it replaces it: a board that
                   would rank on nothing has no behaviour left worth
                   describing, and a warning under a paragraph explaining how
                   well the thing works reads as a footnote to good news. */}
                {autoScored &&
                  statKeyBeingChosen &&
                  state.guild_stat_key !== "" &&
                  (shownCoverage && shownCoverage.carrying === 0 ? (
                    <div
                      className={`${styles.previewBox} ${styles.previewSummary} ${styles.previewWarn}`}
                    >
                      <b>
                        0 of {formatPlayers(shownCoverage.total)} players carry{" "}
                        <code>{shownCoverage.key}</code>.
                      </b>{" "}
                      Every guild would score 0 and the board would rank on
                      nothing. Check that the game writes this key through{" "}
                      <code>PATCH /v1/me/stats</code>.
                    </div>
                  ) : (
                    <div
                      className={`${styles.previewBox} ${styles.previewSummary}`}
                    >
                      {/* The two halves are deliberately NOT symmetrical:
                         `sum` names the whole path it adds up, `max` names the
                         bare key. That is how the mockup writes them, and it is
                         the contract — "is the highest `level` among its
                         current members" (§2). */}
                      A guild’s score is{" "}
                      {state.guild_aggregator === "max" ? (
                        <>
                          the highest <code>{state.guild_stat_key}</code> among
                        </>
                      ) : (
                        <>
                          the sum of{" "}
                          <code>players.stats.{state.guild_stat_key}</code>{" "}
                          across
                        </>
                      )}{" "}
                      its current members.{" "}
                      {/* Said only once the count is in — an unanswered
                         request leaves the sentence unsaid rather than said
                         with a number nobody counted. */}
                      {shownCoverage && (
                        <>
                          <b>
                            {formatPlayers(shownCoverage.carrying)} of{" "}
                            {formatPlayers(shownCoverage.total)} players carry
                            it.
                          </b>{" "}
                          Members without the key count as 0.{" "}
                        </>
                      )}
                      It is recomputed the moment a member’s stat changes and
                      the moment anybody joins, leaves or is kicked, so leaving
                      a guild takes that member’s {state.guild_stat_key} out of
                      its total. Every guild of the project sits on this board,
                      including those still scoring 0, and a guild that is
                      disbanded comes off it. Nothing is submitted here:{" "}
                      <code>POST /submit</code> answers{" "}
                      <code>403 leaderboard_auto_scored</code>.
                    </div>
                  ))}

                {/* Under the same gate as the sort warning next door: naming
                   either field in the PATCH rescores every guild of the
                   project, so the sentence belongs to a value somebody MOVED,
                   not to a form that happens to be holding one. */}
                {(statKeyDirty || aggregatorDirty) && (
                  <div className={styles.alertSlot}>
                    <Alert
                      variant="warn"
                      title="Saving re-draws the standings right now"
                    >
                      {/* Both halves name the same dimension, or the sentence
                         stops parsing. A moved KEY is the mockup's own
                         wording — new key against old key. A moved AGGREGATOR
                         has to say so on both sides: "rescored from
                         players.stats.level, replacing the sum standings"
                         answers a question about the key with a fact about the
                         fold, and reads like a word went missing. */}
                      Every guild of the project is rescored{" "}
                      {statKeyDirty ? (
                        <>
                          from{" "}
                          <code>players.stats.{state.guild_stat_key}</code>,
                          replacing the <code>{savedStatKey}</code>
                        </>
                      ) : (
                        <>
                          as the <code>{state.guild_aggregator}</code> of{" "}
                          <code>players.stats.{state.guild_stat_key}</code>,
                          replacing the <code>{savedAggregator}</code>
                        </>
                      )}{" "}
                      standings players see today. Like flipping{" "}
                      <code>sort</code>, this is immediate and visible to
                      everyone.
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </FieldRow>

          <FieldRow
            label="Visibility"
            apiName="visibility"
            hint="Who sees scores on the read side."
          >
            <SegRadio
              name="visibility"
              options={[
                { value: "public", label: "public" },
                { value: "guild_only", label: "guild_only" },
                { value: "hidden", label: "hidden" },
              ]}
              value={state.visibility}
              onChange={(v) => update("visibility", v)}
            />
          </FieldRow>

          {/* In this section because the country is who the player is ranked
             against in the second view, and not in Segmentation — which the
             toggle also depends on — because that section is immutable after
             create and this switch is the opposite: it moves on a live board,
             backfilling the open cycle when it goes on. Hidden rather than
             disabled where the pair is refused: a greyed switch would invite
             the click it is going to turn into a 400. */}
          {countrySplitAllowed({
            scope: state.scope,
            segmentation: state.segmentation,
          }) && (
            <FieldRow
              label="Country split"
              apiName="country_split"
              hint="Gives the board a second tab, ranked among the entrants from the reader's own country — players here, guilds on an aggregated board. One cycle either way: rewards and the participants count stay worldwide."
            >
              <div className={styles.switchRow}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={state.country_split}
                  aria-label="Country split"
                  className={`${styles.switch} ${
                    state.country_split ? styles.switchOn : ""
                  }`}
                  onClick={() => update("country_split", !state.country_split)}
                >
                  <span className={styles.switchKnob} aria-hidden />
                </button>
                <span className={styles.switchLabel}>
                  {state.country_split
                    ? "On — world and the reader's own country"
                    : "Off — one worldwide list"}
                </span>
              </div>
            </FieldRow>
          )}
        </div>
      </section>

      {/* ============ SECTION 4: Segmentation ============ */}
      <section className={styles.section} hidden={sectionHidden("Segmentation")}>
        <div className={styles.cardTitle}>
          <h3>Segmentation</h3>
          {/* On a tier board the rooms DO move after create, so the caption
             stops promising otherwise. Everything else in the section is
             still a pill. */}
          <span className={styles.cardSub}>
            {mode !== "edit"
              ? "step 4 of 5"
              : cutEditable
                ? "how the field is divided · moves at the next cut"
                : state.segmentation === "tier"
                  ? "only rooms can be changed"
                  : state.segmentation === "personal"
                    ? "mode is fixed after creation"
                    : canSwitchToPersonal
                      ? "live board · one-way switch available"
                      : "immutable after create"}
          </span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Segmentation"
            apiName="segmentation"
            hint={
              mode !== "edit"
                ? guildRows
                  ? // What the section means on a board of teams, and the one
                    // card of the three that a team cannot take.
                    (
                      <>
                        <code>scope = guild_aggregated</code> used to require{" "}
                        <code>none</code>. The restriction is lifted — teams take
                        room seats exactly the way players do.
                      </>
                    )
                  : "How participants are split into comparable groups."
                : cutEditable
                  ? "How the field is divided. The open cycle keeps the division it was cut with — that is what lets this move at all."
                  : state.segmentation === "tier"
                    ? "The mode and the tier ladder are fixed after creation. Rooms can be turned on and resized."
                    : "Can't be changed after creation."
            }
            full
            error={errors.bucket_size}
            /* The row wraps the tier editor and every mode's reveal, so the
               note is placed by hand under the chooser. */
            pendingField={false}
          >
            {/* PATCH silently drops `segmentation` (docs/api.md:2763), so in
               edit mode it's shown read-only instead of an editable control
               whose changes would vanish on save. */}
            {cutFrozen ? (
              <LockedValue value={state.segmentation}>
                {SEGMENTATION_LOCKED_HELPER[state.segmentation]}
              </LockedValue>
            ) : (
              <RadioCardGroup
                name="segmentation"
                /* The cards a board of THIS scope and THIS source may take —
                   see `segmentationOptions`, which also writes the reason on
                   each shut card. `confineGuildSegmentation` covers the other
                   way in, where the scope or the source moves after the card
                   was picked. */
                options={segmentationOptions(
                  state.scope,
                  state.guild_score_source,
                )}
                value={state.segmentation}
                cols={4}
                onChange={(v) => {
                  setState((s) => ({
                    ...s,
                    segmentation: v,
                    // Rooms are a tier-board setting; leaving tier puts the
                    // switch back to its default rather than carrying a
                    // hidden `true` into a segmentation that ignores it.
                    rooms: v === "tier" ? s.rooms : false,
                    tier_mode:
                      v === "tier" && !s.tier_mode
                        ? defaultTierMode(s.operator)
                        : s.tier_mode,
                    // A threshold reward without a tier board has nothing to
                    // open — see resetThresholdDrafts.
                    rewards: v === "tier" ? s.rewards : resetThresholdDrafts(s.rewards),
                  }));
                  // The errors of the branch being left no longer apply — and
                  // bucket_size's would keep showing in the shared slot.
                  if (submitted) {
                    setErrors((prev) => ({
                      ...prev,
                      bucket_size: undefined,
                      tier_mode: undefined,
                      tier_config: undefined,
                      tierRows: undefined,
                      promote_top_k: undefined,
                      demote_bottom_k: undefined,
                    }));
                  }
                }}
              />
            )}
            <PendingNote field="segmentation" />

            {/* The one scheduled change that touches PLAYERS rather than
               configuration. A league outlives the cycle it was won in — the
               snapshot does not carry it — so a package that takes the board
               off its ladder, or off a rung somebody is standing on, has to
               say out loud what becomes of those rows (mockup §10). */}
            {ladderQuestion && (
              <div className={styles.ladderRow}>
                <div className={styles.name}>Player ladder</div>
                <div className={styles.apiName}>leaderboard_player_tiers</div>
                <div className={styles.hint}>
                  The one thing on this board that outlives a cycle.
                </div>
                <div className={pendingStyles.ladderAlert}>
                  <Alert
                    variant="warn"
                    title="Leaving the ladder needs an answer about the leagues already earned"
                  >
                    {ladderImpact ? (
                      <>
                        <b>{formatCount(ladderImpact.players)} players</b> hold
                        a league on this board
                        {orphanedTiers.length > 0 && (
                          <>
                            ; <b>{formatCount(orphanedPlayers)}</b> of them stand
                            on{" "}
                            {orphanedTiers.map((t, i) => (
                              <Fragment key={t.tier_key}>
                                {i > 0 && ", "}
                                <code>{t.tier_key}</code>
                              </Fragment>
                            ))}
                            , which the ladder you are moving to does not have
                          </>
                        )}
                        .{" "}
                      </>
                    ) : null}
                    A league is not configuration — the cycle snapshot does not
                    carry it, so the package has to say out loud what becomes of
                    it.
                  </Alert>
                </div>
                <RadioCardGroup
                  name="ladder_decision"
                  options={[
                    {
                      value: "reset" as LadderDecision,
                      glyph: "\u27f2",
                      title: "Reset the ladder",
                      desc: ladderImpact
                        ? `The ${formatCount(ladderImpact.players)} rows go with the package. Coming back to a ladder later starts everyone on the bottom rung.`
                        : "The rows go with the package. Coming back to a ladder later starts everyone on the bottom rung.",
                    },
                    {
                      value: "keep" as LadderDecision,
                      glyph: "\u2630",
                      title: "Keep the ladder",
                      desc: "The rows stay, unread, until this board is put back on a ladder — and then resume exactly where they stopped.",
                    },
                  ]}
                  value={ladderDecision}
                  cols={2}
                  onChange={setLadderDecision}
                />
                <div className={styles.helper}>
                  Whichever you pick travels in the package, shows up in its
                  diff, and is written to the audit entry — this is the only
                  place where a scheduled change touches players rather than
                  configuration.
                </div>
              </div>
            )}

            {state.segmentation === "bucket" && (
              <div className={styles.reveal}>
                <div className={styles.revealAnno}>bucket · room size</div>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>bucket_size</div>
                    {/* Read-only after create, and shown as a pill for the
                       same reason tier_mode is: on a bucket board the size IS
                       the segmentation — the PATCH answers 400 for it — so an
                       editable box would only promise a change that never
                       lands. The tier board's rooms are the editable case,
                       one branch down. */}
                    <div className={styles.revealCol}>
                      {mode === "edit" ? (
                        <LockedValue value={state.bucket_size}>
                          On a <code>bucket</code> board the size <i>is</i> the
                          segmentation, so it is fixed with it.
                        </LockedValue>
                      ) : (
                        <div className={styles.sizeRow}>
                          <input
                            id={bucketSizeId}
                            aria-label="Bucket size"
                            type="number"
                            min={2}
                            className={`${styles.input} ${styles.number} ${
                              errors.bucket_size ? styles.invalid : ""
                            }`}
                            value={state.bucket_size}
                            onChange={(e) =>
                              update("bucket_size", e.target.value)
                            }
                          />
                          {/* What the number counts, which is what the scope
                             decides: rows on a guild board are teams. */}
                          <span className={styles.sizeUnit}>
                            {guildRows ? "teams per room" : "players per room"}
                          </span>
                        </div>
                      )}
                      {/* The t-149 mockup's own two lines. They are one block
                         and they come and go together — and only where that
                         mockup draws them, on a board of teams. A player
                         bucket board reads exactly as it did before this
                         feature existed. Mode-independent for the same reason:
                         both sentences are about the board, not about whether
                         its size can still be typed. */}
                      {guildRows && (
                        <div className={styles.helper}>
                          Placement rewards apply inside each room separately —
                          one table of places for every room, and all rooms are
                          equal.
                        </div>
                      )}
                    </div>
                  </div>
                  {/* The other half of that block: the one pairing a room board
                     closes off. Said here rather than left for the Scope
                     section to explain by omission — the country toggle simply
                     vanishes there, and this is where the choice that took it
                     away was made. */}
                  {guildRows && (
                    <div className={styles.revealRow}>
                      <div className={`${styles.rl} ${styles.rlText}`}>
                        Not compatible
                      </div>
                      <div className={styles.revealCol}>
                        <div className={styles.rowControls}>
                          <span
                            className={`${styles.badge} ${styles.badgeDanger} ${styles.badgeMono}`}
                          >
                            country_split
                          </span>
                        </div>
                        <div className={styles.helper}>
                          The country split requires{" "}
                          <code>segmentation = none</code> — that restriction
                          stands as it is.
                        </div>
                      </div>
                    </div>
                  )}
                  {/* The one-way switch. It is a form control, not a request:
                     clicking it moves the section into its personal shape and
                     opens the Opponent matchmaking card below, and the sticky
                     Save bar is what actually sends
                     {segmentation, roster_size, matchmaking_config} as one
                     PATCH. Anything else would save a mode with no rules under
                     it and leave the operator configuring a board that is
                     already assembling lists. */}
                  {canSwitchToPersonal && (
                    <div className={styles.revealRow}>
                      <div className={styles.rl}>switch to</div>
                      <div className={styles.revealCol}>
                        <div className={styles.sizeRow}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnWarn} ${styles.btnSm}`}
                            onClick={() => {
                              setState((s) => ({
                                ...s,
                                segmentation: "personal",
                                // The room size the board was already playing
                                // with, carried over as the list length: it is
                                // the number of opponents its players are used
                                // to, and the two mean the same thing to them.
                                roster_size:
                                  s.bucket_size.trim() !== ""
                                    ? s.bucket_size
                                    : s.roster_size,
                                matchmaking:
                                  s.matchmaking.layers.length > 0
                                    ? s.matchmaking
                                    : defaultMatchmakingDraft(),
                              }));
                              if (submitted) {
                                setErrors((prev) => ({
                                  ...prev,
                                  bucket_size: undefined,
                                }));
                              }
                            }}
                          >
                            Switch to personal lists →
                          </button>
                          <span className={styles.sizeUnit}>
                            <code>bucket</code> → <code>personal</code>, one-way
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {errors.bucket_size && (
                    <div className={styles.fieldError}>
                      {errors.bucket_size}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Both warnings BEFORE the switch, not after it: the first says
               WHEN the change lands, the second what it does to the payouts,
               and neither is a thing to discover from the next cycle's
               numbers.

               A sibling of the reveal rather than a child of it, which is
               where the mockup puts them: .reveal draws the accent rule down
               its left edge and the indent that goes with it to mark the
               fields belonging to the mode that is CHOSEN, and a warning
               about leaving that mode is not one of them. */}
            {state.segmentation === "bucket" && canSwitchToPersonal && (
              <>
                <div className={styles.mmSwitchAlert}>
                  <Alert
                    variant="warn"
                    title="The switch applies from the next cycle"
                  >
                    The open cycle finishes on its rooms: its score rows
                    already name a room and its rewards are materialised per
                    room. From the next cycle on, every player gets a
                    personal list instead — and the scores of the cycle
                    running right now become the pace signal the matchmaking
                    is built on, so the first personal cycle starts warm
                    rather than blind. Rooms of finished cycles stay as they
                    are and keep rendering as rooms.
                  </Alert>
                </div>
                <div className={styles.mmSwitchAlert}>
                  <Alert
                    variant="warn"
                    title="Payout volume follows the line-up, not the mode"
                  >
                    A room of ten hands out three prizes; a personal list
                    hands one prize to its owner whenever they place in its
                    top three. With an unbiased line-up both come to the same
                    three players in ten, so the switch does not inflate
                    payouts by itself. What moves the number is the
                    composition — every bot with a weak score curve and every
                    seat that stays at zero all week pushes a real player
                    onto the podium. The reward table is not rewritten by
                    this switch: check it in the Rewards tab, then watch the
                    composition summary on the Cycles tab.
                  </Alert>
                </div>
              </>
            )}

            {state.segmentation === "personal" && (
              <div className={styles.reveal}>
                <div className={styles.revealAnno}>personal · roster size</div>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>roster_size</div>
                    <div className={styles.revealCol}>
                      <div className={styles.sizeRow}>
                        <input
                          id={rosterSizeId}
                          aria-label="Roster size"
                          type="number"
                          min={2}
                          className={`${styles.input} ${styles.number} ${
                            errors.roster_size ? styles.invalid : ""
                          }`}
                          value={state.roster_size}
                          onChange={(e) => update("roster_size", e.target.value)}
                        />
                        <span className={styles.sizeUnit}>
                          players per list, the player included
                        </span>
                      </div>
                      <div className={styles.helper}>
                        The list is assembled once — on the player&apos;s first
                        contact with the board in a cycle — and never changes
                        until the cycle closes.
                      </div>
                      {errors.roster_size && (
                        <div className={styles.fieldError}>
                          {errors.roster_size}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* The Auto-enrol row is gone from the Order section by now — the
               toggle has nothing to enrol into here — and a setting that
               vanishes silently while it is ON is a setting the operator finds
               out about from next week's behaviour. Said where the decision was
               made, and only to the board it actually costs something. */}
            {autoEnrollEndsWithSwitch && (
              <div className={styles.mmSwitchAlert}>
                <Alert variant="warn" title="Auto-enrol will be switched off">
                  This board puts a player on the running cycle with{" "}
                  <b>0</b> the first time they read it, and personal lists have
                  no shared table to do that into — the reader&apos;s own list is
                  assembled on their first contact and already holds them. The
                  save carries <code>auto_enroll: false</code> with the switch,
                  because the server judges the pair the board ends up with.
                  Rows already written stay: they are ordinary rows.
                </Alert>
              </div>
            )}

            {state.segmentation === "tier" && (() => {
              const pending = state.tiers.find(
                (t) => t.uid === tierPendingRemoval,
              );
              return (
              <>
              <div className={styles.reveal}>
                <div className={styles.revealInner}>
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>
                      tier_mode
                      <WhenBadge field="tier_mode" />
                    </div>
                    <div className={styles.revealCol}>
                      {/* PATCH drops tier_mode just as it drops segmentation,
                         so the two are shown the same way: read-only in edit,
                         as a pill rather than a control whose every option is
                         dead. */}
                      {cutFrozen ? (
                        <LockedValue value={tierMode} />
                      ) : (
                        <SegRadio
                          name="tier_mode"
                          groupLabel="Tier mode"
                          options={[
                            {
                              value: "score_bucket",
                              label: "score_bucket",
                              // Incompatible with an accumulating operator.
                              disabled: state.operator === "increment",
                            },
                            { value: "promotion", label: "promotion" },
                            {
                              value: "claim_gated",
                              label: "claim_gated",
                              // No operator restriction here: the threshold is
                              // read off the stored score after the submit, so
                              // an accumulating one can't drift past it the way
                              // it does under score_bucket.
                            },
                          ]}
                          value={tierMode}
                          onChange={(v) => {
                            update("tier_mode", v);
                            if (v !== "claim_gated") {
                              setState((s) => ({
                                ...s,
                                rewards: resetThresholdDrafts(s.rewards),
                              }));
                            }
                          }}
                        />
                      )}
                      {cutFrozen ? (
                        <div className={styles.helper}>
                          The mode can't be changed after creation.
                        </div>
                      ) : cutEditable ? (
                        <div className={styles.helper}>
                          Required by segmentation=tier. score_bucket reads the
                          tier off the score; promotion carries a ladder from
                          cycle to cycle.
                        </div>
                      ) : tierMode === "claim_gated" ? (
                        <div className={styles.helper}>
                          The tier changes when the player <b>claims</b> the
                          threshold reward. Scores can push the league back
                          down, but never above a threshold that was already
                          claimed. Works with any operator, <code>increment</code>{" "}
                          included — the threshold is measured against the final
                          score after the submit.
                        </div>
                      ) : (
                        <div className={styles.helper}>
                          {tierMode === "score_bucket"
                            ? "Tier from a score threshold. Recomputed on every submit."
                            : "League ladder. Players move only when a cycle closes."}
                        </div>
                      )}
                      {errors.tier_mode && (
                        <div className={styles.fieldError}>{errors.tier_mode}</div>
                      )}
                      <PendingNote field="tier_mode" />
                    </div>
                  </div>

                  {/* Rooms — the one control in this section that stays live
                     after create. Same .revealRow as tier_mode above, and the
                     switch is the Realtime toggle's markup. Off is the
                     default, so a board that wants no rooms is left alone
                     rather than emptied out: the size input does not exist
                     while the switch is off. */}
                  <div className={styles.revealRow}>
                    <div className={styles.rl}>rooms</div>
                    <div className={styles.revealCol}>
                      <div className={styles.switchRow}>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={state.rooms}
                          aria-label="Rooms"
                          className={`${styles.switch} ${
                            state.rooms ? styles.switchOn : ""
                          }`}
                          onClick={() => {
                            const next = !state.rooms;
                            setState((s) => ({
                              ...s,
                              rooms: next,
                              // Switching on with nothing typed yet starts from
                              // the size the mockup shows rather than an empty
                              // box that reads as an error waiting to happen.
                              bucket_size:
                                next && !s.bucket_size ? "50" : s.bucket_size,
                            }));
                            if (!next && submitted) {
                              setErrors((prev) => ({ ...prev, bucket_size: undefined }));
                            }
                          }}
                        >
                          <span className={styles.switchKnob} aria-hidden />
                        </button>
                        <span className={styles.switchLabel}>
                          {state.rooms
                            ? "On — each tier is split into rooms"
                            : "Off — the whole tier competes as one list"}
                        </span>
                      </div>
                      {state.rooms && (
                        <>
                          <div className={styles.sizeRow}>
                            <input
                              id={bucketSizeId}
                              aria-label="Room size"
                              type="number"
                              min={2}
                              className={`${styles.input} ${styles.number} ${
                                errors.bucket_size ? styles.invalid : ""
                              }`}
                              value={state.bucket_size}
                              onChange={(e) => update("bucket_size", e.target.value)}
                            />
                            <span className={styles.sizeUnit}>players per room</span>
                          </div>
                          {mode !== "edit" ? (
                            <div className={styles.helper}>
                              A room fills up, closes, and competes on its own
                              until the interval ends — own top-5, own rewards.
                              Bronze with 1 300 players becomes 26 rooms.
                            </div>
                          ) : (
                            // The mockup's two edit-mode sentences for the one
                            // row this form gives the pair: what the number
                            // counts, and that it is the section's live field.
                            <div className={styles.helper}>
                              Players per room. Editable for the life of the
                              board — the change applies to the interval already
                              running.
                            </div>
                          )}
                          {errors.bucket_size && (
                            <div className={styles.fieldError}>
                              {errors.bucket_size}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`${styles.revealRow} ${styles.revealRowFull}`}>
                    <div className={styles.revealCol}>
                      <TierEditor
                        tierMode={tierMode}
                        tiers={state.tiers}
                        rowErrors={errors.tierRows}
                        unlockRewards={unlockRewards}
                        rewardsKnown={rewardsKnown}
                        onKeyChange={(i, key) => {
                          updateTier(i, { key, keyDirty: true });
                          clearTierRowError(i, "key");
                        }}
                        onNameChange={updateTierName}
                        onMinScoreChange={(i, v) => {
                          updateTier(i, { min_score: v });
                          clearTierRowError(i, "min_score");
                        }}
                        onMinScoreBlur={sortTiersByThreshold}
                        onMove={moveTier}
                        onRemove={requestRemoveTier}
                        onAdd={addTier}
                      />

                      {tierMode === "promotion" && state.tiers.length > 0 && (
                        <div className={styles.ladderPreview}>
                          <span>ladder:</span>
                          {state.tiers.map((t, i) => (
                            <Fragment key={t.uid}>
                              {i > 0 && (
                                <span className={styles.ladderArrow} aria-hidden>
                                  →
                                </span>
                              )}
                              <span className={styles.ladderRung}>
                                {t.key || `#${i + 1}`}
                              </span>
                            </Fragment>
                          ))}
                        </div>
                      )}

                      {/* The ribbon is entirely about rewards — league →
                         reward → league — so it waits for them rather than
                         drawing a row of "⚠ no reward" over a request that
                         simply hasn't come back. The rows above still show
                         the ladder itself meanwhile. */}
                      {claimGated && rewardsKnown && rungs.length > 0 && (
                        <ClaimGatedLadder
                          rungs={rungs}
                          unlockRewards={unlockRewards}
                        />
                      )}

                      {shiftedTiers.map((t) => (
                        <div key={t.uid} className={styles.tierAlertSlot}>
                          <Alert
                            variant="warn"
                            title={`Threshold of ${t.key.trim() || t.name.trim()} changed: ${t.savedMinScore} → ${t.min_score.trim()}`}
                          >
                            The new threshold applies from the next submit on.
                            Players who already claimed{" "}
                            {unlockRewards[t.uid] ? (
                              <b>{unlockRewards[t.uid]}</b>
                            ) : (
                              "their way up"
                            )}{" "}
                            stay in {t.name.trim() || t.key.trim()} even if their
                            score is now below the new threshold — what has been
                            claimed is never taken back.
                          </Alert>
                        </div>
                      ))}

                      {claimGated && gapIndex > 0 && (
                        <div className={styles.tierAlertSlot}>
                          <Alert
                            variant="warn"
                            title={`The ladder breaks at ${tierRowLabel(
                              rungs[gapIndex - 1],
                              gapIndex - 1,
                            )}`}
                          >
                            Tier <code>{tierRowKey(rungs[gapIndex], gapIndex)}</code>{" "}
                            has no threshold reward, so players will stay in{" "}
                            <code>
                              {tierRowKey(rungs[gapIndex - 1], gapIndex - 1)}
                            </code>{" "}
                            forever. Add a <code>tier_threshold</code> reward for
                            tier <code>{tierRowKey(rungs[gapIndex], gapIndex)}</code>
                            {mode === "create"
                              ? " in the Rewards section — or later, on the Rewards tab."
                              : " on the Rewards tab."}
                          </Alert>
                        </div>
                      )}

                      {/* No rungs at all — nothing has a threshold yet — and
                         there is no ladder to explain, not even in general
                         terms. Explaining one that doesn't exist is noise, and
                         so is explaining one the alert above has just called
                         broken (see ladderWarned). */}
                      {claimGated && rungs.length > 0 && !ladderWarned && (
                        <div className={styles.tierAlertSlot}>
                          <Alert variant="info" title="What the player sees">
                            {claimGatedStory(rungs, unlockRewards)}
                          </Alert>
                        </div>
                      )}

                      {errors.tier_config && (
                        <div className={styles.fieldError}>{errors.tier_config}</div>
                      )}

                      {!claimGated && (
                        <div className={styles.helper}>
                          {tierMode === "score_bucket" ? (
                            <>
                              The key is filled in from the name (
                              <code>Bronze</code> → <code>bronze</code>) and
                              stays editable. It travels to{" "}
                              <code>leaderboard_player_tiers.tier_key</code>, to
                              rewards and to the SDK, which is why it's visible.
                              Rows are sorted by <code>min_score</code>
                              automatically. At least 2 tiers are required.
                            </>
                          ) : (
                            <>
                              Top to bottom is bottom to top of the ladder: the
                              first row is the lowest league, the last one the
                              highest. At least 2 tiers are required.
                            </>
                          )}
                        </div>
                      )}

                      {mode === "edit" && (
                        <div className={styles.helper}>
                          Keys of saved tiers are locked: rewards and the player
                          distribution point at them. Names and thresholds can
                          still change. A newly added row keeps its key editable.
                        </div>
                      )}

                      {pending && (
                        <div className={styles.tierAlertSlot}>
                          <Alert
                            variant="warn"
                            title={`Remove tier "${pending.name || pending.key}"?`}
                          >
                            Players in this tier keep the key{" "}
                            <code>{pending.key}</code>, which will no longer
                            exist in the config, and rewards bound to it stop
                            being granted. Move the players first.
                            <div className={styles.tierConfirmActions}>
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnSecondary}`}
                                onClick={() => setTierPendingRemoval(null)}
                              >
                                Keep tier
                              </button>
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={() => removeTier(pending.uid)}
                              >
                                Remove tier
                              </button>
                            </div>
                          </Alert>
                        </div>
                      )}
                    </div>
                  </div>

                  {tierMode === "promotion" && (
                    <>
                      <div className={styles.revealRow}>
                        <div className={styles.rl}>promote_top_k</div>
                        <div className={styles.revealCol}>
                          <input
                            aria-label="Promote top k"
                            inputMode="numeric"
                            className={`${styles.input} ${styles.number} ${
                              styles.mono
                            } ${errors.promote_top_k ? styles.invalid : ""}`}
                            value={state.promote_top_k}
                            onChange={(e) =>
                              update("promote_top_k", e.target.value)
                            }
                          />
                          {errors.promote_top_k ? (
                            <div className={styles.fieldError}>
                              {errors.promote_top_k}
                            </div>
                          ) : (
                            <div className={styles.helper}>
                              The top K of every tier move one rung up when the
                              cycle closes.
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.revealRow}>
                        <div className={styles.rl}>demote_bottom_k</div>
                        <div className={styles.revealCol}>
                          <input
                            aria-label="Demote bottom k"
                            inputMode="numeric"
                            className={`${styles.input} ${styles.number} ${
                              styles.mono
                            } ${errors.demote_bottom_k ? styles.invalid : ""}`}
                            value={state.demote_bottom_k}
                            onChange={(e) =>
                              update("demote_bottom_k", e.target.value)
                            }
                          />
                          {errors.demote_bottom_k ? (
                            <div className={styles.fieldError}>
                              {errors.demote_bottom_k}
                            </div>
                          ) : (
                            <div className={styles.helper}>
                              The bottom K drop one rung down. Nobody falls out
                              of the lowest tier.
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* The rooms take effect on the interval already running, so an
                 unsaved change to them says so before it is saved. Only in
                 edit mode: on create there is no interval yet to disturb. */}
              {mode === "edit" && roomsDirty && (
                <div className={styles.roomsWarning}>
                  <Alert
                    variant="warn"
                    title="Rooms will apply to the interval already running"
                  >
                    Players are mid-cycle. After saving, each one is placed in a
                    room on their next submit and the tier's single top splits
                    into per-room tops while the interval is still going. Anyone
                    who does not submit again before the cycle closes stays out
                    of every room and is ranked as a separate group at reward
                    time.
                    {/* Placed like the auto-enrol line under the sort warning:
                       the Realtime row only greys out, and the save carries no
                       `realtime` to show in any count. */}
                    {realtimeTurnsOff && (
                      <p>
                        <b>Realtime will be switched off</b> by this save:
                        without rooms or scope <code>guild_internal</code> there
                        is no table to send <code>rank.changed</code> to.
                      </p>
                    )}
                  </Alert>
                </div>
              )}
              </>
              );
            })()}
          </FieldRow>
        </div>
      </section>

      {/* ============ Opponent matchmaking (segmentation=personal) ============
         Everything that decides WHO ends up on a list, and it exists only where
         there are lists. The rules are read in the order they are drawn — the
         gate first, because a player who does not pass it is not in the cycle at
         all; then who counts as a live candidate; then what «my own level»
         means; then the layers that spend the slots. */}
      {state.segmentation === "personal" && (
      <section className={styles.section} hidden={sectionHidden("Opponent matchmaking")}>
        <div className={styles.cardTitle}>
          <h3>Opponent matchmaking</h3>
          <span className={styles.cardSub}>
            matchmaking_config · who gets into the list
          </span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow label="Entry gate" apiName="entry_gate" error={mmErrors.gate}>
            <div className={styles.switchRow}>
              <button
                type="button"
                role="switch"
                aria-checked={state.matchmaking.gate_on}
                aria-label="Entry gate"
                className={`${styles.switch} ${
                  state.matchmaking.gate_on ? styles.switchOn : ""
                }`}
                onClick={() =>
                  patchMatchmaking({ gate_on: !state.matchmaking.gate_on })
                }
              >
                <span className={styles.switchKnob} />
              </button>
              <span className={styles.switchLabel}>
                {state.matchmaking.gate_on
                  ? "On — below the threshold the board is not shown at all"
                  : "Off — every player of the project is in the cycle"}
              </span>
            </div>
            {state.matchmaking.gate_on && (
              <div className={styles.mmGateRow}>
                <Select
                  className={styles.mmGateKey}
                  value={state.matchmaking.gate_key}
                  onChange={(v) => patchMatchmaking({ gate_key: v })}
                  options={gateKeyOptions}
                  monospaceValue={state.matchmaking.gate_key !== ""}
                  placeholder={
                    noIntStat ? "No int stat declared" : "Select a stat…"
                  }
                  disabled={noIntStat}
                  aria-label="Entry gate stat"
                />
                <span className={styles.mmGateUnit}>≥</span>
                <input
                  id={gateMinId}
                  aria-label="Entry gate threshold"
                  inputMode="numeric"
                  className={`${styles.input} ${styles.mono} ${styles.mmGateMin}`}
                  value={state.matchmaking.gate_min}
                  onChange={(e) => patchMatchmaking({ gate_min: e.target.value })}
                />
              </div>
            )}
            <div className={styles.helper}>
              A key from <code>players.stats</code>. Below the threshold a player
              does not enter the cycle: no list is assembled for them, and they
              never appear on anyone else&apos;s list.
            </div>
          </FieldRow>

          <FieldRow
            label="Candidate freshness"
            apiName="freshness_days"
            error={mmErrors.freshness_days}
            htmlFor={freshnessId}
          >
            <div className={styles.sizeRow}>
              <input
                id={freshnessId}
                type="number"
                min={1}
                className={`${styles.input} ${styles.number} ${
                  mmErrors.freshness_days ? styles.invalid : ""
                }`}
                value={state.matchmaking.freshness_days}
                onChange={(e) =>
                  patchMatchmaking({ freshness_days: e.target.value })
                }
              />
              <span className={styles.sizeUnit}>
                days since <code>last_seen_at</code>
              </span>
            </div>
            <div className={styles.helper}>
              The stricter it is, the fewer dead seats — opponents who sit at
              zero all week — and the more often live candidates run out and bots
              take the remainder.
            </div>
          </FieldRow>

          <FieldRow
            label="Strength metric"
            apiName="strength"
            hint="What counts as an opponent of the player's own level."
            full
          >
            <div className={styles.reveal}>
              <div className={styles.revealAnno}>primary → fallback</div>
              <div className={styles.revealInner}>
                <div className={styles.revealRow}>
                  <div className={styles.rl}>primary</div>
                  <div className={styles.revealCol}>
                    {/* Not a picker: there is one primary metric and it is the
                       one the mode is built on. A list of one is a control that
                       cannot be used. */}
                    <LockedValue value="previous_cycle_score" />
                    <div className={styles.helper}>
                      The player&apos;s score in this board&apos;s previous cycle
                      — the pace of a week, not the progress accumulated over a
                      year.
                    </div>
                  </div>
                </div>
                <div className={styles.revealRow}>
                  <div className={styles.rl}>fallback</div>
                  <div className={styles.revealCol}>
                    <Select
                      value={state.matchmaking.fallback_stat_key}
                      onChange={(v) => patchMatchmaking({ fallback_stat_key: v })}
                      options={fallbackKeyOptions}
                      monospaceValue={state.matchmaking.fallback_stat_key !== ""}
                      aria-label="Strength fallback stat"
                    />
                    <div className={styles.helper}>
                      For players with no previous cycle: newcomers, and the
                      board&apos;s very first week.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FieldRow>

          <FieldRow
            label="Selection layers"
            apiName="layers[]"
            hint="Layers are consumed top to bottom: each takes no more than its quota, the remainder goes to the next one. Drag to reorder."
            full
            error={mmErrors.layers}
          >
            <div>
              <div className={styles.mmLayers}>
                {state.matchmaking.layers.map((layer) => {
                  const meta = LAYER_META[layer.kind];
                  const rowError = mmErrors.rows?.[layer.uid];
                  return (
                    <div
                      key={layer.uid}
                      className={`${styles.mmRow} ${
                        dragLayer === layer.uid ? styles.mmRowDragging : ""
                      } ${
                        overLayer === layer.uid && dragLayer !== layer.uid
                          ? styles.mmRowOver
                          : ""
                      }`}
                      draggable
                      onDragStart={() => setDragLayer(layer.uid)}
                      onDragEnd={() => {
                        setDragLayer(null);
                        setOverLayer(null);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setOverLayer(layer.uid);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragLayer) moveLayer(dragLayer, layer.uid);
                        setDragLayer(null);
                        setOverLayer(null);
                      }}
                    >
                      {/* A BUTTON, not an ornament. The order of these rows is
                         the order the quotas are spent in, so «drag to
                         reorder» is a setting — and a decoration with
                         aria-hidden was the only way to change it, which
                         leaves a keyboard with no way at all. Arrow keys move
                         the row it is on; the pointer still drags the whole
                         row. */}
                      <button
                        type="button"
                        className={styles.mmGrip}
                        aria-label={`Reorder ${meta.title}: use the arrow keys`}
                        title="Drag, or use ↑ / ↓"
                        onKeyDown={(e) => {
                          if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                            return;
                          }
                          e.preventDefault();
                          nudgeLayer(layer.uid, e.key === "ArrowUp" ? -1 : 1);
                        }}
                      >
                        <GripVertical size={12} aria-hidden="true" />
                      </button>
                      <div>
                        <div className={styles.mmKindTitle}>{meta.title}</div>
                        <div className={styles.mmKindApi}>{layer.kind}</div>
                      </div>
                      <div className={styles.mmParams}>
                        {layer.kind === "similar_pace" && (
                          <>
                            <span>band ±</span>
                            <input
                              aria-label="Similar pace band"
                              inputMode="numeric"
                              className={`${styles.input} ${styles.mono} ${styles.mmTiny}`}
                              value={layer.band_pct}
                              onChange={(e) =>
                                patchLayer(layer.uid, { band_pct: e.target.value })
                              }
                            />
                            <span>%</span>
                            <span>· widen</span>
                            <Select
                              className={styles.mmSel}
                              value={layer.widen_steps || "0"}
                              onChange={(v) =>
                                patchLayer(layer.uid, { widen_steps: v })
                              }
                              options={WIDEN_OPTIONS}
                              aria-label="Similar pace widening"
                            />
                            <span>·</span>
                          </>
                        )}
                        {/* Bots are picked in no particular order and there is
                           no preference to express over a simulation, so the row
                           says what it IS instead of offering a control that
                           would change nothing. */}
                        {layer.kind === "bots" ? (
                          <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                            simulation · never receives a reward
                          </span>
                        ) : (
                          <>
                            <span>prefer</span>
                            <Select
                              className={styles.mmSel}
                              value={layer.prefer || "fresher_last_seen"}
                              onChange={(v) =>
                                patchLayer(layer.uid, {
                                  prefer: v as MatchmakingPrefer,
                                })
                              }
                              options={PREFER_OPTIONS}
                              aria-label={`${meta.title} preference`}
                            />
                          </>
                        )}
                      </div>
                      <div className={styles.mmQuota}>
                        {/* «rest» is a state, not a number: a layer with no
                           quota takes everything still missing, and a number
                           standing for that would have to be retyped every time
                           the list length moved. Clearing the box is how the
                           operator says it. */}
                        {layer.quota.trim() === "" ? (
                          <button
                            type="button"
                            className={styles.mmRest}
                            onClick={() => patchLayer(layer.uid, { quota: "1" })}
                            aria-label={`Cap ${meta.title}`}
                          >
                            rest
                          </button>
                        ) : (
                          <>
                            <span>{layer.kind === "bots" ? "cap" : "max"}</span>
                            <input
                              aria-label={`${meta.title} quota`}
                              inputMode="numeric"
                              className={`${styles.input} ${styles.mono}`}
                              value={layer.quota}
                              onChange={(e) =>
                                patchLayer(layer.uid, { quota: e.target.value })
                              }
                            />
                            <span>slots</span>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        className={styles.mmDel}
                        aria-label={`Remove ${meta.title}`}
                        onClick={() =>
                          patchMatchmaking({
                            layers: state.matchmaking.layers.filter(
                              (l) => l.uid !== layer.uid,
                            ),
                          })
                        }
                      >
                        <Trash2 size={14} />
                      </button>
                      {rowError && (
                        <div className={styles.mmRowError}>{rowError}</div>
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className={styles.tierAdd}
                  onClick={() => setLayerMenuOpen((o) => !o)}
                  aria-expanded={layerMenuOpen}
                  aria-haspopup="menu"
                >
                  <Plus size={12} /> Add layer
                </button>
              </div>
              {layerMenuOpen && (
                <div
                  className={styles.mmMenu}
                  role="menu"
                  ref={layerMenuRef}
                  // Escape closes it, which is the one key every menu is
                  // expected to answer — and, until this, the menu had no way
                  // out but picking a layer.
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setLayerMenuOpen(false);
                    }
                  }}
                >
                  <div className={styles.mmMenuHead}>Add a selection layer</div>
                  {LAYER_KINDS.map((kind) => {
                    const used = state.matchmaking.layers.some(
                      (l) => l.kind === kind,
                    );
                    const meta = LAYER_META[kind];
                    return (
                      <button
                        key={kind}
                        type="button"
                        role="menuitem"
                        // aria-disabled, not disabled: a disabled button leaves
                        // the tab order and is skipped by a screen reader
                        // altogether, and the whole point of keeping the used
                        // kinds on this menu is that the operator is TOLD
                        // «Friends — already added» rather than left wondering
                        // where the entry went.
                        aria-disabled={used}
                        className={`${styles.mmMenuItem} ${
                          used ? styles.mmMenuItemUsed : ""
                        }`}
                        onClick={() => {
                          if (used) return;
                          patchMatchmaking({
                            layers: [
                              ...state.matchmaking.layers,
                              newLayerDraft(kind),
                            ],
                          });
                          setLayerMenuOpen(false);
                        }}
                      >
                        <span className={styles.mmMenuTitle}>
                          {meta.title} <code>{kind}</code>
                        </span>
                        <span className={styles.mmMenuDesc}>
                          {meta.desc}
                          {used ? " Already added." : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className={styles.helper}>
                The last layer must be a filler (<code>recent_active</code> or{" "}
                <code>bots</code>) — otherwise a list can stay incomplete, and an
                incomplete list is exactly what this board type exists to avoid.
              </div>
            </div>
          </FieldRow>
        </div>
      </section>
      )}

      {/* ============ Cycle lifetime ============
         Outside the numbered steps on purpose: the section belongs to the
         window kinds where a cycle finishes at all, and an all_time board
         walks the same five steps it always did. */}
      {hasCycleLifetime(state.window_kind) && (
      <section className={styles.section} hidden={sectionHidden("Cycle lifetime")}>
        <div className={styles.cardTitle}>
          <h3>Cycle lifetime</h3>
          {/* Named by what the section is about rather than by a list of the
             kinds that qualify — the list was two long and is now three. */}
          <span className={styles.cardSub}>boards whose cycles finish</span>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.sectionDesc}>
            How long a finished cycle is kept — and, by the same number, how
            long a player has to claim the reward it handed out. Once the term
            is over the cycle is deleted: scores, rooms and grants.
          </p>

          {/* On a guild_aggregated board the score row belongs to the guild, so
             one member's acknowledgement would speak for the other forty. The
             server refuses that pair outright, and the switch isn't offered
             where it could only produce a 400. */}
          {state.scope !== "guild_aggregated" && (
            <FieldRow label="Hold finished cycles" apiName="hold_finished_cycles">
              <div className={styles.switchRow}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={state.hold_finished_cycles}
                  aria-label="Hold finished cycles"
                  className={`${styles.switch} ${
                    state.hold_finished_cycles ? styles.switchOn : ""
                  }`}
                  onClick={() =>
                    update("hold_finished_cycles", !state.hold_finished_cycles)
                  }
                >
                  <span className={styles.switchKnob} aria-hidden />
                </button>
                {/* One label in both positions, unlike the Realtime and Rooms
                   switches: it names what the setting does, and the Off half
                   of the story is the hint's last sentence. */}
                <span className={styles.switchLabel}>
                  Keep a player in their finished cycle until they claim or view it
                </span>
              </div>
              {/* .hint rather than the .helper the reveal rows use: both hints
                 of this section run under their control at full width, and the
                 rich text in them could not travel through FieldRow's own
                 `hint` prop, which takes a string. */}
              <div className={styles.hint}>
                While a player has a finished cycle they have not closed, submits
                are refused with <code>leaderboard_cycle_pending</code> and reads
                answer for that cycle. Off — the previous behaviour: the cycle
                rolls over silently and scores go straight into the new one.
              </div>
            </FieldRow>
          )}

          <FieldRow
            label="Retention"
            apiName="retention_days"
            error={errors.retention_days}
            htmlFor={retentionId}
          >
            <input
              id={retentionId}
              type="number"
              min={0}
              step={1}
              className={`${styles.input} ${styles.number} ${
                errors.retention_days ? styles.invalid : ""
              }`}
              value={state.retention_days}
              onChange={(e) => update("retention_days", e.target.value)}
            />
            <div className={styles.hint}>
              Days after a cycle closes. <b>0</b> — do not keep finished cycles
              at all: their rows are deleted as soon as rewards are calculated,
              and nothing can be claimed later.
            </div>

            {/* Everywhere else a 0 costs the history. On a board with a pause
               it costs the pause itself: between two windows the reads fall
               back on the window that just finished, and with nothing kept
               there is nothing to fall back on. Said only at 0, and only here
               — a line that stands under every board is a line nobody reads by
               the time it matters. Not an error: the server takes the pair,
               and a board whose game only reads while a window is open is a
               board this costs nothing. */}
            {state.window_kind === "recurring_event" &&
              parseRetentionDays(state.retention_days) === 0 && (
                <div className={styles.hint}>
                  On this board that also empties the pause: while no window is
                  open, <code>/top</code>, <code>/around-me</code>,{" "}
                  <code>/me</code> and the player card answer from the window
                  that just finished — deleted at its close, they answer{" "}
                  <code>404 leaderboard_not_found</code> instead, and the{" "}
                  <code>next_cycle</code> that carries the countdown to the next
                  start goes with it.
                </div>
              )}

            {shownImpact && (
              <div className={styles.retentionAlertSlot}>
                <Alert
                  variant="warn"
                  title="Lowering this deletes rewards nobody claimed"
                >
                  {/* The term is echoed as it was typed; the counts are
                     grouped, being numbers nobody typed and everybody has to
                     read. */}
                  At {shownImpact.days} days the next sweep removes{" "}
                  {formatCount(shownImpact.cycles)} finished cycles and{" "}
                  {formatCount(shownImpact.unclaimed_grants)} unclaimed rewards.
                  This cannot be undone.
                </Alert>
              </div>
            )}
          </FieldRow>
        </div>
      </section>
      )}

      {/* ============ Anti-cheat guards ============
         Outside the numbered steps, like Cycle lifetime: the caps apply to
         every board and belong to neither of the five decisions. They have
         been enforced on every submit and patchable through the API all
         along — this section is the first control that ever set them.

         The whole card goes on an auto-scored board: every one of the three
         caps is a rule about an incoming submit, and there are none. The
         Scoring Alert says so once rather than each field saying it here. */}
      {!autoScored && (
      <section className={styles.section} hidden={sectionHidden("Anti-cheat guards")}>
        <div className={styles.cardTitle}>
          <h3>Anti-cheat guards</h3>
          <span className={styles.cardSub}>applied on every submit</span>
        </div>
        <div className={styles.cardBody}>
          <p className={styles.sectionDesc}>
            The caps the server turns a suspicious submit away by. An empty
            field is a guard that is off — a <code>0</code> is not: it is a cap
            of zero, and on the two score caps that refuses everything.
          </p>

          <FieldRow
            label="Max score"
            apiName="guards.max_score_per_submit"
            hint={
              <>
                Rejects a submit whose score is above this —{" "}
                <code>guard_max_score</code>.
              </>
            }
            error={errors.guard_max_score}
            htmlFor={guardMaxScoreId}
          >
            <input
              id={guardMaxScoreId}
              inputMode="numeric"
              className={`${styles.input} ${styles.number} ${styles.mono} ${
                errors.guard_max_score ? styles.invalid : ""
              }`}
              value={state.guard_max_score}
              onChange={(e) => update("guard_max_score", e.target.value)}
            />
          </FieldRow>

          <FieldRow
            label="Max delta"
            apiName="guards.max_delta_per_submit"
            hint={
              <>
                Rejects a jump bigger than this from the stored score —{" "}
                <code>guard_max_delta</code>.
              </>
            }
            error={errors.guard_max_delta}
            htmlFor={guardMaxDeltaId}
          >
            <input
              id={guardMaxDeltaId}
              inputMode="numeric"
              className={`${styles.input} ${styles.number} ${styles.mono} ${
                errors.guard_max_delta ? styles.invalid : ""
              }`}
              value={state.guard_max_delta}
              onChange={(e) => update("guard_max_delta", e.target.value)}
            />
          </FieldRow>

          <FieldRow
            label="Min interval"
            apiName="guards.min_interval_sec"
            hint={
              <>
                Seconds between two submits by the same player —{" "}
                <code>guard_interval</code>, 429.
              </>
            }
            error={errors.guard_min_interval}
            htmlFor={guardIntervalId}
          >
            <input
              id={guardIntervalId}
              inputMode="numeric"
              className={`${styles.input} ${styles.number} ${styles.mono} ${
                errors.guard_min_interval ? styles.invalid : ""
              }`}
              value={state.guard_min_interval}
              onChange={(e) => update("guard_min_interval", e.target.value)}
            />
          </FieldRow>
        </div>
      </section>
      )}

      {/* ============ Metadata ============ */}
      <section className={styles.section} hidden={sectionHidden("Metadata")}>
        <div className={styles.cardTitle}>
          <h3>Metadata</h3>
          <span className={styles.cardSub}>free-form JSON for the game</span>
        </div>
        <div className={styles.cardBody}>
          <FieldRow
            label="Metadata"
            apiName="metadata"
            hint="Anything the game wants to carry with the board. The server stores it as it is."
            full
            error={errors.metadata}
            htmlFor={metadataId}
          >
            <textarea
              id={metadataId}
              className={`${styles.textarea} ${styles.jsonArea} ${
                errors.metadata ? styles.invalid : ""
              }`}
              spellCheck={false}
              placeholder={'{\n  "icon": "trophy_gold",\n  "season": 7\n}'}
              value={state.metadata}
              onChange={(e) => update("metadata", e.target.value)}
            />
            <div className={styles.helper}>
              Invalid JSON blocks saving with a field error — the same rule the
              player meta editor uses. An empty box means the board carries no
              metadata.
            </div>
          </FieldRow>
        </div>
      </section>

      {/* ============ SECTION 5: Rewards (create mode only) ============
         In edit mode the SettingsTab embeds the full RewardsEditor (same
         component the Rewards tab uses) directly between the form and the
         Danger zone, so the form omits the section entirely to avoid a
         duplicate "Rewards" heading. */}
      {mode === "create" && (
      <section className={styles.section} hidden={sectionHidden("Rewards")}>
        <div className={styles.cardTitle}>
          <h3>Rewards</h3>
          <span className={styles.cardSub}>step 5 of 5</span>
        </div>
        <div className={styles.cardBody}>
          {imported && (
            <div className={styles.rewardsImportSlot}>
              <Alert
                variant="info"
                title={`${imported.count} ${
                  imported.count === 1 ? "reward" : "rewards"
                } loaded from ${imported.fileName}`}
              >
                Nothing is saved yet — they are created together with the
                leaderboard.
              </Alert>
            </div>
          )}

          {state.rewards.length === 0 ? (
            <div className={styles.rewardsEmpty}>
              No rewards yet. Add them one by one, or import a file exported
              from another leaderboard.
            </div>
          ) : (
            <div className={styles.rewardEditor}>
              <div
                className={`${styles.rewardRow} ${styles.rewardHead} ${
                  rewardsHaveTier ? styles.rewardRowTier : ""
                }`}
              >
                <div>Pos.</div>
                <div>Name</div>
                {rewardsHaveTier && <div>Tier</div>}
                <div>Type</div>
                <div>Range</div>
                <div>Payload (JSON)</div>
                <div aria-hidden />
              </div>
              {state.rewards.map((r, i) => {
                const rowErr = errors.rewardRows?.[i];
                return (
                  <div
                    key={i}
                    className={`${styles.rewardRow} ${
                      rewardsHaveTier ? styles.rewardRowTier : ""
                    } ${r.imported ? styles.rowImported : ""} ${
                      rowErr ? styles.rewardRowInvalid : ""
                    }`}
                  >
                    <div className={styles.rewardPos}>{i + 1}</div>
                    <input
                      className={styles.input}
                      aria-label={`Reward ${i + 1} name`}
                      placeholder="Champion"
                      value={r.name}
                      onChange={(e) =>
                        updateRewardDraft(i, { name: e.target.value })
                      }
                    />
                    {rewardsHaveTier && (
                      <Select
                        aria-label={`Reward ${i + 1} tier`}
                        className={styles.rewardSelect}
                        value={
                          // A tier row deleted on step 4 leaves a uid nothing
                          // answers to — fall back to "all tiers" so the shown
                          // value matches what would be submitted.
                          state.tiers.some((t) => t.uid === r.tier_uid)
                            ? r.tier_uid
                            : ALL_TIERS
                        }
                        onChange={(v) => updateRewardDraft(i, { tier_uid: v })}
                        options={
                          r.type === "tier_threshold"
                            ? thresholdTierOptions
                            : tierOptions
                        }
                      />
                    )}
                    <Select
                      aria-label={`Reward ${i + 1} type`}
                      className={styles.rewardSelect}
                      monospaceValue
                      value={r.type}
                      onChange={(v) => {
                        const type = v as RewardDraft["type"];
                        // A tier that was a legal scope for a cycle-close prize
                        // may be the starting league, which a threshold reward
                        // may not point at. Drop the binding rather than keep
                        // one the picker no longer offers.
                        const dropsTier =
                          type === "tier_threshold" && r.tier_uid === bottomUid;
                        updateRewardDraft(i, {
                          type,
                          ...(dropsTier ? { tier_uid: ALL_TIERS } : {}),
                        });
                      }}
                      options={rewardTypeOptions}
                    />
                    {r.type === "tier_threshold" ? (
                      // No range to fill in — the tier in the previous column
                      // is what this reward is scoped to.
                      <div className={styles.rangeNA}>— tier threshold</div>
                    ) : (
                      <div className={styles.rewardRangePair}>
                        <input
                          className={`${styles.input} ${styles.mono}`}
                          aria-label={`Reward ${i + 1} range from`}
                          placeholder="1"
                          inputMode="numeric"
                          value={r.range_from}
                          onChange={(e) =>
                            updateRewardDraft(i, { range_from: e.target.value })
                          }
                        />
                        <span className={styles.rewardDash}>–</span>
                        <input
                          className={`${styles.input} ${styles.mono}`}
                          aria-label={`Reward ${i + 1} range to`}
                          placeholder="1"
                          inputMode="numeric"
                          value={r.range_to}
                          onChange={(e) =>
                            updateRewardDraft(i, { range_to: e.target.value })
                          }
                        />
                        {r.type === "percentile" && (
                          <span className={styles.rewardPctMark}>%</span>
                        )}
                      </div>
                    )}
                    <input
                      className={`${styles.input} ${styles.mono}`}
                      aria-label={`Reward ${i + 1} payload`}
                      placeholder='{"gold":1000}'
                      value={r.payload}
                      onChange={(e) =>
                        updateRewardDraft(i, { payload: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className={styles.rewardRemove}
                      aria-label={`Remove reward ${i + 1}`}
                      onClick={() => removeRewardDraft(i)}
                    >
                      ×
                    </button>
                    {rowErr && (
                      <div className={styles.rewardRowError}>{rowErr}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Both ways of getting a row carry the same weight and stand in the
             same place whether or not there are rows already — the import is
             only discoverable if it is visible before the first reward
             exists. */}
          <div className={styles.rewardsToolbar}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={addRewardDraft}
            >
              + Add reward
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => setImportOpen(true)}
            >
              <Upload size={13} aria-hidden="true" />
              Import JSON
            </button>
            {errors.rewards && (
              <>
                <span className={styles.toolbarSpacer} />
                <span className={styles.fieldError}>{errors.rewards}</span>
              </>
            )}
          </div>

          <ImportRewardsDraftModal
            open={importOpen}
            onClose={closeImport}
            draftCount={state.rewards.length}
            review={(file) =>
              reviewRewardsFile(file, state.tiers, state.segmentation, tierMode)
            }
            onApply={applyImportedRewards}
          />

          {claimGated && (
            <div className={styles.rewardsAlertSlot}>
              <Alert variant="info" title="Two kinds of reward on one board">
                <code>tier_threshold</code> is granted at submit time, the
                moment the score first crosses the threshold, and opens the
                league when it is claimed. <code>rank_range</code> and{" "}
                <code>percentile</code> are still granted when the cycle closes
                — on an <code>all_time</code> board the cycle never closes, so
                there they never fire.
              </Alert>
            </div>
          )}
        </div>
      </section>
      )}

      {/* ============ Fixed action bar (page-bottom) ============
         The outer .actionBar is positioned: fixed at viewport bottom so it
         stays visible while the user scrolls the page (including past the
         form into siblings like SettingsTab's Danger zone). The inner
         wrapper mirrors the page body (capped at --content-max with a 32px
         gutter) so the buttons align with the right edge of the form
         sections. */}
      <div className={styles.actionBar}>
        <div className={styles.actionBarInner}>
          {mode === "create" ? (
            <>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.btn} ${styles.btnPrimary}`}
                disabled={saving}
              >
                {saving ? "Saving…" : effectiveSubmitLabel}
              </button>
            </>
          ) : (
            <>
              {/* What the bar is about, on its left — the count, and the one
                 fact that decides which button can carry it (mockup §2, §6,
                 §9, §10). */}
              {cutFrozen ? (
                <span className={`${pendingStyles.abNote} ${pendingStyles.abMuted}`}>
                  <Clock size={12} aria-hidden />
                  This board has no next cycle —{" "}
                  {state.window_kind === "all_time"
                    ? "an all_time window never closes"
                    : "an event window closes without a successor"}
                  , so {frozenCutFields.length} of its fields cannot be edited
                  at all.
                </span>
              ) : (
                <span className={`${pendingStyles.abNote} ${pendingStyles.abWarn}`}>
                  {dirty && <span className={pendingStyles.dot} />}
                  {dirty
                    ? `${countedChanges.length} unsaved ${
                        countedChanges.length === 1 ? "change" : "changes"
                      }`
                    : "No unsaved changes"}
                  {dirty && (
                    <span className={pendingStyles.diffNote}>
                      {" · "}
                      {saveBarSuffix}
                    </span>
                  )}
                </span>
              )}

              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost}`}
                onClick={discard}
                disabled={saving || scheduling || !dirty}
              >
                Discard
              </button>

              {/* Only where the plural reads: with one boundary field the
                 label would say "Show the 1 that need a cut", and the row it
                 would filter down to is the one the operator just edited. */}
              {cutOnlyDirty.length >= 2 && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={() => setOnlyCuts((v) => !v)}
                >
                  {onlyCuts
                    ? "Show every field"
                    : `Show the ${cutOnlyDirty.length} that need a cut`}
                </button>
              )}

              {/* The order flips with the answer: whichever save can carry
                 this edit is the primary one, and it is the last button on
                 the bar. */}
              {saveNowRefusal ? (
                <>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnPrimary} ${styles.btnDisabled}`}
                    aria-disabled
                    title={saveNowRefusal}
                    /* aria-disabled rather than `disabled`: the button has
                       something to say — the title names the way out — and a
                       disabled button is not focusable, so nobody reading the
                       page with a keyboard would ever hear it. The click is
                       refused here instead. */
                    onClick={() => undefined}
                  >
                    Save now
                  </button>
                  {/* The way out, where there is one. A board with no next
                     cycle has no cut to hand the edit to, and offering one
                     would promise a moment that never comes. */}
                  {boardCuts && (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={() => submitWith("schedule")}
                      disabled={saving || scheduling}
                    >
                      <Clock size={16} aria-hidden />
                      {scheduling ? "Scheduling…" : "Schedule for next cycle"}
                    </button>
                  )}
                </>
              ) : (
                <>
                  {boardCuts && (
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnSecondary}`}
                      onClick={() => submitWith("schedule")}
                      disabled={saving || scheduling || !dirty}
                    >
                      <Clock size={16} aria-hidden />
                      Schedule for next cycle
                    </button>
                  )}
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    disabled={saving || scheduling}
                  >
                    {saving ? "Saving…" : "Save now"}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </form>
    </PendingMarksCtx.Provider>
  );
}
